/**
 * Multi-hop executor runner for the ReAct-pattern agent.
 *
 * Owns the provider-agnostic tool loop for the executor phase. Assembles
 * the full tool set (global read/chart/formula tools + run-scoped control
 * and write tools), dispatches calls, emits progress events, and returns
 * an `ExecutorRunResult` compatible with the legacy step-loop tail in
 * `ExcelApp.tsx`.
 *
 * This module is UI-agnostic: it communicates progress via typed event
 * callbacks so the caller can drive the thought-process timeline,
 * streaming UI, or debug logs without coupling the runner to React state.
 */

import { withCredentials } from "../shared/apiClient";
import { fetchWithRetry } from "../shared/utils/http";
import { isAbortError } from "../shared/utils/abortError";
import { isExcelClaudeOpusEnabled } from "../shared/featureFlags/excelExecutorModel";
import {
  FLAGGED_EXECUTOR_MODEL_ID,
  getExecutorAdapter,
  isFatalProviderTurnError,
  resolveExecutorModel,
  type AgentToolCall,
  type AgentTurn,
  type AgentTurnResult,
  type ExecutorStreamTurnParams,
} from "./providers";
import {
  buildFinsterQueryTool,
  getExcelFinsterBrandContext,
  getFinsterQueryToolInstruction,
} from "./finsterQueryTool";
import { ID_LOOKUP_TOOL_NAME, getIdLookupToolInstruction } from "./idLookupTool";
import {
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
  type IdLookupWorkloadCompletionIssue,
} from "./idLookupWorkload";
import {
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
  TabularQueryWorkloadSuspensionError,
  getTabularQueryWorkloadInstruction,
  type ExcelTabularQueryFeatureConfig,
  type TabularIdentityResolver,
  type TabularQueryWorkloadClient,
  type TabularQueryWorkloadCompletionIssue,
} from "./tabularQueryWorkload";
import {
  TABULAR_TOPIC_TERM,
  TABULAR_TOPIC_TERM_GLOBAL,
} from "./tabularTopicTerms";
import {
  carriesClassifiableSignal,
  classifyWorksheetColumnIntent,
  matchWorksheetColumnAlias,
  type WorksheetColumnIntent,
} from "./worksheetColumnIntent";
import {
  detectWorksheetCompanyList,
  detectWorksheetCompanyListFromContext,
  type WorksheetCompanyList,
} from "./worksheetCompanyList";
import {
  IdLookupRoutingPolicy,
  buildBlockedIdLookupResearchOutput,
  detectIdLookupRoutingIntent,
  detectIdLookupWorksheetWorkload,
  type IdLookupResearchDecision,
  type IdLookupRoutingIntent,
} from "./idLookupRoutingPolicy";
import { resolveIdLookupAvailability } from "./idLookupMode";
import {
  buildChartOutputModesPromptBlock,
  buildFactSetCatalogPromptBlock,
  buildFactSetMultiChartIntentPromptBlock,
  resolveForwardPeIntentSignals,
} from "./chartCapabilityRegistry";
import {
  executeToolCallDetailed,
  type ToolCallContext,
  type ToolCallDetailedResult,
  type ToolDef,
} from "./toolRegistry";
import {
  createExecutorToolRuntime,
  toPersistedStepStatus,
  type ExecutorApplyWorkbookModifications,
  type ExecutorRunAccumulators,
  type ExecutorRunSessionState,
  type ExecutorToolRuntimeContext,
  type ExecutorWorkbookHandoff,
  type PlanStateChangeCallback,
  type RuntimeToolCallContext,
  type RuntimeToolExecutor,
} from "./executorToolRuntime";
import { mapWithConcurrencyLimit, type ExecutorWorkloadParticipant } from "./executorWorkload";
import {
  chartStepRequiresEvidence,
  type ChartStoppingEvidence,
} from "./executorChartStopping";
import {
  EXECUTOR_RUN_TIME_BUDGET_MS,
  MAX_EXECUTOR_ITERATIONS,
  MAX_STAGNANT_EXECUTOR_ITERATIONS,
  createExecutorStagnationBudget,
  executorProgressSignature,
  hasExhaustedExecutorRunTimeBudget,
  normalizeExecutorToolCalls,
} from "./executorLimits";
import {
  executeFactSetFormatSafetyPass,
  WORKBOOK_EDIT_STEPS_REQUIRED_ERROR,
} from "./executorWriteTools";
import { recordColumnSizingIntent } from "./columnSizingPolicy";
import { isFdsBoundFormula } from "./formulaWriteDetection";
import { FQL_DATE_INPUT_GUIDANCE } from "./fqlDatePolicy";
import {
  cloneForwardPeChartVerificationState,
  createForwardPeChartVerificationState,
  getForwardPeRangeChartBlock,
  updateForwardPeChartVerificationIntent,
  updateForwardPeChartVerificationState,
} from "./forwardPeChartGuard";
import type {
  ExecutorRefreshFactSetData,
  ExecutorRecalculateWorkbook,
} from "./executorRefreshTools";
import {
  resetWorkbookScopedRunAccumulators,
  setStepResultLine,
} from "./executorAccumulators";
import { ExecutorWorkbookHandoffError } from "./executorWorkbookTools";
import type { ApplyTemplateOutcome } from "./templateTools";
import type {
  ExecutionPlan,
  ExecutorPlanState,
  ExecutorRunCheckpoint,
  ExecutorRunResult,
  ExecutorUnifiedRunResult,
  ExcelStepStatus,
  WorkbookContent,
} from "./types";
import { extractSourcesFromCitations, type ExcelAgentSource } from "./agentTimeline";
import { diag, isDiagEnabled, recordPhase } from "./aiDiag";
import { buildSkillContextBlock, withPushSkills } from "./excelSkills";
import {
  getExcelBrandDefaults,
  type ExcelBrandId,
} from "./branding/excelBrandDefaults";
import {
  buildExcelUnifiedAgentGenerationMetadata,
  withOptionalExcelLangfuseMetadata,
  type ExcelLangfuseRunContext,
} from "./langfuseTrace";
import { postExcelToolObservation } from "./langfuseToolObservation";
import { withChatHistoryGuidance } from "./chatHistoryContext";
import { columnIndex, numberValue, stringValue } from "./excelValueUtils";
import { isAnyResearchToolName, type FinsterStreamSubEvent } from "../shared/api/finster/queryTool";
import { TabularQueryExecutionError } from "../shared/api/finster/tabularQueryExecution";
import {
  classifyHardErrorsInReadRangeResult,
  classifyHardErrorsInFormulaValues,
  summarizeHardErrors,
  type ExcelHardErrorCell,
} from "./excelHardErrors";
import type { FactSetSpreadsheetNoteCapability } from "../shared/factset/hostContextService";
import { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import {
  extractCitedValuesFromText,
  extractCitedValuesById,
  ingestAgentResponse,
  mergeCitedValuesByUuid,
  type CitationNoteIndex,
} from "./citationNotes";
import {
  appendExcelOperationEvidence,
  findReusableExcelEvidence,
  renderExcelOperationEvidence,
  type ExcelOperationEvidence,
} from "./userSkills/operationEvidence";
import {
  hasActionableExcelCheckpointWork,
  isReadbackRecoverableBlockedWrite,
} from "./executorCheckpoint";
import {
  evaluateWorksheetContentReadback,
  hasDenseWorksheetContentReadbackEvidence as hasDenseReadbackEvidence,
  type WorksheetContentReadbackEvaluation,
  type WorksheetContentReadbackTarget,
  type WorksheetExpectedContentCell,
} from "./worksheetContentReadback";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Hard iteration cap for the tool loop. Protects against a confused model
 * that loops indefinitely. If it is reached, the runner returns a partial
 * result instead of discarding completed workbook edits.
 */
export const MAX_EXECUTOR_VERIFICATION_REMINDERS = 3;
export const MAX_EXECUTOR_NON_TERMINAL_REMINDERS = 2;
export const MAX_EXECUTOR_MISSING_PLAN_REMINDERS = 2;
export const MAX_EXECUTOR_ID_LOOKUP_ROUTING_REMINDERS = 3;
export const MAX_EXECUTOR_WORKLOAD_REMINDERS = 3;
/** @deprecated Use the workload-generic reminder budget. */
export const MAX_EXECUTOR_ID_LOOKUP_WORKLOAD_REMINDERS = MAX_EXECUTOR_WORKLOAD_REMINDERS;
const MAX_PARALLEL_READ_ONLY_TOOL_CALLS = 4;

type RunnerIdLookupWorkloadIssue =
  | IdLookupWorkloadCompletionIssue
  | {
      issue: "id_lookup_workload_registration_required";
      workloadId: null;
      nextTool: typeof START_ID_LOOKUP_WORKLOAD_TOOL_NAME;
      remainingRows: null;
      nextRowNumbers: number[];
      instruction: string;
    };

type RunnerTabularQueryWorkloadIssue =
  | TabularQueryWorkloadCompletionIssue
  | {
      issue: "tabular_query_workload_registration_required";
      workloadId: null;
      nextTool: typeof START_TABULAR_QUERY_WORKLOAD_TOOL_NAME;
      remainingRows: null;
      nextRowNumbers: number[];
      instruction: string;
    };

type RunnerWorksheetWorkloadIssue = RunnerIdLookupWorkloadIssue | RunnerTabularQueryWorkloadIssue;

export function resolveIdLookupIterationCapFailure(options: {
  workloadIssue: { remainingRows: number | null } | null;
  workloadIncompleteRows: readonly string[];
  routingBlock: { issue: string } | null;
  routingUnresolved: readonly string[];
  workloadKind?: "id_lookup" | "tabular_query";
}): { reason: string; unresolved: string[] } | null {
  if (options.workloadIssue) {
    const reason =
      options.workloadKind === "tabular_query"
        ? "The executor reached its iteration limit before every tabular query row reached a terminal state."
        : "The executor reached its iteration limit before every worksheet ID lookup row was written and verified.";
    return {
      reason,
      unresolved:
        options.workloadIncompleteRows.length > 0 ? [...options.workloadIncompleteRows] : [reason],
    };
  }
  if (options.routingBlock) {
    const reason =
      "The executor reached its iteration limit before required company identity lookup routing completed.";
    return {
      reason,
      unresolved: options.routingUnresolved.length > 0 ? [...options.routingUnresolved] : [reason],
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Dispatch infrastructure
// ---------------------------------------------------------------------------

/**
 * Tools that mutate shared state (host writes, plan state, accumulators).
 *
 * A single assistant turn can emit several tool calls. When a batch contains
 * any stateful tool, the whole batch is executed sequentially in emission
 * order so that (a) writes apply deterministically and (b) a same-turn
 * `read_range` after a write observes the post-write state rather than racing
 * ahead of it. Batches that are entirely read-only run fully in parallel.
 */
export const STATEFUL_TOOLS = new Set<string>([
  "set_research_route",
  "modify_workbook",
  "set_range_values",
  "set_range_formulas",
  "format_range",
  "clear_range",
  "edit_range_structure",
  "layout_range",
  "sort_range",
  "manage_worksheet",
  "copy_worksheet",
  "manage_named_range",
  "insert_company_logo",
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "create_sheet",
  "rename_sheet",
  "activate_sheet",
  "set_run_plan",
  "set_step_status",
  "revise_plan",
  "apply_template",
  "refresh_factset_data",
  "recalculate_workbook",
  "open_workbook",
  "create_workbook",
  "activate_workbook",
  START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
  NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
  NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
]);

export const WRITE_TOOLS = new Set<string>([
  "modify_workbook",
  "set_range_values",
  "set_range_formulas",
  "format_range",
  "clear_range",
  "edit_range_structure",
  "layout_range",
  "sort_range",
  "manage_worksheet",
  "copy_worksheet",
  "manage_named_range",
  "insert_company_logo",
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "create_sheet",
  "rename_sheet",
  "activate_sheet",
  "apply_template",
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
]);

/**
 * Writes protected by the executor's argument-only replay fingerprint.
 *
 * Worksheet workload commits own their lifecycle and exact read-back
 * verification. Their run-scoped IDs can be reused after resume, so a generic
 * fingerprint can otherwise suppress a new controller's first commit and leave
 * its active wave permanently open.
 *
 * Membership must stay all-or-nothing per tool: it gates the fingerprint, the
 * `blockedOperation` guards, and the assignment of `blockedOperation` itself.
 * Re-admitting commits to only the guards would deadlock them, because every
 * wave commits the same `{workloadId}` arguments -- so an exact-match guard
 * would treat wave N+1 as a replay of wave N, and the overlap guard fails
 * closed when it cannot derive a target range from those arguments. Commits are
 * instead safe to replay on their own terms: each one re-reads its targets and
 * writes only rows that come back blank.
 */
const REPLAY_GUARDED_WRITE_TOOLS = new Set(
  Array.from(WRITE_TOOLS).filter(
    (toolName) =>
      toolName !== COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME &&
      toolName !== COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME
  )
);

/** Cell annotation writes whose ambiguous acknowledgements are isolated. */
const ANNOTATION_WRITE_TOOLS = new Set([
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
]);

/**
 * Workbook observations that make a repeated structural mutation deliberate
 * rather than a blind continuation replay.
 */
const CONTINUATION_REPEAT_INSPECTION_TOOLS = new Set([
  "list_sheets",
  "read_range",
  "get_comment",
  "get_note",
  "get_all_comments",
  "get_all_notes",
]);

export const RUN_PLAN_REQUIRED_TOOLS = new Set<string>([
  "modify_workbook",
  "set_range_values",
  "set_range_formulas",
  "format_range",
  "clear_range",
  "edit_range_structure",
  "layout_range",
  "sort_range",
  "manage_worksheet",
  "copy_worksheet",
  "manage_named_range",
  "insert_company_logo",
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "create_sheet",
  "rename_sheet",
  "activate_sheet",
  "apply_template",
  "set_step_status",
  "generate_formula",
  "refresh_factset_data",
  "recalculate_workbook",
  "open_workbook",
  "create_workbook",
  "activate_workbook",
  START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
  NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  COMMIT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME,
  START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
  NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
]);

const CHART_TOOL_NAMES = new Set([
  "range_chart",
  "native_graph", // dispatcher-only compatibility alias
  "active_graph",
  "native_chart",
]);

/**
 * The range-chart tool and the pre-rename alias the dispatcher still accepts.
 * Guards keyed to only one of the two silently stop firing, so they all go
 * through here.
 */
function isRangeChartToolName(name: string): boolean {
  return name === "range_chart" || name === "native_graph";
}

/**
 * Every chart operation that only reads, including the pre-rename ids a paused
 * run can still replay. Listing the reads rather than the writes is what makes
 * the classification below safe by default: a new or misspelled operation is
 * gated as a write instead of slipping past the workbook guards.
 */
const READ_ONLY_CHART_OPERATIONS = new Set([
  "read_range_chart",
  "get_range_chart_image",
  "read_native_graph",
  "get_native_graph_image",
  "read_active_graph",
  "list_active_graph_stylesets",
  "read_native_chart",
]);

function chartOperation(rawInput: unknown): string | null {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    return null;
  }
  const operation = (rawInput as Record<string, unknown>).operation;
  return typeof operation === "string" ? operation : null;
}

function isChartWriteCall(toolName: string, rawInput: unknown): boolean | null {
  if (!CHART_TOOL_NAMES.has(toolName)) return null;
  const operation = chartOperation(rawInput);
  return !operation || !READ_ONLY_CHART_OPERATIONS.has(operation);
}

/** Operation-aware shared-state classification for executor tool calls. */
export function isStatefulToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return isChartWriteCall(toolName, rawInput) ?? STATEFUL_TOOLS.has(toolName);
}

/** Operation-aware workbook-write classification for executor tool calls. */
export function isWriteToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return isChartWriteCall(toolName, rawInput) ?? WRITE_TOOLS.has(toolName);
}

/**
 * Classify a write for generic replay protection, excluding workload commits.
 *
 * @param toolName - Provider-visible executor tool name.
 * @param rawInput - Parsed arguments for operation-aware chart calls.
 * @returns Whether fingerprint and ambiguity guards apply.
 */
export function isReplayGuardedWriteToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return (
    isChartWriteCall(toolName, rawInput) ??
    REPLAY_GUARDED_WRITE_TOOLS.has(toolName)
  );
}

/**
 * Whether a confirmed successful write may be suppressed by semantic argument
 * fingerprint. Repeatable structural mutations stay ambiguity-guarded, but a
 * later identical call is treated as intentional and must reach the host.
 */
export function isConfirmedWriteDeduplicationCandidate(
  toolName: string,
  rawInput?: unknown,
): boolean {
  if (!isReplayGuardedWriteToolCall(toolName, rawInput)) return false;
  if (
    toolName === "copy_worksheet" ||
    toolName === "edit_range_structure" ||
    toolName === "insert_company_logo" ||
    toolName === "manage_named_range"
  ) {
    return false;
  }
  if (toolName === "manage_worksheet") {
    return recordValue(rawInput).operation === "activate";
  }
  return true;
}

/** Operation-aware run-plan gate for executor tool calls. */
export function isRunPlanRequiredToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return (
    isChartWriteCall(toolName, rawInput) ??
    RUN_PLAN_REQUIRED_TOOLS.has(toolName)
  );
}

export type ResearchRoute = "selected_sources" | "workbook_only";

export const SET_RESEARCH_ROUTE_TOOL: ToolDef = {
  name: "set_research_route",
  description:
    "Classify whether this request needs external facts/content from the user's " +
    "explicitly selected research sources or is entirely workbook-only. This is " +
    "a semantic decision: choose selected_sources when external evidence is needed; " +
    "choose workbook_only when the task can be completed solely from workbook data " +
    "and ordinary Excel operations, including refreshing existing FactSet formulas.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      route: {
        type: "string",
        enum: ["selected_sources", "workbook_only"],
      },
    },
    required: ["route"],
    additionalProperties: false,
  },
};

const SELECTED_SOURCE_PRE_RESEARCH_TOOLS = new Set(["generate_formula"]);

export function requiresExplicitResearchRoute(
  researchContext?: ToolCallContext["researchContext"]
): boolean {
  return researchContext?.selectionMode === "filtered";
}

/**
 * Parse the immutable research route selected for one executor run.
 *
 * @param rawInput - Untrusted model-authored tool arguments.
 * @returns A supported route, or `undefined` for invalid arguments.
 */
export function parseResearchRoute(rawInput: unknown): ResearchRoute | undefined {
  if (!rawInput || typeof rawInput !== "object") return undefined;
  const route = (rawInput as { route?: unknown }).route;
  return route === "selected_sources" || route === "workbook_only" ? route : undefined;
}

function isTerminalWorkloadStatus(rawInput: unknown): boolean {
  if (!rawInput || typeof rawInput !== "object") return false;
  const status = (rawInput as { status?: unknown }).status;
  return (
    status === "success" ||
    status === "warning" ||
    status === "failed" ||
    status === "recovered" ||
    status === "paused"
  );
}

export function shouldDeferToolUntilSelectedResearch(
  toolName: string,
  route: ResearchRoute | undefined,
  successfulResearchCallCount: number
): boolean {
  return (
    route === "selected_sources" &&
    successfulResearchCallCount === 0 &&
    SELECTED_SOURCE_PRE_RESEARCH_TOOLS.has(toolName)
  );
}

function normalizeCheckpointResearchCallCount(value: unknown): number {
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < 0
  ) {
    return 0;
  }
  return value;
}

export function isClientVisibleExecutorTool(toolName: string): boolean {
  return toolName !== "set_research_route";
}

export function requiresSourceConstrainedResearch(
  researchContext?: ToolCallContext["researchContext"]
): boolean {
  return (researchContext?.userFileIds.length ?? 0) > 0;
}

/**
 * Direct lookup must not bypass a source allowlist or uploaded-file request.
 * Those runs stay entirely on the selected research path.
 */
export function isDirectIdLookupSourceRestricted(
  researchContext?: ToolCallContext["researchContext"]
): boolean {
  return (
    requiresSourceConstrainedResearch(researchContext) ||
    researchContext?.selectionMode === "filtered" ||
    researchContext?.selectionMode === "user_files"
  );
}

export function requiresSuccessfulResearch(
  researchContext: ToolCallContext["researchContext"] | undefined,
  route: ResearchRoute | undefined
): boolean {
  return requiresSourceConstrainedResearch(researchContext) || route === "selected_sources";
}

/**
 * Model, template, and visualization builds can mention worksheet ranges and
 * tabular metrics without asking for one aligned research result. Keep those
 * tasks on the normal workbook-editing path.
 */
const WORKBOOK_BUILD_REQUEST =
  /\b(?:dcf|lbo|three[-\s]statement|3[-\s]statement|operating\s+model|financial\s+model|merger\s+model|credit\s+model|debt\s+schedule|precedent\s+transactions?|football\s+field|sensitivity\s+(?:table|analysis)|scenario\s+analysis|data\s+table|waterfall|pivot\s+table|template|chart|graph|dashboard)\b/i;
const COMPS_WORKBOOK_BUILD_REQUEST =
  /(?:\b(?:build|create|construct|generate|make|prepare|set\s*up)\b.{0,80}\b(?:trading\s+comps|comparable\s+compan(?:y|ies))\b|\b(?:trading\s+comps|comparable\s+compan(?:y|ies))\b.{0,80}\b(?:build|create|construct|generate|make|prepare|set\s*up)\b)/i;

/**
 * Identifies worksheet-scoped requests for a supported tabular topic.
 *
 * The model still supplies exact ranges after inspecting the workbook; this
 * detector only decides whether the gated run-scoped lifecycle should replace
 * the competing free-form research route.
 */
export function detectTabularQueryWorksheetWorkload(userRequest: string): boolean {
  const request = userRequest.trim();
  if (!request) return false;
  if (
    WORKBOOK_BUILD_REQUEST.test(request) ||
    COMPS_WORKBOOK_BUILD_REQUEST.test(request)
  ) {
    return false;
  }

  // Strip catalog topics before testing aggregate intent. Otherwise metric
  // names such as "Total Assets" and "Employee Count" look like requests to
  // summarize the list as a whole.
  const requestWithoutTopics = request.replace(
    TABULAR_TOPIC_TERM_GLOBAL,
    " ",
  );
  const hasExplicitPerRowTargets =
    /\b(?:each|every)\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(
      request
    );
  const hasSequencedPerRowAction =
    hasExplicitPerRowTargets &&
    /(?:\b(?:populate|fill|write|retrieve|research|find|look\s*up|answer|add)\b.{0,160}\b(?:then|after(?:ward)?s?)\b|\b(?:then|after(?:ward)?s?)\b.{0,160}\b(?:populate|fill|write|retrieve|research|find|look\s*up|answer|add)\b)/i.test(
      request
    );
  if (
    ASKS_ABOUT_THE_LIST_AS_A_WHOLE.test(requestWithoutTopics) &&
    !hasSequencedPerRowAction
  ) {
    return false;
  }
  // "These companies" or "for each company" points at the open list as plainly
  // as "column A" does, even when the list contains only one company.
  const referencesTheOpenList =
    /\b(?:these|those)\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?|names?)\b/i.test(
      request
    ) ||
    hasExplicitPerRowTargets ||
    /\ball\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(
      request
    );
  const hasWorksheetScope =
    referencesTheOpenList ||
    /\b(?:worksheet|sheet|column|range|cells?|rows?)\b/i.test(request) ||
    /\b[A-Z]{1,3}\d+\s*:\s*[A-Z]{1,3}\d+\b/i.test(request);
  const hasCatalogTopic =
    /\b(?:(?:latest|current|closing|close|end[-\s]+of[-\s]+day|eod)\s+)?(?:share|stock|equity)\s+prices?\b/i.test(
      request
    ) ||
    /\bprices?\s+per\s+share\b/i.test(request) ||
    TABULAR_TOPIC_TERM.test(request);
  const hasPerRowCompanyTargets =
    referencesTheOpenList &&
    /\b(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(request);
  const hasTabularTopic = hasCatalogTopic || hasPerRowCompanyTargets;
  const hasRequestedOutput =
    /\b(?:populate|fill|write|return|retrieve|research|find|look\s*up|answer|add)\b/i.test(request) ||
    // A per-row question asked directly, such as "Who are the current CEOs of
    // these companies?", requests one answer per row without naming a verb.
    (referencesTheOpenList && /^(?:who|what|which|when|where)\b/i.test(request));
  return hasWorksheetScope && hasTabularTopic && hasRequestedOutput;
}

/**
 * Questions about the list as a whole: one answer summarizing, counting, or
 * filtering the companies, rather than one answer per row.
 */
const ASKS_ABOUT_THE_LIST_AS_A_WHOLE =
  /\b(?:how\s+many|which\s+of|any\s+of|average|median|mean|total|sum|aggregate|count|compare|rank|sort|filter|top\s+\d+|bottom\s+\d+|summar(?:y|ize|ise))\b/i;

const EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST =
  /\b(?:aligned\s+)?tabular[-\s]+query(?:[-\s]+(?:workload|workflow|path|route))?\b/i;

/**
 * Gives an explicit tabular-query workflow request precedence over incidental
 * identity language such as an "Ultimate Parent Company" source-column label.
 */
export function shouldRouteToTabularQueryWorksheetWorkload(
  userRequest: string,
  enabled: boolean
): boolean {
  if (!enabled || !detectTabularQueryWorksheetWorkload(userRequest)) {
    return false;
  }
  if (EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST.test(userRequest)) {
    return true;
  }
  const idLookupIntent = detectIdLookupRoutingIntent(userRequest);
  return !idLookupIntent.active;
}

export interface WorksheetColumnRoute {
  route: "tabular" | "id_lookup" | "none";
  /** Catalog topic the request resolved to, when one was identified. */
  topicKey?: string;
  topicName?: string;
  score?: number;
  confidence?: WorksheetColumnIntent["confidence"];
  usedEmbeddings?: boolean;
  /**
   * The request is a terse identity-column alias whose worksheet scope comes
   * from the active company list rather than from explicit request wording.
   */
  inferredWorksheetList?: boolean;
  /**
   * Intent to use downstream. Either the detected intent, or one synthesized
   * from a classifier-identified identity topic the phrase detector missed.
   */
  idLookupIntent: IdLookupRoutingIntent;
  reason: string;
}

/**
 * Chooses the workload that owns an aligned worksheet-column request.
 *
 * The old gate required the request text to carry its own worksheet scope and
 * a verb from a fixed list, so terse phrasings such as "Pull CEO names" fell
 * through to free-form research. Scope now comes from the workbook itself and
 * subject matter from the topic classifier, with the lexical detector kept for
 * the case where embeddings are unreachable.
 */
export async function resolveWorksheetColumnRoute(input: {
  userRequest: string;
  tabularEnabled: boolean;
  companyListPresent: boolean;
  idLookupIntent: IdLookupRoutingIntent;
  signal?: AbortSignal;
}): Promise<WorksheetColumnRoute> {
  const { userRequest, tabularEnabled, companyListPresent } = input;
  const detected = input.idLookupIntent;
  const inactive: IdLookupRoutingIntent = { active: false, requestedFields: [] };

  // An explicit request for the tabular workflow outranks incidental identity
  // language, such as an "Ultimate Parent Company" source-column label.
  if (
    tabularEnabled &&
    EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST.test(userRequest) &&
    detectTabularQueryWorksheetWorkload(userRequest)
  ) {
    return {
      route: "tabular",
      idLookupIntent: { active: false, requestedFields: [] },
      reason: "explicit_tabular_request",
    };
  }

  // A deterministic user-request alias outranks identity language found only
  // in generated plan hints. This keeps requests such as "Pull CEOs" on the
  // tabular route even if a plan mentions an Ultimate Parent Company column.
  const aliasIntent = matchWorksheetColumnAlias(userRequest);
  const aliasDiagnostics = aliasIntent
    ? {
        score: aliasIntent.score,
        confidence: aliasIntent.confidence,
        usedEmbeddings: aliasIntent.usedEmbeddings,
      }
    : {};
  if (aliasIntent?.owner === "id_lookup" && aliasIntent.idLookupField) {
    // The phrase detector only claims a parent request when the word
    // "ultimate" appears, so "Fill parents" and "Parent co?" can arrive
    // inactive. Downstream eligibility reads `.active`, and the policy is
    // built from `requestedFields`, so synthesize the complete intent here.
    return {
      route: "id_lookup",
      topicKey: aliasIntent.topicKey,
      topicName: aliasIntent.topicName,
      ...aliasDiagnostics,
      inferredWorksheetList: companyListPresent,
      idLookupIntent: {
        active: true,
        requestedFields: [aliasIntent.idLookupField],
      },
      reason: "id_lookup_topic_classified",
    };
  }

  if (aliasIntent?.owner === "none") {
    return {
      route: "none",
      ...aliasDiagnostics,
      idLookupIntent: inactive,
      reason: "classified_not_per_row",
    };
  }

  if (aliasIntent?.owner === "tabular") {
    if (!tabularEnabled) {
      return {
        route: "none",
        ...aliasDiagnostics,
        idLookupIntent: inactive,
        reason: "tabular_disabled",
      };
    }
    if (!companyListPresent) {
      return {
        route: "none",
        ...aliasDiagnostics,
        idLookupIntent: inactive,
        reason: "no_company_list",
      };
    }
    return {
      route: "tabular",
      topicKey: aliasIntent.topicKey,
      topicName: aliasIntent.topicName,
      ...aliasDiagnostics,
      idLookupIntent: inactive,
      reason: "topic_classified",
    };
  }

  // The phrase detector remains authoritative when the request itself did not
  // resolve to a deterministic catalog alias.
  if (detected.active) {
    return {
      route: "id_lookup",
      idLookupIntent: detected,
      reason: "id_lookup_phrase_detected",
    };
  }

  if (!tabularEnabled) {
    return { route: "none", idLookupIntent: inactive, reason: "tabular_disabled" };
  }

  // Long-form requests already carry enough worksheet and per-row signal to
  // route deterministically, without unnecessary embedding I/O.
  if (shouldRouteToTabularQueryWorksheetWorkload(userRequest, tabularEnabled)) {
    return {
      route: "tabular",
      idLookupIntent: inactive,
      reason: "lexical_fallback",
    };
  }

  // Embeddings can only add a useful route when the active sheet supplies the
  // company rows that a terse request omitted from its text.
  if (!companyListPresent) {
    return {
      route: "none",
      idLookupIntent: inactive,
      reason: "no_company_list",
    };
  }

  if (!carriesClassifiableSignal(userRequest)) {
    return {
      route: "none",
      idLookupIntent: inactive,
      reason: "insufficient_signal",
    };
  }

  const intent = await classifyWorksheetColumnIntent(userRequest, {
    signal: input.signal,
  });
  const intentDiagnostics = {
    score: intent.score,
    confidence: intent.confidence,
    usedEmbeddings: intent.usedEmbeddings,
  };

  if (intent.usedEmbeddings && intent.confidence === "low") {
    return {
      route: "none",
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "low_confidence",
    };
  }

  if (intent.owner === "id_lookup" && intent.idLookupField) {
    return {
      route: "id_lookup",
      topicKey: intent.topicKey,
      topicName: intent.topicName,
      ...intentDiagnostics,
      inferredWorksheetList: true,
      idLookupIntent: {
        active: true,
        requestedFields: [intent.idLookupField],
      },
      reason: "id_lookup_topic_classified",
    };
  }

  if (intent.owner === "tabular") {
    return {
      route: "tabular",
      topicKey: intent.topicKey,
      topicName: intent.topicName,
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "topic_classified",
    };
  }

  // A confident non-routing verdict — formatting, modelling, or an aggregate
  // question — is respected. Anything else means semantic classification had
  // no signal after the deterministic alias and lexical checks both missed.
  if (intent.usedEmbeddings && intent.owner === "none") {
    return {
      route: "none",
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "classified_not_per_row",
    };
  }

  return {
    route: "none",
    ...intentDiagnostics,
    idLookupIntent: inactive,
    reason: "no_match",
  };
}

export function hasUsableResearchAnswer(output: unknown): boolean {
  if (!output || typeof output !== "object") return false;
  const answer = (output as { answer?: unknown }).answer;
  return typeof answer === "string" && answer.trim().length > 0;
}

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

/**
 * Progress events emitted by the runner for UI rendering or debugging.
 * Callers receive these via `ExecutorRunnerConfig.onEvent`.
 */
export type ExecutorRunEvent =
  | { type: "iteration_start"; iteration: number }
  | { type: "reasoning_delta"; iteration: number; text: string }
  | { type: "assistant_text_delta"; iteration: number; text: string }
  | { type: "assistant_text_reset"; iteration: number }
  | { type: "tool_call_started"; name: string; args: unknown; iteration: number; callId: string }
  | {
      type: "tool_call_completed";
      name: string;
      result: string;
      resultPreview: string;
      elapsedMs: number;
      callId: string;
      status: "ok" | "error";
      iteration?: number;
      researchCitations?: unknown[];
      researchCitedValuesByUuid?: Record<string, string>;
      observationMetadata?: Record<string, unknown>;
    }
  | { type: "research_sub_event"; callId: string; event: FinsterStreamSubEvent }
  | {
      type: "research_group_progress";
      groupId: string;
      name: string;
      iteration: number;
      completedRequests: number;
      totalRequests: number;
    }
  | {
      type: "workload_progress";
      callId: string;
      name: string;
      iteration: number;
      kind: "requests" | "closed_rows";
      completed: number;
      total: number;
      usable?: number;
      failed?: number;
    }
  | { type: "plan_state_changed"; planState: ExecutorPlanState }
  | { type: "status"; message: string };

export interface ExecutorProgressSnapshot {
  plan: ExecutionPlan | null;
  stepResults: string[];
  stepDisplayResults: string[];
  stepStatuses: ExcelStepStatus[];
  workbookEditAttempted: boolean;
  /**
   * Materializes a resume checkpoint on demand. Snapshots fire on every tool
   * completion, and building a checkpoint clones the entire run state, so the
   * cost is deferred to the callers that actually need one — in practice only
   * the error and abort paths. Calling it later returns the newest state,
   * which is exactly what a resume point should capture.
   */
  getCheckpoint: () => ExecutorRunCheckpoint | null;
}

// ---------------------------------------------------------------------------
// Runner configuration
// ---------------------------------------------------------------------------

/**
 * How a run reaches its operation's research evidence.
 *
 * The pack itself is owned by the in-memory operation registry, so the runner
 * receives it and reports updates rather than reaching into that module.
 */
export interface ExecutorResearchEvidenceConfig {
  readonly operationId: string;
  /** Policy this run resolved. Evidence from any other policy is not applied. */
  readonly policyFingerprint: string;
  readonly pack?: ExcelOperationEvidence;
  /**
   * Citations persisted on the paused message, used only when the in-memory
   * pack is gone — after a task-pane reload, for instance. They rebuild the
   * citation index so Notes still resolve, but they carry no locked answers,
   * so research stays open.
   */
  readonly fallbackCitations?: readonly unknown[];
  readonly onUpdate?: (pack: ExcelOperationEvidence) => void;
}

/**
 * Configuration for a single multi-hop executor run. Provided by
 * `ExcelApp.tsx` (or a test harness) at invocation time.
 */
export interface ExecutorRunnerConfig {
  /** Optional initial run steps from a compatibility planning path. */
  plan?: ExecutionPlan;

  /** Serializable state from a prior incomplete run. */
  resumeCheckpoint?: ExecutorRunCheckpoint;

  /** Optional visual-default brand selected for this run. */
  brandId?: ExcelBrandId | null;

  /** The user's original natural-language request. */
  userRequest: string;

  /** Workbook content snapshot for the model's initial context. */
  workbookContext: string;

  /**
   * Structured form of the same snapshot, used to decide whether the active
   * sheet holds a company list. Falls back to parsing `workbookContext`.
   */
  workbookSnapshot?: WorkbookContent;

  /** Compact prior chat transcript for follow-up context. */
  chatHistory?: string;

  /** Sheet captured at run start for safe apply fallback. */
  sessionLockSheet: string;

  /** Optional pinned workbook name for host writes. */
  pinnedWorkbookName?: string;

  /** Callback to apply workbook modifications through the host. */
  applyWorkbookModifications: ExecutorApplyWorkbookModifications;

  /** Callback fired when plan state changes (for live UI progress). */
  onPlanStateChange: PlanStateChangeCallback;

  /**
   * Moves the in-flight conversation/plan when a tool activates a new
   * workbook. Shared by apply_template and native_chart.
   */
  onWorkbookHandoff?: (
    handoff: ExecutorWorkbookHandoff,
  ) => void | Promise<void>;

  /**
   * Keeps the task pane on this conversation while a tool creates the workbook
   * it is about to hand off to. Returns a release callback.
   */
  beginWorkbookHandoff?: () => () => void;

  /** Optional structured event callback for fine-grained progress. */
  onEvent?: (event: ExecutorRunEvent) => void;

  /**
   * Optional sink for raw reasoning-summary deltas. The UI must transform these
   * through the polished trace narrator before displaying them.
   */
  onReasoningDelta?: (delta: string, iteration: number) => void;

  /** Optional callback for live partial progress used by failure recovery UI. */
  onProgressSnapshot?: (snapshot: ExecutorProgressSnapshot) => void;

  /** Optional abort signal for cancellation. */
  signal?: AbortSignal;

  /** Optional context for templates, skills, and the run's research scope. */
  toolCallContext?: ToolCallContext;

  /**
   * User turns prepended verbatim ahead of the JSON request envelope, used to
   * deliver user-authored Skill guidance. Each entry is sent byte-for-byte as
   * its own turn so the model sees guidance as instruction rather than data,
   * and so guidance can never be mistaken for part of the request payload.
   */
  preludeUserMessages?: readonly string[];

  /**
   * Research this operation already completed, and where to put what this run
   * adds. A resumed run seeds its citation index from the pack instead of
   * starting empty, replays the locked answers to the model, and appends new
   * calls so the next resume inherits them too.
   */
  researchEvidence?: ExecutorResearchEvidenceConfig;

  /**
   * Signal-aware request-init wrapper. Required by the backend proxy
   * for abort propagation. Same shape as `ExcelApp.tsx`'s `withSignal`.
   */
  withSignal: (init: RequestInit) => RequestInit;

  /**
   * Probes the currently active Excel context to discover the workbook
   * name after `apply_template` opens a new workbook. Used for re-pin.
   * Returns `{ workbookName?: string }`.
   */
  probeActiveContext?: () => Promise<{ workbookName?: string }>;

  /**
   * Whether the Intent Bridge is available (required for re-pin probing).
   */
  bridgeAvailable?: boolean;

  /** Run-start capability for generated citation Notes. */
  citationNoteCapability?: FactSetSpreadsheetNoteCapability;

  /**
   * Optional callback to refresh FactSet data mid-run. Injected from host.
   */
  refreshFactSetData?: ExecutorRefreshFactSetData;

  /**
   * Optional callback to trigger workbook recalculation mid-run.
   */
  recalculateWorkbook?: ExecutorRecalculateWorkbook;

  /** Optional Langfuse run context for grouping multi-hop LLM calls. */
  langfuseContext?: ExcelLangfuseRunContext;

  /** Server feature gate and independent tabular execution concurrency limits. */
  excelTabularQuery?: ExcelTabularQueryFeatureConfig;

  /** Focused-test injection; production uses the authenticated typed client. */
  tabularQueryClient?: TabularQueryWorkloadClient;

  /** Focused-test injection; production reuses the PR #535 lookup client. */
  tabularIdentityResolver?: TabularIdentityResolver;
}

export function buildExecutorSkillAssembly(
  selectedSkills: string[] | undefined,
  brandId?: string | null,
): { skillIds: string[]; skillBlock: string } {
  const skillIds = withPushSkills(selectedSkills);
  const brandDefaults = getExcelBrandDefaults(brandId);
  const skillBlock = brandDefaults
    ? buildSkillContextBlock(skillIds, {
        formattingStyleOverride: brandDefaults.formattingStyleOverride,
      })
    : buildSkillContextBlock(skillIds);

  return { skillIds, skillBlock };
}

// ---------------------------------------------------------------------------
// System prompt
// ---------------------------------------------------------------------------

/**
 * V0 executor system prompt. Instructs the model to follow the ReAct
 * observe -> act -> verify loop and use the exposed tools correctly.
 *
 * NOTE: prompt refinement (especially `modify_workbook` examples and
 * `retryReason` / `paused` guidance) is deferred to a follow-up prompt
 * tuning pass informed by real-model eval results.
 */
export const EXECUTOR_SYSTEM_PROMPT = `
You are the single agentic Execution Agent for the Finster Excel add-in. You own
the full workflow: decide whether the user needs a chat answer, a clarification,
or workbook edits; research external facts when needed; create run steps before writing;
execute against the LIVE Excel workbook using tools; verify your own work; and
return a concise final answer.

You are given: the user's request, optional prior chat history, optional current
run steps (each step has a stable id, a description, an action, and an advisory
targetSheet), and basic workbook context. Planning is separate from execution:
if no run steps exist and workbook edits are needed, the FIRST tool call for
that workbook-edit run MUST be set_run_plan. Do not call list_sheets,
read_range, any note/comment get tool, set_step_status, generate_formula, or any
write/chart tool before set_run_plan. If the request only needs an answer or a
clarification, do not invent run steps.

# Data provenance / no fabrication
Do not invent factual market, company, financial, forecast, formatting, chart,
or workbook data from model memory. Use real sources:
- Workbook cell/layout data must come only from list_sheets and read_range
  observations. Annotation content must come only from get_comment, get_note,
  get_all_comments, or get_all_notes.
- Numeric FactSet data and FactSet formulas should come from generate_formula /
  TTF outputs when a usable live formula exists, then workbook
  refresh/recalculation and read-back when values are needed. When a bounded
  formula attempt cannot produce the requested dataset, explicit company-disclosed figures may come from the
  research tool, provided its cited answer clearly supplies every written label,
  period, value, unit, and reporting basis.
- When the user explicitly requires SEC filings, annual reports, selected
  uploaded files, static sourced values, or no formulas, those source-constrained
  values must come from the FactSet AI / Finster research tool and must not be
  copied from workbook cells.
- External company facts, selected-file content, and qualitative research must
  come only from the FactSet AI / Finster research tools.
- When uploaded files are selected, the research tool is mandatory before the
  final answer and before using file-derived content in workbook writes.
  Other Excel tools remain available for independent workbook work. Questions
  about the selected file's rows, columns, records, contents, highlights, or
  summary must be answered from the research result, never from the open workbook.
- FactSet ActiveGraphs must come only from active_graph and its real catalog/tool
  output.
- Range charts must be based on observed workbook ranges. Native Chart Creator
  workbooks use explicit FactSet parameters and require an explicit chart type.
  Do not create made-up sample tables unless the user explicitly asks for
  sample/mock data.

# Native Chart Creator workbook operations
- native_chart is the default for a chart built from FactSet parameters. Its
  operations are exactly create_native_chart, read_native_chart, and
  update_native_chart.
- create_native_chart downloads and activates a complete new workbook, then
  re-pins this run and moves the in-flight conversation/plan to it. Creation
  then inserts the XLSX, refreshes the inserted sheets, fully recalculates, and
  verifies the native chart. Treat this lifecycle evidence as authoritative;
  do not continue against the prior workbook and do not call read_native_chart
  as redundant verification after a successful create.
- No native_chart operation writes a parameter cell. create_native_chart never
  discovers worksheet inputs or reads used ranges either; read_native_chart
  does, and update_native_chart only when this session no longer remembers the
  request the chart was built from.
- update_native_chart never edits a chart in place. It re-requests the chart
  from FactSet with the change merged into the parameters it was created with —
  or, when this session no longer has them, into what the worksheet still shows
  — and delivers it in another new workbook it moves this conversation to. The
  workbook you called it on keeps the chart it had. Never guess a sheet, label,
  or cell address, and never use a generic workbook write to bypass the tool. Tell the
  user the chart opened in a new workbook and that manual worksheet tweaks were
  not carried over.
- For a follow-up such as "add Netflix to the chart", use one chart plan step
  and call update_native_chart directly with the complete updated tickers and
  matching companyNames.single arrays. Do not use list_sheets, read_range,
  set_range_values, refresh_factset_data, or range_chart
  before or after it: update_native_chart owns the rebuild, its insertion,
  refresh, recalculation, and chart verification. This remains true in a later
  user turn; do not assume create-time run memory is available.
- A Chart Creator workbook is not a range_chart merely because its chart is a
  native Excel Chart object. Never use range_chart to read or verify it.
- Summary tables apply only to the native_chart chart types marked applicable by
  list_chart_types, and those types already include a horizontal table, so the
  summaryTable field carries overrides only. Send just what the user named: a
  display of Vertical or No, or up to five period slots to set, swap, or remove.
  Omit the field when they named neither, including for a plain chart request and
  for a bare "add a summary table" — there is nothing to override, and the tool
  will say so rather than rebuilding. FactSet is still rolling this feature out
  and no tool reads the table's own cells, so tell the user a table was requested
  rather than confirming it appeared, and never describe the periods a workbook
  currently shows unless read_native_chart reported them.
- Do not mark a chart step success, warning, or recovered until its chart tool
  returns completionEvidence.satisfied=true. Range-chart creation requires an
  observed source range, cleared refresh/recalc debt, and a successful write.
  ActiveGraph writes require successful preview/host mutation and OLE
  ActiveGraph read-back. Native creation requires workbook bytes,
  activation/re-pin, completed conversation handoff, inserted-sheet refresh and
  full recalculation. GetCharts is bounded best-effort metadata confirmation
  after those mutations: a timeout or a chart it does not report back does not
  undo a write Excel already applied, so such a step carries
  nativeChartVerified=false with a warning to pass on — never a second attempt
  at the same chart. A native update is held to those
  same requirements, because it is a fresh build. When the tool returns
  completionEvidence.satisfied=false it has already done what it could; mark
  that step warning, repeat its warning text to the user, and do not re-run the
  tool or reach for read_native_chart, refresh_factset_data, or range_chart to
  verify it by hand.
- completionEvidence.dataPopulated is reported separately from satisfied. When
  it is false the chart exists and is verified but Excel has not finished
  resolving every value; say so in the step note instead of retrying creation.
User-provided assumptions are allowed. Clearly labeled modeling assumptions are
allowed in an explicit assumptions block when the user asked for a model, but
never present an assumption as FactSet/company data. Never put guessed,
illustrative, or placeholder values in a source-data, historical, or market-data
block, and never use literal-only formulas such as =400000 to make sourced
outputs appear complete. If required factual data cannot be sourced, preserve
the source formula or leave the cell blank, mark the affected work failed or
partial, and explain the missing source. Do not overwrite it with static values.
Do not clear a sourced formula merely to remove an error or make verification
pass. Replacing a failed formula with an explicit cited-data fallback is allowed;
replacing it with worksheet status/diagnostic text is not. Keep failure
explanations in the final reply and step notes.

# User-facing communication
Your reasoning summary feeds a separate user-facing trace narrator. Reason
naturally and concretely about the workbook evidence, why the next action is
needed, and what you verified. Keep protocol details, tool ids, arguments,
channels, instruction hierarchy, retry policy, and JSON out of the final reply
and step notes. The final assistant output channel must remain JSON-only.
If a tool call returns a correctable sequencing instruction to create run steps,
follow that instruction exactly and continue. If a range write returns
code="range_matrix_shape_mismatch", correct the address or matrix dimensions and
call the SAME range-write tool exactly once. Do not terminate the step before
that corrected call, and never broadcast, pad, truncate, or replace formulas
with static values. A correctly shaped granular set_range_values or
set_range_formulas call that gets an ambiguous attempted-host failure is handled
readback-first by the runtime: use its recoveredByReadback/recoveryExhausted
result and do not blindly replay the original range.
Otherwise, if a tool call returns a generate_formula input-shape, query-size, or
batch-limit error with
retryable=true and retryWith="queries", correct the arguments and retry
generate_formula exactly ONCE with concise queries[] entries. For an input-shape
or size error, rebuild one entry per requested metric; for a batch-limit error,
submit only the unprocessed overflow entries.
If generate_formula returns status "partial" with unresolvedQueries and
continuationBatches, preserve every successful result and retry only those
unresolved entries, using the provided bounded batches. Perform at most one
continuation round for each unresolved entry; never resubmit successful queries.
Do not fail the source or dependent steps before that corrected call returns. If
the corrected call fails, or for any other tool error, do not retry with an
alternate path. Return JSON with mode "failed" and a user-facing explanation of
the workbook issue only. The one semantic-routing exception is
code="active_graph_metric_basis_mismatch": do not retry active_graph, but use
revise_plan once if needed and continue through the exact formula-backed
range-chart route defined below. That correction preserves the requested
metric; it is not an alternate-data fallback.
In final replies and user-facing status notes, do not describe remaining work
as a gap. Use "needs attention" or "remaining issue" instead.
Corrected intermediate issues are implementation details. Once a retry or
remediation is verified, do not mention the earlier issue in the final reply,
step note, or unresolved list.

# Operating loop (ReAct) - work ONE step at a time
First decide the turn type:
- answer: inspect/research as needed and answer in chat without writing.
- clarification: ask one concise question when the request is ambiguous or unsafe.
- workbook_edit: call set_run_plan (unless initial run steps were supplied), then
  follow the run steps.

For each step, in order:
1. OBSERVE - After set_run_plan has succeeded, inspect the workbook before
   acting. Call list_sheets to learn the REAL sheet names and used ranges. Call
   read_range to read the cells you will change or depend on. Never assume
   layout or names.
2. ACT - Call set_step_status(id, "running"), then apply the change with the
   appropriate write tool.
3. VERIFY - Call set_step_status(id, "verifying"), then use read_range or the
   corresponding annotation get tool and confirm the result matches the intent.
4. RECORD - Call set_step_status(id, terminal status, short note).
Only advance to the next step after recording the current one.
Focused write tools NEVER complete a step on
their own. A step is only done once you call set_step_status with a terminal
status. A step that has been written to but not recorded is still unfinished.

# Verification depth - budget by complexity, do not over-verify
Verify just enough to confirm the change landed; do NOT re-read the same cells
repeatedly or loop hoping values change. One confirming read_range (or matching
annotation get) per step is the norm. A cleanly applied static write is already
acknowledged by the host, so do NOT spend a read re-confirming it. Spend the read
where the outcome is genuinely uncertain: FactSet formula writes, and any write
whose result reported an error, a failed/skipped action, or an ambiguous host
acknowledgement. Use the run's estimatedComplexity as your budget:
- simple: 0-1 verification reads per step. When a single value / label / formula
  write already comes back confirmed by the write tool (cells written, no
  error), record the step WITHOUT a separate read.
- moderate: about 1 verification read per step.
- complex: at most 1-2 verification reads per step, on the cells that matter most.
When you do verify a formula write, read the exact written range with format
"json" and includeFormulas:true so the formula text at every requested
coordinate is visible; a truncated or values-only read is not evidence.
Never re-verify a step that is already in a terminal state. Spend remediation
reads only on a concrete, named issue (a specific cell that is wrong or missing) -
never on a vague "let me double-check everything" pass.

# Tool-call ordering - batch independent calls in ONE turn
Tool calls emitted in the same assistant turn are chosen before you see any
results, so they must be mutually independent. When they ARE independent, emit
them TOGETHER in a single turn - the runtime runs a read-only batch fully in
parallel, which is much faster than one call per turn. Do NOT artificially
serialize independent reads across many turns.

Batch together (same turn) whenever independent, e.g.:
- read_range on several different sheets/ranges you already know the names of.
- get_comment / get_note calls for independent cells whose sheets are known.
- list_sheets plus read_range on a sheet whose exact name you already know.
- generate_formula is best batched via its own "queries" array (see below),
  not via multiple separate tool calls.

Still serialize (one call, wait, then decide) ONLY when a later call depends on
an earlier result:
- Call apply_template alone. Wait for its result and workbook re-pin before
  calling list_sheets, read_range, or a focused write tool.
- Call list_sheets before read_range when you do NOT yet know the real sheet name.
- Call a focused write tool before read_range verification.
- Call set_step_status("running") before a write, then wait for its result
  before set_step_status("verifying" or a terminal status).

# targetSheet is ADVISORY
A step's targetSheet is an initial routing hint and may be stale or wrong. Authoritative
sheet names come from list_sheets. If the named sheet does not exist, pick the
correct one or create it if the step requires it, and pass the RESOLVED name to
the focused write tool. Do not blindly forward the step's targetSheet.

# Focused write tools
- set_range_values for one rectangular value write.
- set_range_formulas for one rectangular formula write.
- format_range for one formatting pass.
- clear_range for a non-shifting clear.
- edit_range_structure for insert, delete, move, or copy.
- layout_range for sizing, autofit, and row/column visibility.
- sort_range for ordered table/range mutations.
- manage_worksheet for create, rename, delete, move, or activate.
- copy_worksheet, manage_named_range, and insert_company_logo only for those
  specific operations.
Each call performs one bounded mutation. Sequence dependent writes and verify
the resulting workbook state rather than constructing a mixed batch.
For named ranges, manage_named_range requires a name and, for
create_or_update, a refersTo expression.

# Excel comments and notes
Excel comments and notes are different annotation types:
- A comment is a threaded purple Excel comment, with author metadata.
- A note is the legacy yellow sticky note attached to a cell.
Never substitute one for the other and never write annotations through a range
content or formatting tool.

Use get_comment / get_note to observe one cell before changing it and to verify
the matching write. Each get_all_comments / get_all_notes call targets exactly
one worksheet observed from list_sheets; emit independent per-sheet calls
together when several sheets are in scope. Use set_comment / set_note and
delete_comment / delete_note for writes, preserving the current run stepId.
When setting multiple comments or notes on one worksheet, put all targets in
one set-tool entries array so they share one atomic apply and undo bracket.
For set tools, replaceExisting=false safely fails if that annotation already
exists; use replaceExisting=true only when the user's intent is to replace it.
Delete tools accept address batches and are idempotent: deleting a missing
comment or note is still a successful operation. Verify a write with the
corresponding get tool, not read_range, because ordinary cell reads do not
return annotation content.

# Citation Notes for researched workbook content
When workbook content comes from finster_query / factset_ai_query, use judgment
to attach exact citation IDs only to citation-worthy hardcoded research inputs:
- Always cite a hardcoded number copied from research in the cell containing
  that number.
- Cite non-numeric content only when the cell states a substantive claim that
  could be challenged or verified, such as a business assertion, management
  quote, guidance statement, causal explanation, risk, or strategic conclusion.
- Descriptive metadata is not a claim. Do not cite company/entity names,
  tickers, filing/report types, filing/publication dates, fiscal-period labels,
  units, metric names, or source labels when they merely identify a cited value.
- In a factual table row containing descriptors plus a sourced number, put the
  citation on the number cell only. The surrounding company, filing, period,
  metric, and units cells remain uncited.
- Prefer one clear anchor cell per independent sourced fact. Do not duplicate
  the same citation across nearby labels, units, captions, or restatements merely
  because they came from the same research response.
- Do not cite generic row/column labels, headings, decorative or structural
  cells, blank cells, repeated unit text, "Source" caption rows, or formulas and
  derived outputs whose lineage is already visible through cited input cells.
- For set_range_values, including 1x1 writes, use a rectangular citationIdsMatrix
  aligned exactly with values. Each coordinate is
  null when uncited or a non-empty array of exact IDs when cited.
- Never invent, shorten, transform, or reuse an ID from a different claim. If
  the needed ID is absent, still write otherwise valid sourced content without
  citationIds.
- Do not author citation Note text and do not call set_note for citations. The
  write runtime attempts yellow Excel source Notes on a silent best-effort
  basis and preserves user-authored Notes.
- Citation Note attachment and read-back never affect workbook-write success.
  Do not retry, downgrade a step, or mention a missing/unverified source Note.
- Use normal set_note only for user-provided assumptions or other intentional
  non-citation Notes. Ordinary generate_formula/FDS cells are self-auditing
  through their visible formula and do not need citation IDs unless external
  research evidence was explicitly used for that cell.

# Chart output modes
${buildChartOutputModesPromptBlock()}
Route every chart request by its data source, then by where the user wants it:
- Data already in the workbook, or data you are about to write there, goes to
  range_chart in the current workbook.
- Data that comes from FactSet parameters goes to native_chart, which opens a
  new workbook with the FactSet template, its underlying data, and a summary
  table where the chart type supports one.
- Use active_graph only when the user asks to keep the chart on the current
  sheet, or explicitly asks for an ActiveGraph / live FactSet OLE object.
For parameter-driven requests, require an explicit Chart Creator chart type;
never guess P_PRICE or P_INDEX. Call list_chart_types and ask the user which
chart they want when the request does not name one.

# Forward / NTM P/E chart fidelity
An explicit forward-P/E request includes wording such as forward P/E, fwd P/E,
NTM P/E, next-twelve-month P/E, 1-year forward P/E, or FY1 P/E.
- \`VAL_PE2\` is a trailing/LTM P/E ActiveGraph. NEVER use it for an explicit
  forward/NTM P/E request, even if the requested forward series is unavailable.
  A different startDate changes only the displayed time window; it does not
  change the earnings basis.
- Plan an exact forward-P/E source-data step and a dependent range-chart
  step. Use generate_formula with the requested company, explicit forward
  earnings basis, frequency, and full historical window. Preserve that window;
  do not shorten it to obtain a non-empty result.
- After writing the exact Date and forward-P/E series, refresh FactSet data,
  recalculate, and read back the complete source range before creating a chart.
  The generate_formula result plus read-back must verify the forward basis, the
  requested historical coverage, aligned Date/value rows, and usable numeric
  observations. A formula that resolves only at its anchor, a shorter window,
  blank/error-only values, or trailing/LTM data does not qualify.
- Only after those checks pass may range_chart create the chart from that
  observed Date/value range. Never create the chart before refresh/read-back
  and never use active_graph or native_chart as a fallback.
- If active_graph returns code \`active_graph_metric_basis_mismatch\`, do not
  retry it and do not substitute another valuation chart. Use revise_plan once
  when the current steps need rerouting, then continue only through the exact
  formula-backed route above.
- If generate_formula immediately reports dataUnavailable, or the bounded
  correction plus refresh/recalculation still yields no usable observations or
  incomplete requested coverage, create no chart. Record any source cells as
  unavailable under the normal n/a policy, mark the dependent chart step failed,
  return mode \`partial\` when other requested work succeeded, and say:
  "FactSet returned no verified 1-year forward P/E observations for the requested
  period. I did not substitute trailing/LTM P/E, and no chart was inserted."
  Adapt "1-year" only when the user requested another exact forward basis.

# Range charts from workbook cells
Chart requests based on worksheet data ARE supported through range_chart.
Use range_chart for chart-from-cells requests such as "create a chart from A1:C10",
"plot this table", "add a clustered column chart near the data", or "update this
chart's series/colors/title".
- Treat any request to create a range chart in the workbook as workbook_edit
  from the start. The FIRST tool call must be set_run_plan, even if you still
  need to inspect the workbook, generate formulas, or prepare source data.
- For company revenue / financial metric range charts, establish run steps first,
  then observe the workbook, generate/write the source data table or formula
  range, and only then call range_chart.create_range_chart from that exact range.
- Do not call set_step_status, generate_formula, set_range_values,
  set_range_formulas, or range_chart write operations before set_run_plan
  when the final outcome is a workbook chart.
- Set sourceRange to the observed A1 range, e.g. "A1:C10".
- Prefer chartType "xlColumnClustered" as the clear default for generic sample
  data unless the user asks for a different standard Excel chart type.
- Put the chart on the resolved sheetName.
- Place it near the data with either a point position (top/left/width/height)
  or a legacy cell anchor position.startCell. A good default size is about
  width 400-520, height 280-340.
- Use chartInfo for title, legend, axes, chartArea, plotArea, etc. For a simple
  title, set title and/or chartInfo.title.text.
- To update an existing range chart's bar/column colors, use
  range_chart.update_range_chart_series with chartIndex, seriesIndex,
  series.fill.colorRgb, and point fills for the visible bars when you know the
  number of data rows. For a single-series column/bar chart, each visible bar is
  a series point; setting only series.fill may be accepted by the host without
  repainting the displayed bars.
- Do NOT use chartInfo.series or update_range_chart / UpdateExcelChart for series
  colors. UpdateExcelChart is chart-level only and docs say it ignores series.
  If a series operation returns applied:false without retryReason, mark the step failed
  with the host error instead of retrying with UpdateExcelChart.
- Example range_chart create call:
  {
    "operation": "create_range_chart",
    "stepId": "step_2_chart",
    "sheetName": "Sheet1",
    "sourceRange": "B2:C8",
    "chartType": "xlColumnClustered",
    "position": { "top": 100, "left": 260, "width": 480, "height": 320 },
    "title": "Sample Data",
    "chartInfo": {
      "title": { "hasTitle": true, "text": "Sample Data" },
      "legend": { "hasLegend": false }
    }
  }

# FactSet formulas
This section applies ONLY to ordinary numeric FactSet values/formulas that
belong in workbook cells. It does not apply when the user requires a selected
uploaded file or named document source, requests SEC/annual-report values,
asks for static sourced values, or prohibits FDS/FactSet formulas or mapping
from nearby cells. In those cases call the research tool before the focused
write tool, use its evidence, and do NOT use generate_formula / TTF / FDS
or copy an adjacent workbook value. The user's explicit source and output
constraints override the default formula route.
If a step needs external company facts, SEC/transcript/IR content, management
commentary, business strategy, competitor narrative, or other qualitative
research, call the research tool before the focused write tool and do NOT use
generate_formula / TTF / FDS for that content.
For answer-only external research turns, do not say you could not retrieve or
access company documents unless you actually called the research tool and it
returned an error or no relevant evidence.
When a step needs a FactSet/FDS formula and the exact formula is not already
known from the workbook, call generate_formula with the user's natural-language
intent and relevant identifiers. Do not invent FactSet formulas from memory.
Every executor call to generate_formula MUST pass a non-empty "queries" array.
This includes calls for only one metric (use a one-item array).
The singular "query" field is not available. Put exactly one concise metric in
each array entry; when a step needs several metrics, use several entries in the
same call.
Never combine comma-separated or conjunction-separated metrics in one entry.
Keep repeated parameters constant across related queries whenever possible
(same ticker, period syntax, currency, units, and date language) so the TTF
service can benefit from server-side caching. Group related metrics naturally,
e.g. market data, historicals, consensus, or balance sheet. Read each entry of
the returned "results" array (aligned in order with your queries). A partial
response preserves successful entries and lists only unresolved entries in
"unresolvedQueries" and "continuationBatches"; retry only those entries once.
For a time-series table with a Date column, use the companion shape
\`=FDSC("-", identifier, "julian(<the exact returned value series>.dates)")\`.
Preserve its identifier, start/end inputs, and frequency. This returns true
Excel serial dates; format the entire
spill as \`dd-mmm-yyyy\` by default (for example, \`10-Jul-2026\`) unless the
user explicitly requests another date format. Do not use
\`JULIAN(P_DATE(...))\`, \`P_DATE()\`, SEQUENCE, WORKDAY, or calendar arithmetic
for the displayed date axis, and do not otherwise alter the returned series
expression. A Date header without a written date-axis formula is incomplete and
must not pass verification.
Never stack synonyms, repeat clauses, or concatenate alternate phrasings inside
one query. If generate_formula returns the recoverable size error, reconstruct
the concise queries[] from the step's original metric needs rather than trying
to split or salvage the repeated text. For a recoverable batch-limit error,
retry only the unprocessed overflow entries.
When writing formulas, set_range_formulas.formulas must be a non-empty
rectangular 2D matrix whose dimensions match the target address. Never call
set_range_formulas with formulas: []. If generate_formula returned usable
formulas, copy those exact returned formula strings into the matrix; if it did
not, do not attempt the formula write and mark the source step warning/failed
with the missing formula evidence. The sole approved transformation is a
matching time-series Date companion that wraps an exact returned value-series
expression as \`julian(<series>.dates)\`, as described above.
Use each result's formatHint when formatting its output. A
percentage_points result already returns whole percentage points (75 means
75%, and 0.3 means 0.3%): keep the exact formula/value unchanged and use the
recommended literal-percent format such as \`0.00"%"\`. A decimal_fraction
result may use Excel's multiplying \`0.00%\` format. If formatHint is unknown,
do not infer percentage scale from magnitude, a header, or nearby cells.
Never replace a FactSet formula written in this run with a guessed value or a
literal-only formula merely to clear an error. A cited disclosure fallback
containing explicit sourced figures is allowed after the bounded formula path
fails; otherwise only convert a sourced formula to a static value when the user
explicitly requested that conversion.
Treat upstream validated=true as syntax/entitlement evidence, not proof that the
formula matches the user's requested dataset. Use the formula, template,
description, commentary, and placeholders to reason about whether the result
preserves the requested metric, dimensions, granularity, period, currency, and
units. If it does not, retry once with a concise clarification that preserves
the original intent; do not silently substitute a different dataset.
If the bounded formula attempt still cannot produce the requested dataset and
the user did not require a live formula, use the research tool as a sourced
fallback. Ask for the exact requested breakdown and time window. Build a static
source table only from explicit cited figures whose labels, periods, units, and
reporting basis are unambiguous; leave unsupported cells blank. Add a concise
source note identifying the cited disclosure basis, then calculate and chart
from the observed table normally.
For workbook edits, when generate_formula returns formula_template + placeholders,
do NOT write the inlined formula directly. Bind every placeholder to visible
input cells and construct the workbook formula from formula_template using cell
references. This includes ticker, period, currency/unit, and date/as-of/start/end
date placeholders.
Handle date-named placeholders before generic literals. Absolute dates go into
numeric \`=DATE(year,month,day)\` cells with a date number format and are
concatenated through raw cell references; never write them as text. Relative
FQL tokens such as \`-9D\` and \`0D\` remain text and must be ordered from
earlier Start to later End (for example, \`-9D\` through \`0D\`, never
\`0\` through \`-9D\`). generate_formula automatically restores an exactly
reversed TTF pair when the query explicitly states the ordered range. If a
reversed-range error remains, retry once with clearly labelled start and end
dates; do not stop after the first recoverable formula-generation error.
${FQL_DATE_INPUT_GUIDANCE}
Only use the exact returned inlined formula when formula_template is absent. After
writing FactSet formulas, verify the cells contain formulas in the intended
locations; do not treat temporary loading values as failure unless the formula
text itself is wrong or missing.
For any requested multi-dimensional or time-series table, reason about the
expected source shape before building dependent calculations or charts. Verify
that the read-back contains the requested labels, dimensions, and observation
count. Do not build downstream outputs from a materially different or incomplete
source shape.
After refresh, compare every value-series row count with its matching Date
axis. If the Date/peer series spans multiple rows but a requested metric only
populates its anchor or first row, treat that as a recoverable wrong-series
formula rather than missing observations. Re-query for one observation per
trading date as a daily frequency time series, replace the formula, refresh,
and verify again. Do not finish with a warning while that safe remediation
remains.
For raw per-security closing, opening, high, low, intraday, bid, ask, or VWAP
outputs created in this run, use \`0.0000\` as the default display format.
If the user explicitly requests another number of decimal places, their request
overrides the default. Apply the chosen format to the entire reserved spill
range and verify the written numberFormats before completing the formatting step.
That four-decimal default never applies to percentage-point return items such as
\`P_PRICE_RETURNS\` and \`P_TOTAL_RETURN\` — those keep a literal-percent format
such as \`0.00"%"\` even when the request mentions share price.

# FactSet ActiveGraphs
active_graph inserts a live FactSet ActiveGraph (an OLE object) on the current
sheet. Use it for parameter-driven requests only when the user wants the chart
to stay in the sheet they are working in, or explicitly asks for an ActiveGraph;
otherwise parameter-driven requests go to native_chart.
- \`VAL_PE2\` means trailing/LTM P/E only. Explicit forward/NTM/FY1 P/E
  requests must follow the verified formula-backed range-chart route above.
- Use active_graph with operation "create_active_graph" for a new on-sheet
  FactSet ActiveGraph.
- Use active_graph with operation "edit_active_graph" only when the
  user selected an existing ActiveGraph, or after active_graph.read_active_graph identified an
  explicit sheetRef + chartIndex target for an ActiveGraph OLE object.
- For existing-chart edits, prefer the selected ActiveGraph. If target selection
  is uncertain, call active_graph.read_active_graph first and use only ActiveGraph/OLEObject
  targets. Do not imply that range charts or Chart Creator workbooks can be
  edited through this tool.
- Do not hand-roll FDSChart XML. Provide complete chartParameters; active_graph
  generates the FDSChart XML through Chart Creator and inserts/updates it.
- Time range: there is NO "period" field. Express ranges with
  startDate/endDate/frequency. "last N years" -> startDate "-NAY" (e.g. "-3AY"),
  endDate "NOW". Omitting startDate yields only the last 1 year. For multi-year
  fundamentals (revenue, EPS) set frequency "Q" or "Y" (the "D" default is daily).
- Set chartParameters.companyNames.single to the resolved company name(s)
  (e.g. ["Apple Inc."]) so the chart identifies the entity rather than showing
  only the metric name.
- Place charts with target.coords only when needed. Use top/left placement; the
  ActiveGraph insert path normalizes the shape frame after creation. When adding
  more than one chart, call active_graph.read_active_graph first and use a compact grid with modest
  gaps so the charts sit near each other without overlapping.
- When the user's ActiveGraph request implies a family of charts or would be
  more useful as several charts, consider creating a small set of appropriate
  ActiveGraphs rather than arbitrarily choosing one.
- Do not use active_graph for charts built from workbook ranges. Use
  range_chart.
- Do not return chart bubbles or terminal chart payloads. If a FactSet
  ActiveGraph should be created or updated in the workbook, use active_graph.
  If the user only needs chart guidance in chat, answer in text.
- Tool names and operation ids are implementation details. Do NOT include
  "active_graph", "create_active_graph", "edit_active_graph", or
  similar tool identifiers in set_step_status notes, revised run-step descriptions,
  or other user-visible progress text. Say "Create a FactSet ActiveGraph" or
  "Edit the selected FactSet ActiveGraph" instead.

Broad ActiveGraph guidance:
${buildFactSetMultiChartIntentPromptBlock()}

Valid FactSet ActiveGraph chartType codes:
${buildFactSetCatalogPromptBlock()}

# Handling retryReason (paused host)
If a focused write returns applied:false with a retryReason (e.g. "stuck_busy"
or "stuck_edit"), do NOT retry. Instead, immediately call
set_step_status(id, "paused", "Excel is busy") and move on.

# When the run steps need to change
If a step is missing, mis-ordered, or impossible, call revise_plan with the FULL
revised run step list. Reuse the EXACT existing id for every step that already exists;
only mint new step_<n>_<slug> ids for genuinely new steps.

# Status semantics
Your selected terminal status is authoritative and is shown to the user as
chosen. Decide from the final observed workbook state, not from wording in the
note. Before selecting warning, ask: "What concrete issue still affects the
client now?" If the answer is none, do not select warning.
running = actively working the step.
verifying = applied a change and reading back to confirm.
success = verified; the step achieved its intent.
warning = a client-impacting caveat still remains at the end of the run.
failed = could not complete; not recoverable this run.
paused = blocked on a transient host condition; retryable.
recovered = an earlier issue was fixed and the final result is verified. Report
the successful outcome only; do not retain the temporary warning detail.
Never use warning merely to document a retry, remediation, or issue that has
already been corrected. Put only the final outcome in the note.

# Rules
- Read before you write. Verify after you write.
- Prefer small, targeted reads. Use read_range format "csv" for plain
  value/label reads and value verification (compact, values only). Only use
  format "json" with includeFormulas:true when you need to read or verify
  formula TEXT, or includeFormats:true when you need number formats / styles.
- One focused write call equals one bounded mutation within a logical step.
- Never fabricate results. If read-back shows the change did not take, mark the
  step failed or paused with a note.
- If verification finds a small correctable issue, fix it with another targeted
  focused write call, then verify again. After a successful correction, mark
  the step recovered or success and describe only the final outcome. Use warning
  only if a concrete client-impacting issue remains; if the issue is ambiguous
  or risky, mark the step warning/failed with that specific remaining issue.
- Do not write status, diagnostic, "needs attention", reason, or next-step blocks
  into the workbook unless the user explicitly requested an in-sheet status
  report. Keep failure explanations in the final reply and step notes.
- Do not loop forever.

# Formatting discipline
- When adding or modifying cells, reading the adjacent cells with includeFormats:true
  FIRST can be helpful.
- Year values (2024, 2025, etc.) are integers — use numberFormat "0" or null
  (General), not a thousands-separator format like "#,###.00".
- You do not need to invent formatting. If adjacent cells use a specific format code, you can copy it
  for cells in the same logical row or column. Use your best judgement here.
- If a run step's action or description says format, formatting, layout, polish,
  table styling, or clean presentation, value/formula writes alone are not enough.
  Apply a concrete format operation before marking that step success: use
  format_range for headers, input cells, output cells, number formats,
  borders/fills, and alignment; use layout_range for autofit. If formatting
  cannot be applied, mark the step
  warning with the reason rather than success.

# Refreshing & Recalculating - ONE consolidated pass, near the end
- FactSet formulas (=FDS, =FDSB, =FDSC, =FDSRC, =FDT, etc.) show #Calc or #NAME?
  until refreshed. That is EXPECTED mid-run and is NOT a failure.
- When the user asks only to update an existing model to the latest date/data,
  update the workbook's existing date control, refresh, recalculate, and read
  back the outputs. If no new facts or formulas are needed, do not call FactSet
  AI / Finster research or generate_formula.
- Do NOT refresh or recalculate after every step. Mid-run, verify a FactSet
  write by confirming the FORMULA TEXT is present in the intended cells
  (read_range with format "json", includeFormulas:true) - not by resolving its
  value. A correct formula in the right place confirms placement, but keep the
  source step verifying while it still shows #Calc when downstream work or the
  requested deliverable depends on resolved values.
- Once you have written ALL the FactSet formulas this run needs, do ONE
  consolidated pass: call refresh_factset_data once with scope "worksheets" and
  every relevant sheet name, or scope "workbook" when every worksheet genuinely
  needs refreshing. Do not refresh each worksheet separately. Then call
  recalculate_workbook with mode "full" and read back the affected ranges.
- Refresh/recalc EARLIER than the end only when a later step in THIS run
  genuinely needs the resolved value (e.g. a downstream formula keys off the
  computed number) or the user explicitly asked to see resolved values mid-run.
- Only call these tools when you wrote FactSet formulas this run or the user
  explicitly asked to refresh existing FactSet formulas. Do NOT call them
  speculatively. If refresh or recalc fails (ok:false), diagnose the returned
  error. Do not repeat refresh merely because a read remains unresolved; mark
  the affected step "warning" and note when manual refresh is needed.

# Formula error classification & self-correction
After the consolidated refresh + recalculate pass, read back the cells and classify the result:
- Values resolved correctly (numbers, text, dates) → step is "success".
- #CALC or #NAME? still showing after the consolidated refresh, full recalc, and
  readback → inspect the formula and FactSet add-in availability. Do not start
  another identical refresh merely because these values remain unresolved; if
  the formula and add-in cannot be corrected, mark the affected step "warning".
- #NUM!, #VALUE!, #N/A, #REF! after a successful refresh → treat the FORMULA
  ITSELF as wrong first (bad parameters, invalid period, wrong data item).
  Never rationalize a first-attempt #NUM!/#VALUE! as acceptable. Instead:
  1. Read the erroring cell's formula.
  2. Diagnose the likely cause (wrong period format, invalid identifier,
     incorrect function arguments).
  3. Call generate_formula again with corrected parameters, or manually fix the
     formula using set_range_formulas.
  4. Refresh and recalculate again, then verify.

# Unavailable data - always land on a definite answer
FactSet genuinely does not carry every metric for every company and period.
Once a coordinate has had ONE corrected generate_formula attempt plus ONE
refresh + recalculate round and still errors or remains blank - or generate_formula returned
dataUnavailable:true for it - the data is unavailable. Do NOT try a third
formula, do NOT refresh again, and do NOT leave the cell erroring. Instead:
  1. Overwrite those exact coordinates with the literal text n/a using
     set_range_values (a plain string, not a formula and not an error token).
  2. Name the affected cells and the metric in the step note, e.g.
     "Free Cash Flow 2027E-2029E unavailable from FactSet; wrote n/a".
  3. Mark the step "success" - the step delivered everything that exists.
  4. Mention the unavailable metric once in the final reply. Do NOT list it in
     "unresolved": it is a finished outcome, not outstanding work.
An n/a cell counts as delivered content and passes verification. Never end a
run still owing work on a coordinate you have already declared unavailable.
This success rule applies to the unavailable source coordinate, not to a
dependent deliverable that could not be created. In particular, when verified
forward/NTM P/E observations are unavailable, create no chart and mark the
dependent chart step failed as required by the forward-P/E fidelity rules.

# Finishing
For workbook-edit turns, every step must be terminal before the final answer. If
the runner tells you to stop, do not call more tools; mark unfinished work
as partial in the final json. For answer and clarification turns, no run steps or
step status are required.
Before returning final JSON, review all warning steps. If the note says the
issue was fixed, corrected, resolved, remediated, or verified clean and no
separate problem remains, change the status to recovered or success and clear
the warning note. The reply and unresolved list may contain only issues that
still affect the delivered workbook.
Workbook-edit success also requires a clean read-back of delivered formula
outputs after any required refresh/recalculation. Do not report success while
delivered cells still show #NUM!, #N/A, #VALUE!, #REF!, #DIV/0!, #NAME?,
#CALC!, or #SPILL!.

When complete, stop calling tools and return only json:
{
  "mode": "answer" | "clarification" | "workbook_edit" | "partial" | "failed",
  "reply": "concise user-facing response. Put execution details in tool/status notes, not here.",
  "unresolved": ["optional list of remaining issues"]
}
`.trim();

/**
 * Assemble the executor system prompt at call time. Appends the brand-aware
 * research-tool instruction ({@link getFinsterQueryToolInstruction}) to the
 * base {@link EXECUTOR_SYSTEM_PROMPT}. Resolved at runtime (not module-eval)
 * so the FactSet vs dev brand is current and the prompt can never embed a
 * stale/undefined import.
 */
export function buildExecutorSystemPrompt(
  researchContext?: ToolCallContext["researchContext"],
  options: {
    includeIdLookup?: boolean;
    includeIdLookupWorkload?: boolean;
    includeTabularQueryWorkload?: boolean;
  } = {}
): string {
  const researchRouteInstruction = requiresExplicitResearchRoute(researchContext)
    ? `

# Explicit selected-source routing
The user has explicitly filtered the available research sources. Before any
other tool call or final answer, call set_research_route exactly once:
- selected_sources: the request needs external facts or content. Query the
  research tool with the selected source scope before using TTF, FactSet
  formula generation, or another external-data path.
- workbook_only: the request can be completed entirely from workbook data and
  ordinary Excel operations. Refreshing existing workbook FactSet formulas is workbook_only;
  research is not required.
Base this decision on the meaning of the request, not on keyword matching.
`
    : "";
  const includeIdLookup = options.includeIdLookup === true;
  const includeIdLookupWorkload = options.includeIdLookupWorkload === true;
  const idLookupInstruction =
    includeIdLookup || includeIdLookupWorkload
      ? getIdLookupToolInstruction({
          includeDirectLookup: includeIdLookup,
          includeWorksheetWorkload: includeIdLookupWorkload,
        })
      : "";
  const tabularQueryInstruction =
    options.includeTabularQueryWorkload === true ? getTabularQueryWorkloadInstruction() : "";
  return (
    EXECUTOR_SYSTEM_PROMPT +
    idLookupInstruction +
    tabularQueryInstruction +
    researchRouteInstruction +
    getFinsterQueryToolInstruction(undefined, researchContext, {
      includeIdLookup,
      includeIdLookupWorkload,
    })
  );
}

export function buildEffectiveResearchContext(
  toolCallContext?: ToolCallContext,
  pinnedWorkbookName?: string,
  sessionLockSheet?: string
): ToolCallContext["researchContext"] | undefined {
  if (toolCallContext?.researchContext) {
    return {
      ...toolCallContext.researchContext,
      workbookName: pinnedWorkbookName ?? toolCallContext.researchContext.workbookName,
      sheetName: sessionLockSheet ?? toolCallContext.researchContext.sheetName,
      ...(sessionLockSheet ? { sheetNumber: undefined } : {}),
    };
  }

  if (!pinnedWorkbookName && !sessionLockSheet) {
    return undefined;
  }

  return {
    dataSources: [],
    userFileIds: [],
    scopeAvailable: false,
    ...(pinnedWorkbookName ? { workbookName: pinnedWorkbookName } : {}),
    ...(sessionLockSheet ? { sheetName: sessionLockSheet } : {}),
  };
}

/**
 * Ingest one completed research-tool result into both existing source-card
 * accumulators and the run-scoped citation index used by workbook writes.
 * Citation ingestion is intentionally independent of source-card extraction:
 * a supported raw citation must remain usable even if it has no UI card.
 */
export function ingestResearchToolResult(
  toolOutput: string,
  retrievedSources: ExcelAgentSource[],
  retrievedCitations: unknown[],
  accumulators: ExecutorRunAccumulators,
  researchCitations?: unknown[]
): void {
  let parsed: Record<string, unknown> | undefined;
  try {
    const value = JSON.parse(toolOutput) as unknown;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    // Non-JSON research outputs still retain their timeline preview.
  }

  const citations = Array.isArray(researchCitations)
    ? researchCitations
    : Array.isArray(parsed?.citations)
      ? parsed.citations
      : [];
  const sources = extractSourcesFromCitations(citations);
  if (sources.length > 0) retrievedSources.push(...sources);
  if (citations.length > 0) retrievedCitations.push(...citations);

  const response = { ...(parsed ?? {}), citations };
  ingestAgentResponse((accumulators.citationNoteIndex ??= {}), response);
  Object.assign((accumulators.retrievedCitedValuesByUuid ??= {}), extractCitedValuesById(response));
}

// ---------------------------------------------------------------------------
// Runner entry point
// ---------------------------------------------------------------------------

/**
 * Runs the multi-hop executor agent for a single Excel request. When an
 * initial plan is supplied this preserves the existing edit-executor path;
 * otherwise the agent can answer, clarify, or create a run plan in-loop.
 *
 * @param config - Run configuration including the plan, callbacks, and host ops.
 * @returns Edit-compatible `ExecutorRunResult` or lightweight answer result.
 */
export async function runMultiHopExecutor(
  config: ExecutorRunnerConfig
): Promise<ExecutorUnifiedRunResult> {
  const {
    plan,
    resumeCheckpoint,
    userRequest,
    workbookContext,
    chatHistory,
    sessionLockSheet,
    pinnedWorkbookName,
    applyWorkbookModifications,
    onPlanStateChange,
    onWorkbookHandoff,
    beginWorkbookHandoff,
    onEvent,
    onReasoningDelta,
    onProgressSnapshot,
    signal,
    toolCallContext,
    preludeUserMessages,
    withSignal,
    probeActiveContext,
    bridgeAvailable,
    citationNoteCapability,
    refreshFactSetData,
    recalculateWorkbook,
    excelTabularQuery,
    tabularQueryClient,
    tabularIdentityResolver,
  } = config;

  // -------------------------------------------------------------------------
  // Resolve the model and its provider adapter
  // -------------------------------------------------------------------------
  //
  // The loop below is provider-agnostic: it appends semantic `AgentTurn`s and
  // lets the adapter own every wire detail.
  //
  // Read once here rather than per turn, so a mid-run flag flip cannot swap
  // providers underneath an in-flight run.
  const executorModel = resolveExecutorModel(
    isExcelClaudeOpusEnabled() ? FLAGGED_EXECUTOR_MODEL_ID : null,
  );
  const adapter = getExecutorAdapter(executorModel.provider);

  // -------------------------------------------------------------------------
  // Initialize mutable state
  // -------------------------------------------------------------------------

  const sourcePlan: ExecutionPlan = resumeCheckpoint?.plan ??
    plan ?? {
      summary: "",
      estimatedComplexity: "moderate",
      steps: [],
    };
  const completedStepIds = new Set(resumeCheckpoint?.completedStepIds ?? []);
  const completedTerminalWorkloadStepIds = new Set<string>();
  const checkpointStepById = new Map(
    (resumeCheckpoint?.stepStates ?? []).map((step) => [step.id, step])
  );
  const idLookupFieldHintText = [
    sourcePlan.summary,
    ...sourcePlan.steps.flatMap((step) => [step.description, step.action, step.targetSheet ?? ""]),
  ].join("\n");
  const phraseIdLookupRoutingIntent = detectIdLookupRoutingIntent(
    userRequest,
    idLookupFieldHintText
  );
  const companyList: WorksheetCompanyList = config.workbookSnapshot
    ? detectWorksheetCompanyList(config.workbookSnapshot)
    : detectWorksheetCompanyListFromContext(workbookContext);
  const worksheetColumnRoute = await resolveWorksheetColumnRoute({
    userRequest,
    tabularEnabled: excelTabularQuery?.enabled === true,
    companyListPresent: companyList.present,
    idLookupIntent: phraseIdLookupRoutingIntent,
    signal,
  });
  const detectedIdLookupRoutingIntent = worksheetColumnRoute.idLookupIntent;
  const tabularWorksheetIntentEligible = worksheetColumnRoute.route === "tabular";
  diag("llm", "multi_hop_executor_worksheet_column_route", {
    route: worksheetColumnRoute.route,
    reason: worksheetColumnRoute.reason,
    topicKey: worksheetColumnRoute.topicKey,
    score: worksheetColumnRoute.score,
    confidence: worksheetColumnRoute.confidence,
    usedEmbeddings: worksheetColumnRoute.usedEmbeddings,
    companyListPresent: companyList.present,
    companyListRows: companyList.rowCount,
  });
  // Explicit list wording decides workload cardinality. A detected company
  // list only supplies worksheet scope for terse identity-column aliases; it
  // must not turn a named single-company question into a whole-column job.
  const isWorksheetListLookup =
    detectedIdLookupRoutingIntent.active &&
    (detectIdLookupWorksheetWorkload(userRequest) ||
      worksheetColumnRoute.inferredWorksheetList === true);
  const idLookupSourceRestricted = isDirectIdLookupSourceRestricted(
    toolCallContext?.researchContext
  );
  const directLookupIntentEligible =
    detectedIdLookupRoutingIntent.active && !isWorksheetListLookup && !idLookupSourceRestricted;
  const worksheetLookupIntentEligible =
    detectedIdLookupRoutingIntent.active &&
    isWorksheetListLookup &&
    !idLookupSourceRestricted &&
    !tabularWorksheetIntentEligible;
  let idLookupAvailable = false;
  if (directLookupIntentEligible || worksheetLookupIntentEligible) {
    const availability = await resolveIdLookupAvailability({
      log: (message) => onEvent?.({ type: "status", message }),
    });
    idLookupAvailable = availability.available;
    if (!availability.available) {
      diag("llm", "multi_hop_executor_id_lookup_unavailable", {
        reason: availability.reason,
        message: availability.message,
      });
    }
  }
  const includeIdLookup = directLookupIntentEligible && idLookupAvailable;
  const includeIdLookupWorkload = worksheetLookupIntentEligible && idLookupAvailable;
  const includeTabularQueryWorkload =
    tabularWorksheetIntentEligible ||
    Boolean(
      resumeCheckpoint?.tabularQueryWorkload &&
        excelTabularQuery?.enabled === true
    );
  const idLookupRoutingIntent: IdLookupRoutingIntent =
    includeIdLookup || includeIdLookupWorkload
      ? detectedIdLookupRoutingIntent
      : { active: false, requestedFields: [] };
  const idLookupRoutingPolicy = new IdLookupRoutingPolicy(idLookupRoutingIntent, {
    disabled: !includeIdLookup && !includeIdLookupWorkload,
  });
  const planState: ExecutorPlanState = {
    summary: sourcePlan.summary,
    estimatedComplexity: sourcePlan.estimatedComplexity,
    steps: sourcePlan.steps.map((step, index) => {
      const generatedId = `step_${index + 1}_${slugify(step.action || step.description)}`;
      const saved = checkpointStepById.get(generatedId) ?? resumeCheckpoint?.stepStates[index];
      const id = saved?.id ?? generatedId;
      const completed = completedStepIds.has(id);
      return {
        id,
        planStep: step,
        status: completed ? (saved?.status ?? "success") : "queued",
        ...(completed && saved?.note ? { note: saved.note } : {}),
      };
    }),
  };
  const resumedCurrentStepId = resumeCheckpoint?.currentStepId;
  if (
    resumedCurrentStepId &&
    !completedStepIds.has(resumedCurrentStepId) &&
    planState.steps.some((step) => step.id === resumedCurrentStepId)
  ) {
    planState.currentStepId = resumedCurrentStepId;
  }

  // Evidence applies only when it was collected under the policy this run
  // resolved and matches the policy the checkpoint was written under. Anything
  // else is treated as unavailable: the run keeps the citation fallback but
  // replays no locked answers, so it can research again rather than present
  // findings from a policy that no longer holds.
  const evidenceConfig = config.researchEvidence;
  const checkpointEvidenceFingerprint = resumeCheckpoint?.evidencePolicyFingerprint;
  const restoredEvidence =
    evidenceConfig?.pack &&
    evidenceConfig.pack.operationId === evidenceConfig.operationId &&
    evidenceConfig.pack.policyFingerprint === evidenceConfig.policyFingerprint &&
    (checkpointEvidenceFingerprint === undefined ||
      checkpointEvidenceFingerprint === evidenceConfig.policyFingerprint)
      ? evidenceConfig.pack
      : undefined;
  let researchEvidence = restoredEvidence;
  /**
   * Citation index this run starts from.
   *
   * Rebuilt from the pack when it survived, and otherwise from the citations
   * persisted on the paused message, so a cited write after resume can still
   * resolve the Note for an ID retrieved before the pause.
   */
  const restoredCitationIndex: CitationNoteIndex = {};
  if (restoredEvidence) {
    for (const [citationId, record] of Object.entries(
      restoredEvidence.citationNoteIndex
    )) {
      restoredCitationIndex[citationId] = { ...record };
    }
  } else if (evidenceConfig?.fallbackCitations?.length) {
    ingestAgentResponse(restoredCitationIndex, {
      citations: evidenceConfig.fallbackCitations,
    });
  }

  const accumulators: ExecutorRunAccumulators = {
    stepResults: resumeCheckpoint?.stepResults.slice() ?? [],
    stepResultIndexById: new Map(resumeCheckpoint?.stepResultIndexById ?? []),
    stepDisplayResults:
      resumeCheckpoint?.stepDisplayResults.slice() ?? sourcePlan.steps.map((s) => s.description),
    stepStatuses: planState.steps.map((step) => toPersistedStepStatus(step.status)),
    lastModifications: resumeCheckpoint?.lastModifications ?? null,
    editedCells: resumeCheckpoint?.editedCells.slice() ?? [],
    formulaWriteTargets:
      resumeCheckpoint?.formulaWriteTargets.map((target) => ({ ...target })) ?? [],
    fdsFormulaWriteTargets:
      resumeCheckpoint?.fdsFormulaWriteTargets.map((target) => ({ ...target })) ?? [],
    fdsFormulaWriteAttempts: {
      ...(resumeCheckpoint?.fdsFormulaWriteAttempts ?? {}),
    },
    unavailableFdsFormulaTargets:
      resumeCheckpoint?.unavailableFdsFormulaTargets?.map((target) => ({
        ...target,
      })) ?? [],
    fdsDateAxisWriteTargets:
      resumeCheckpoint?.fdsDateAxisWriteTargets.map((target) => ({ ...target })) ?? [],
    autofitSheetNames: new Set(resumeCheckpoint?.autofitSheetNames ?? []),
    autofitTargets: resumeCheckpoint?.autofitTargets.map((target) => ({ ...target })) ?? [],
    columnSizingHints:
      resumeCheckpoint?.columnSizingHints?.map((hint) => ({ ...hint })) ?? [],
    columnSizingCellEvidence: Object.fromEntries(
      Object.entries(resumeCheckpoint?.columnSizingCellEvidence ?? {}).map(
        ([key, entry]) => [key, { ...entry }],
      ),
    ),
    explicitColumnWidthKeys:
      resumeCheckpoint?.explicitColumnWidthKeys?.map((key) => ({ ...key })) ?? [],
    fdsRefreshSheets: new Set(resumeCheckpoint?.fdsRefreshSheets ?? []),
    deferredRefreshAllRequested: resumeCheckpoint?.deferredRefreshAllRequested ?? false,
    deferredRecalcMode: resumeCheckpoint?.deferredRecalcMode ?? null,
    workbookEditAttempted: resumeCheckpoint?.workbookEditAttempted ?? false,
    citationNoteIndex: restoredCitationIndex,
    // Provenance and audits describe writes this run verified itself, so they
    // deliberately start empty even when the evidence behind them was restored.
    citationCellProvenance: {},
    sourceNoteAudits: [],
    retrievedCitedValuesByUuid: { ...(restoredEvidence?.citedValuesByUuid ?? {}) },
  };

  const session: ExecutorRunSessionState = {
    sessionLockSheet,
    userRequest,
    pinnedWorkbookName,
    isContinuation: Boolean(resumeCheckpoint),
    citationNoteCapability: bridgeAvailable === false ? "unsupported" : citationNoteCapability,
    citationNoteAttachmentDisabled:
      bridgeAvailable === false || citationNoteCapability === "unsupported",
    allowFdsFormulaOverwriteWithStaticValues:
      userRequestedFdsFormulaOverwriteWithStaticValues(userRequest),
  };
  const verificationState = restoreExecutorVerificationState(resumeCheckpoint?.verification);
  const forwardPeIntentSignals = resolveForwardPeIntentSignals(userRequest, [
    sourcePlan.summary,
    ...sourcePlan.steps.flatMap((step) => [step.description, step.action]),
  ]);
  const forwardPeChartVerification =
    resumeCheckpoint?.forwardPeChartVerification
      ? cloneForwardPeChartVerificationState(
          resumeCheckpoint.forwardPeChartVerification,
        )
      : createForwardPeChartVerificationState(forwardPeIntentSignals);
  updateForwardPeChartVerificationIntent(
    forwardPeChartVerification,
    forwardPeIntentSignals,
  );
  const forwardPeRangeChartIntentForCall = (rawInput: unknown) => {
    const input = recordValue(rawInput);
    const stepId = typeof input.stepId === "string" ? input.stepId.trim() : "";
    const planStep = stepId ? planState.steps.find((step) => step.id === stepId) : undefined;
    return {
      planStepResolved: planStep !== undefined,
      planStepSignals: planStep
        ? [planStep.planStep.description, planStep.planStep.action]
        : [],
    };
  };
  const executedWriteFingerprints = new Set(resumeCheckpoint?.executedWriteFingerprints ?? []);
  const resumedWriteFingerprints = new Set(resumeCheckpoint?.executedWriteFingerprints ?? []);
  const confirmedRepeatableWriteFingerprints = new Set(
    resumeCheckpoint?.confirmedRepeatableWriteFingerprints ?? [],
  );
  const resumedRepeatableWriteFingerprints = new Set(
    resumeCheckpoint?.confirmedRepeatableWriteFingerprints ?? [],
  );
  let repeatableWriteInspectionObserved = false;
  let confirmedRepeatableWriteCount = 0;
  const granularRecoveryKeys = new Set(resumeCheckpoint?.granularRecoveryKeys ?? []);
  const formulaResolutionCache = new Map(resumeCheckpoint?.formulaResolutionCache ?? []);
  const formulaResolutionAttempts = new Map(resumeCheckpoint?.formulaResolutionAttempts ?? []);
  let blockedOperation: ExecutorRunCheckpoint["blockedOperation"] | undefined =
    resumeCheckpoint?.blockedOperation;
  const researchAccounting = {
    successfulResearchCallCount: normalizeCheckpointResearchCallCount(
      resumeCheckpoint?.successfulResearchCallCount,
    ),
    tabularResearchSuccessCounted:
      resumeCheckpoint?.tabularResearchSuccessCounted === true,
  };
  /** True once this run confirms a write fingerprint absent from the checkpoint. */
  const madeNewConfirmedEdits = (): boolean =>
    confirmedRepeatableWriteCount > 0 ||
    Array.from(executedWriteFingerprints).some(
      (fingerprint) => !resumedWriteFingerprints.has(fingerprint)
    );
  let continuationExhausted = false;
  const resumedOutstandingSignature = checkpointOutstandingWorkSignature(resumeCheckpoint);
  let snapshotTabularQueryWorkload: () =>
    ExecutorRunCheckpoint["tabularQueryWorkload"] = () =>
      resumeCheckpoint?.tabularQueryWorkload;
  const buildCurrentPlan = (): ExecutionPlan | null =>
    planState.steps.length > 0 ? buildExecutionPlanFromState(planState, sourcePlan) : null;
  const buildCheckpoint = (): ExecutorRunCheckpoint | null => {
    const currentPlan = buildCurrentPlan();
    if (!currentPlan) return null;
    const tabularQueryWorkload = snapshotTabularQueryWorkload();
    return {
      version: 1,
      plan: currentPlan,
      stepStates: planState.steps.map((step) => ({
        ...step,
        planStep: { ...step.planStep },
      })),
      completedStepIds: planState.steps
        .filter(
          (step) =>
            isCompletedCheckpointStep(step.status) ||
            ((completedStepIds.has(step.id) || completedTerminalWorkloadStepIds.has(step.id)) &&
              step.status === "warning")
        )
        .map((step) => step.id),
      terminalStepIds: planState.steps
        .filter((step) => isTerminalExecutorStepStatus(step.status))
        .map((step) => step.id),
      ...(planState.currentStepId ? { currentStepId: planState.currentStepId } : {}),
      stepResults: accumulators.stepResults.slice(),
      stepResultIndexById: Array.from(accumulators.stepResultIndexById.entries()),
      stepDisplayResults: accumulators.stepDisplayResults.slice(),
      stepStatuses: accumulators.stepStatuses.slice(),
      lastModifications: accumulators.lastModifications,
      editedCells: accumulators.editedCells.slice(),
      formulaWriteTargets: accumulators.formulaWriteTargets.map((target) => ({
        ...target,
      })),
      fdsFormulaWriteTargets:
        accumulators.fdsFormulaWriteTargets?.map((target) => ({
          ...target,
        })) ?? [],
      fdsFormulaWriteAttempts: {
        ...(accumulators.fdsFormulaWriteAttempts ?? {}),
      },
      unavailableFdsFormulaTargets:
        accumulators.unavailableFdsFormulaTargets?.map((target) => ({
          ...target,
        })) ?? [],
      fdsDateAxisWriteTargets:
        accumulators.fdsDateAxisWriteTargets?.map((target) => ({
          ...target,
        })) ?? [],
      autofitSheetNames: Array.from(accumulators.autofitSheetNames),
      autofitTargets: accumulators.autofitTargets.map((target) => ({
        ...target,
      })),
      columnSizingHints:
        accumulators.columnSizingHints?.map((hint) => ({ ...hint })) ?? [],
      columnSizingCellEvidence: Object.fromEntries(
        Object.entries(accumulators.columnSizingCellEvidence ?? {}).map(
          ([key, entry]) => [key, { ...entry }],
        ),
      ),
      explicitColumnWidthKeys:
        accumulators.explicitColumnWidthKeys?.map((key) => ({ ...key })) ?? [],
      fdsRefreshSheets: Array.from(accumulators.fdsRefreshSheets),
      deferredRefreshAllRequested: accumulators.deferredRefreshAllRequested,
      deferredRecalcMode: accumulators.deferredRecalcMode,
      workbookEditAttempted: accumulators.workbookEditAttempted,
      successfulResearchCallCount:
        researchAccounting.successfulResearchCallCount,
      tabularResearchSuccessCounted:
        researchAccounting.tabularResearchSuccessCounted,
      executedWriteFingerprints: Array.from(executedWriteFingerprints),
      confirmedRepeatableWriteFingerprints: Array.from(
        confirmedRepeatableWriteFingerprints,
      ),
      granularRecoveryKeys: Array.from(granularRecoveryKeys),
      formulaResolutionCache: Array.from(formulaResolutionCache.entries()),
      formulaResolutionAttempts: Array.from(formulaResolutionAttempts.entries()),
      forwardPeChartVerification: cloneForwardPeChartVerificationState(
        forwardPeChartVerification,
      ),
      ...(tabularQueryWorkload ? { tabularQueryWorkload } : {}),
      ...(blockedOperation ? { blockedOperation } : {}),
      ...(researchEvidence
        ? {
            evidenceVersion: researchEvidence.version,
            evidencePolicyFingerprint: researchEvidence.policyFingerprint,
          }
        : {}),
      verification: snapshotExecutorVerificationState(verificationState),
    };
  };
  const emitProgressSnapshot = (): void => {
    onProgressSnapshot?.({
      plan: buildCurrentPlan(),
      stepResults: accumulators.stepResults.slice(),
      stepDisplayResults: accumulators.stepDisplayResults.slice(),
      stepStatuses: accumulators.stepStatuses.slice(),
      workbookEditAttempted: accumulators.workbookEditAttempted,
      getCheckpoint: buildCheckpoint,
    });
  };

  // -------------------------------------------------------------------------
  // Create run-scoped tool runtime
  // -------------------------------------------------------------------------

  const factSetFormatRegistry = new FactSetFormulaFormatRegistry();
  let remainingExecutorIterations = MAX_EXECUTOR_ITERATIONS;
  const runtimeCtx: ExecutorToolRuntimeContext = {
    planState,
    onPlanStateChange: (state) => {
      onPlanStateChange(state);
      onEvent?.({ type: "plan_state_changed", planState: state });
      emitProgressSnapshot();
    },
    onWorkbookHandoff,
    beginWorkbookHandoff,
    applyWorkbookModifications,
    accumulators,
    session,
    idLookupRequestedFields: idLookupRoutingIntent.requestedFields,
    idLookupWorkloadEnabled: includeIdLookupWorkload,
    excelTabularQuery:
      includeTabularQueryWorkload && excelTabularQuery
        ? { ...excelTabularQuery, enabled: true }
        : undefined,
    researchContext: buildEffectiveResearchContext(
      toolCallContext,
      pinnedWorkbookName,
      sessionLockSheet
    ),
    userSkillGuidance: toolCallContext?.userSkillGuidance,
    // A resumed run continues the queued work. The steps the checkpoint
    // already finished are history, and the runtime — not the continuation
    // prompt — is what holds them there.
    lockedStepIds: [...completedStepIds],
    tabularQueryClient,
    langfuseContext: config.langfuseContext,
    tabularIdentityResolver,
    signal,
    getRemainingExecutorIterations: () => remainingExecutorIterations,
    factSetFormatRegistry,
    refreshFactSetData,
    recalculateWorkbook,
  };

  const runtime = createExecutorToolRuntime(runtimeCtx);
  snapshotTabularQueryWorkload = () =>
    runtime.tabularQueryWorkload.snapshot() ??
    resumeCheckpoint?.tabularQueryWorkload;
  // Publish a lazy checkpoint builder before workbook validation so a failed
  // restore still leaves the original continuation state available to the UI.
  emitProgressSnapshot();
  if (resumeCheckpoint?.tabularQueryWorkload) {
    if (!includeTabularQueryWorkload) {
      throw new Error(
        "The saved tabular query workload cannot resume because the capability is disabled.",
      );
    }
    await runtime.tabularQueryWorkload.restore(
      resumeCheckpoint.tabularQueryWorkload,
    );
    emitProgressSnapshot();
  }
  let executorWorkloadParticipant: ExecutorWorkloadParticipant<RunnerWorksheetWorkloadIssue> | null =
    null;
  if (includeIdLookupWorkload) {
    executorWorkloadParticipant = {
      get progressVersion() {
        return runtime.idLookupWorkload.progressVersion;
      },
      get isComplete() {
        return runtime.idLookupWorkload.isComplete;
      },
      completionIssue: (): RunnerIdLookupWorkloadIssue | null => {
        if (!runtime.idLookupWorkload.hasWorkload) {
          return {
            issue: "id_lookup_workload_registration_required",
            workloadId: null,
            nextTool: START_ID_LOOKUP_WORKLOAD_TOOL_NAME,
            remainingRows: null,
            nextRowNumbers: [],
            instruction:
              `Register the complete worksheet company list with ` +
              `${START_ID_LOOKUP_WORKLOAD_TOOL_NAME}, then alternate the next-wave ` +
              "and commit tools until every nonblank row is written and verified.",
          };
        }
        return runtime.idLookupWorkload.completionIssue();
      },
      describeIncompleteRows: () =>
        runtime.idLookupWorkload.hasWorkload
          ? runtime.idLookupWorkload.describeIncompleteRows()
          : ["The worksheet lookup workload was not registered."],
      reset: () => runtime.idLookupWorkload.reset(),
    };
  } else if (includeTabularQueryWorkload) {
    executorWorkloadParticipant = {
      get progressVersion() {
        return runtime.tabularQueryWorkload.progressVersion;
      },
      get isComplete() {
        return runtime.tabularQueryWorkload.isComplete;
      },
      completionIssue: (): RunnerTabularQueryWorkloadIssue | null => {
        if (!runtime.tabularQueryWorkload.hasWorkload) {
          return {
            issue: "tabular_query_workload_registration_required",
            workloadId: null,
            nextTool: START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
            remainingRows: null,
            nextRowNumbers: [],
            instruction:
              `Register the aligned worksheet column with ` +
              `${START_TABULAR_QUERY_WORKLOAD_TOOL_NAME}, then alternate ` +
              `${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} and ` +
              `${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} until every row is closed; ` +
              "usable results must be written and read-back verified.",
          };
        }
        return runtime.tabularQueryWorkload.completionIssue();
      },
      describeIncompleteRows: () =>
        runtime.tabularQueryWorkload.hasWorkload
          ? runtime.tabularQueryWorkload.describeIncompleteRows()
          : ["The tabular query workload was not registered."],
      reset: () => runtime.tabularQueryWorkload.reset(),
    };
  }
  const finalizeWithWorkloadReset = <T>(value: T): T => {
    executorWorkloadParticipant?.reset();
    return value;
  };
  const getExecutorWorkloadIssue = (): RunnerWorksheetWorkloadIssue | null =>
    executorWorkloadParticipant?.completionIssue() ?? null;
  // Discarding a workload bumps the participant's progress version, so a start
  // that fails and resets would otherwise refresh the reminder budget every
  // iteration and force the same failing tool call until the hard loop cap.
  const isWorkloadRegistrationRequired = (
    issue: RunnerWorksheetWorkloadIssue,
  ): boolean =>
    issue.issue === "tabular_query_workload_registration_required" ||
    issue.issue === "id_lookup_workload_registration_required";
  const requiredExecutorWorkloadToolName = (): string => {
    if (planState.steps.length === 0) return "set_run_plan";
    return (
      getExecutorWorkloadIssue()?.nextTool ??
      (includeTabularQueryWorkload
        ? NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME
        : NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME)
    );
  };
  const requiredIdLookupToolName = (): string => {
    if (!includeIdLookupWorkload) return ID_LOOKUP_TOOL_NAME;
    return requiredExecutorWorkloadToolName();
  };
  const executorWorkloadLabel = includeTabularQueryWorkload
    ? "tabular query workload"
    : "worksheet ID lookup";
  const executorWorkloadStoppedReason =
    `The ${executorWorkloadLabel} stopped making progress before every ` +
    "registered row was closed and verified.";
  const applyTemplateOutcomes: ApplyTemplateOutcome[] = [];
  const baseToolCallContext = withApplyTemplateOutcomeCapture(
    toolCallContext,
    applyTemplateOutcomes,
  );
  const buildCurrentEffectiveToolCallContext = (): ToolCallContext => ({
    ...baseToolCallContext,
    requireFormulaQueries: true,
    researchContext: buildEffectiveResearchContext(
      toolCallContext,
      session.pinnedWorkbookName,
      session.sessionLockSheet,
    ),
  });
  const effectiveToolCallContext = buildCurrentEffectiveToolCallContext();
  const explicitResearchRouteRequired = requiresExplicitResearchRoute(
    effectiveToolCallContext.researchContext
  );
  let researchRoute: ResearchRoute | undefined = requiresSourceConstrainedResearch(
    effectiveToolCallContext.researchContext
  )
    ? "selected_sources"
    : undefined;

  // -------------------------------------------------------------------------
  // Assemble tool definitions for the model
  // -------------------------------------------------------------------------

  const runScopedToolDefs = explicitResearchRouteRequired
    ? [SET_RESEARCH_ROUTE_TOOL, ...runtime.toolDefs]
    : runtime.toolDefs;
  const assembledTools = adapter.serializeTools({
    scope: "react-executor",
    extraTools: runScopedToolDefs,
    options: { includeIdLookup },
  });
  const workloadResearchDescription = includeIdLookupWorkload
    ? buildFinsterQueryTool(undefined, {
        includeIdLookupWorkload: true,
      }).description
    : includeTabularQueryWorkload && getExecutorWorkloadIssue()
      ? "The current aligned worksheet-column request must use " +
        `${START_TABULAR_QUERY_WORKLOAD_TOOL_NAME} and its next/commit lifecycle. ` +
        "This tool is unavailable for this run; do not duplicate the row queries here."
      : null;
  const researchToolName = getExcelFinsterBrandContext().researchToolName;
  const tools = workloadResearchDescription
    ? assembledTools.map((tool) => {
        const definition = tool as Record<string, unknown>;
        return definition.name === researchToolName
          ? { ...definition, description: workloadResearchDescription }
          : tool;
      })
    : assembledTools;

  // -------------------------------------------------------------------------
  // Inject skill context (push + selected pull skills)
  // -------------------------------------------------------------------------

  const {
    skillIds: executorSkillIds,
    skillBlock: executorSkillBlock,
  } = buildExecutorSkillAssembly(sourcePlan.selectedSkills, config.brandId);
  const executorSystemPrompt = withChatHistoryGuidance(
    buildExecutorSystemPrompt(effectiveToolCallContext.researchContext, {
      includeIdLookup,
      includeIdLookupWorkload,
      includeTabularQueryWorkload,
    })
  );
  const executorInstructions = executorSkillBlock
    ? `${executorSystemPrompt}\n\n${executorSkillBlock}`
    : executorSystemPrompt;

  if (executorSkillIds.length > 0) {
    onEvent?.({
      type: "status",
      message: `Skill context injected: ${executorSkillIds.join(", ")}`,
    });
  }

  // -------------------------------------------------------------------------
  // Build user message
  // -------------------------------------------------------------------------

  const userMessage = JSON.stringify({
    userRequest,
    plan:
      planState.steps.length > 0
        ? {
            summary: sourcePlan.summary,
            estimatedComplexity: sourcePlan.estimatedComplexity,
            executorSteps: planState.steps.map((s) => ({
              id: s.id,
              step: s.planStep.step,
              description: s.planStep.description,
              action: s.planStep.action,
              targetSheet: s.planStep.targetSheet ?? null,
              status: s.status,
            })),
          }
        : null,
    ...(resumeCheckpoint
      ? {
          continuation: {
            completedStepIds: resumeCheckpoint.completedStepIds,
            terminalStepIds: resumeCheckpoint.terminalStepIds,
            blockedOperation: resumeCheckpoint.blockedOperation ?? null,
            instruction:
              "Continue only queued remaining work. Do not replay completed writes. " +
              "Before repeating a structural write from the prior run, inspect the current workbook state; " +
              "the runtime blocks a blind repeat but permits a deliberate repeat after fresh inspection.",
          },
        }
      : {}),
    ...(chatHistory?.trim() ? { chatHistory } : {}),
    workbookContext,
    outputFormat:
      'Return your final answer as JSON: {"mode":"answer|clarification|workbook_edit|partial|failed","reply":"concise user-facing reply","unresolved":[...optional]}. ' +
      'In reply citation markup, preserve each exact citation id, but put the complete user-facing cited value or claim inside <u>, including its currency and unit (for example, <u id="exact-id">$109.4B</u>). ' +
      "Never expose internal citation tokens and IDs as visible citation text. For example, SEC_0F962A59, IR_8A31D2, and WEB_42C19F are internal references, not cited passages; keep the exact reference only in the citation id attribute and render the human-readable value or claim inside <u>.",
  });

  // -------------------------------------------------------------------------
  // Tool loop
  // -------------------------------------------------------------------------

  // Canonical conversation in the provider-agnostic IR. The adapter flattens it
  // into wire items on every turn; nothing here is provider-shaped.
  // Skill guidance leads, verbatim and unnormalized, so it frames the request
  // envelope that follows it rather than arriving as payload inside it.
  // Completed research sits between the two: it is governed by the guidance
  // above it and is context for the request below it, not part of either.
  const lockedResearchEvidence = renderExcelOperationEvidence(restoredEvidence);
  const turns: AgentTurn[] = [
    ...(preludeUserMessages ?? []).map(
      (content): AgentTurn => ({ kind: "user", content })
    ),
    ...(lockedResearchEvidence
      ? [{ kind: "user", content: lockedResearchEvidence } as AgentTurn]
      : []),
    { kind: "user", content: userMessage },
  ];
  let orchestratorMessageCount = 0;
  /** Appends an orchestrator message from the runner to the model. */
  const pushUser = (payload: unknown): void => {
    orchestratorMessageCount += 1;
    turns.push({ kind: "user", content: JSON.stringify(payload) });
  };
  /** Replays a model turn verbatim so provider-internal item pairing survives. */
  const pushModelTurn = (raw: readonly unknown[]): void => {
    turns.push({ kind: "model", raw });
  };
  const pushToolResult = (callId: string, output: string): void => {
    turns.push({ kind: "tool_result", callId, output });
  };
  const executorRunStartedAt = Date.now();
  const runPhaseTimings = new Map<string, { count: number; totalMs: number; maxMs: number }>();
  let iterationsCompleted = 0;
  // Encoding is O(conversation), so each hop encodes once and everything else
  // (diagnostics, the timing summary) reads the counts that hop recorded.
  let lastEncodedItemCount = 1;
  let maxInputItemCount = 1;
  const recordRunPhase = (
    phase: string,
    elapsedMs: number,
    fields: Record<string, unknown> = {}
  ): void => {
    recordPhase(phase, elapsedMs, fields);
    if (!isDiagEnabled()) return;
    const current = runPhaseTimings.get(phase) ?? { count: 0, totalMs: 0, maxMs: 0 };
    current.count += 1;
    current.totalMs += elapsedMs;
    current.maxMs = Math.max(current.maxMs, elapsedMs);
    runPhaseTimings.set(phase, current);
  };
  const timingFor = (phase: string): { count: number; totalMs: number; maxMs: number } => {
    const timing = runPhaseTimings.get(phase);
    return timing
      ? {
          count: timing.count,
          totalMs: Math.round(timing.totalMs),
          maxMs: Math.round(timing.maxMs),
        }
      : { count: 0, totalMs: 0, maxMs: 0 };
  };
  const emitRunTimingSummary = (): void => {
    if (!isDiagEnabled()) return;
    diag("perf", "multi_hop_executor_timing_summary", {
      wallMs: Date.now() - executorRunStartedAt,
      iterations: iterationsCompleted,
      finalInputItemCount: lastEncodedItemCount,
      maxInputItemCount,
      llmRequests: timingFor("executor:llm_request"),
      recalculateWorkbook: timingFor("tool:recalculate_workbook"),
      refreshFactsetData: timingFor("tool:refresh_factset_data"),
      readRange: timingFor("tool:read_range"),
      generateFormula: timingFor("tool:generate_formula"),
    });
  };

  diag("llm", "multi_hop_executor_start", {
    stepCount: sourcePlan.steps.length,
    userRequestLen: userRequest.length,
    workbookContextLen: workbookContext.length,
    toolCount: tools.length,
  });
  emitProgressSnapshot();

  let completed = false;
  let stopReason: string | null = null;
  /**
   * Set when a bounded guard - the run time budget, repeated iterations with no
   * progress, or a provider turn that cannot produce a usable result - ends the
   * loop. It replaces the iteration-cap wording in the partial-result path
   * below; the run never reports success after one of these.
   */
  let forcedStopReason: string | null = null;
  const stagnationBudget = createExecutorStagnationBudget();
  let finalTextContent = "";
  // A resumed run reports the whole operation's sources and citations, not just
  // what this leg retrieved, so the source cards and Note lookups a completed
  // turn publishes still cover research that ran before the pause.
  const retrievedSources: ExcelAgentSource[] = [
    ...(restoredEvidence?.sources ?? []),
  ];
  const retrievedCitations: unknown[] = [
    ...(restoredEvidence?.citations ?? []),
  ];
  let nonTerminalReminderCount = 0;
  let missingRunPlanReminderCount = 0;
  let idLookupRoutingReminderCount = 0;
  let executorWorkloadReminderCount = 0;
  let executorWorkloadReminderProgressVersion = -1;
  let forcedToolChoiceName: string | null = null;
  let missingSelectedFileResearchReminderCount = 0;
  let missingResearchRouteReminderCount = 0;

  for (let iter = 0; iter < MAX_EXECUTOR_ITERATIONS; iter += 1) {
    throwIfAborted(signal);
    // The iteration cap bounds how many turns a run takes, not how long they
    // take, so the wall-clock budget is checked before starting another one.
    const elapsedMs = Date.now() - executorRunStartedAt;
    if (hasExhaustedExecutorRunTimeBudget(elapsedMs)) {
      forcedStopReason =
        `Executor stopped after its ${Math.round(EXECUTOR_RUN_TIME_BUDGET_MS / 60000)}-minute ` +
        "run budget, before a final response.";
      diag("llm", "multi_hop_executor_time_budget_stop", {
        iteration: iter + 1,
        elapsedMs,
        budgetMs: EXECUTOR_RUN_TIME_BUDGET_MS,
      });
      onEvent?.({ type: "status", message: forcedStopReason });
      break;
    }
    iterationsCompleted = iter + 1;
    remainingExecutorIterations = MAX_EXECUTOR_ITERATIONS - iter;
    const encodedInput = adapter.encodeTurns(turns);
    lastEncodedItemCount = encodedInput.length;
    maxInputItemCount = Math.max(maxInputItemCount, encodedInput.length);
    onEvent?.({ type: "iteration_start", iteration: iter + 1 });
    const stopReasonAtRequestStart = stopReason;

    // Compact the replayed history so per-turn token cost - and therefore
    // latency - does not grow unbounded across a long ReAct run. What can
    // safely shrink is the adapter's call; canonical `turns` stays full.
    const projectedInput = adapter.projectItems(encodedInput);
    const requestedToolName = forcedToolChoiceName;
    forcedToolChoiceName = null;
    const providerRequest = adapter.buildRequest({
      model: executorModel,
      instructions: executorInstructions,
      items: projectedInput,
      tools,
      forcedToolName: requestedToolName,
    });
    diag("llm", "multi_hop_executor_request", {
      iteration: iter + 1,
      inputItemCount: encodedInput.length,
      projectedItemCount: projectedInput.length,
      toolNames: tools.map((tool) => (tool as { name?: unknown }).name).filter(Boolean),
      request: providerRequest.body,
    });
    if (isDiagEnabled()) {
      onEvent?.({
        type: "status",
        message: `LLM request ${iter + 1}: ${encodedInput.length} input item(s), ${tools.length} tool(s).`,
      });
    }

    const llmStartedAt = Date.now();
    let streamedReasoningContent = "";
    const turnRequest: ExecutorStreamTurnParams = {
      request: () =>
        fetchWithRetry(
          providerRequest.url,
          withSignal(withOptionalExcelLangfuseMetadata(withCredentials({
            method: "POST",
            body: JSON.stringify(providerRequest.body),
            ...(providerRequest.headers ? { headers: providerRequest.headers } : {}),
          }), config.langfuseContext, buildExcelUnifiedAgentGenerationMetadata({
            executorMode: "plan-protocol",
            provider: executorModel.provider,
            model: executorModel.model,
            iteration: iter + 1,
            stream: true,
            toolCount: tools.length,
          }))),
          2,
          "multi-hop-executor"
        ),
      contextLabel: `multi-hop-executor ${executorModel.label}`,
      signal,
      maxBodyRetries: 1,
      onReasoning: (partial) => {
        const delta = partial.startsWith(streamedReasoningContent)
          ? partial.slice(streamedReasoningContent.length)
          : partial;
        streamedReasoningContent = partial;
        if (delta) onReasoningDelta?.(delta, iter + 1);
      },
      onRetry: (error) => {
        diag("llm", "multi_hop_executor_stream_retry", {
          iteration: iter + 1,
          error: error instanceof Error ? error.message : String(error),
        });
        onEvent?.({
          type: "status",
          message: `Model stream interrupted; safely retrying iteration ${iter + 1} once.`,
        });
      },
    };
    let turnResult: AgentTurnResult;
    try {
      turnResult = await adapter.streamTurn(turnRequest);
    } catch (error) {
      // An abort is the caller's signal and stops the run immediately; a
      // provider turn that cannot produce a usable result (output cap, refusal,
      // malformed tool call, stream still broken after its replay) ends the run
      // through the bounded partial-result path instead, so the edits this run
      // already applied are reported rather than lost to the exception.
      if (isAbortError(error) || signal?.aborted || !isFatalProviderTurnError(error)) {
        throw error;
      }
      const detail = error instanceof Error ? error.message : String(error);
      forcedStopReason = `The ${executorModel.label} turn could not be completed: ${detail}`;
      diag("llm", "multi_hop_executor_provider_stop", {
        iteration: iter + 1,
        error: detail,
      });
      onEvent?.({ type: "status", message: forcedStopReason });
      break;
    }
    recordRunPhase("executor:llm_request", Date.now() - llmStartedAt, { iteration: iter + 1 });

    const functionCalls = turnResult.toolCalls;
    const textContent = turnResult.text;
    const reasoningSummary = turnResult.reasoning;
    diag("llm", "multi_hop_executor_response", {
      iteration: iter + 1,
      outputItemCount: turnResult.raw.length,
      functionCalls: functionCalls.map((call) => ({
        name: call.name,
        callId: call.callId,
        arguments: call.arguments,
      })),
      reasoningSummary,
      textPreview: textContent.slice(0, 1000),
      output: turnResult.raw,
    });
    if (isDiagEnabled()) {
      onEvent?.({
        type: "status",
        message:
          `LLM response ${iter + 1}: ${functionCalls.length} tool call(s)` +
          (textContent.trim() ? `, text ${textContent.trim().slice(0, 160)}` : ""),
      });
    }
    // Terminal: model returned text and no further tool calls
    if (functionCalls.length === 0) {
      if (!textContent.trim()) {
        // Nothing to dispatch and nothing to report is the same unusable-turn
        // category as an output cap or a refusal, so it ends the run on the
        // bounded path rather than throwing away the edits already applied.
        forcedStopReason =
          `The ${executorModel.label} turn could not be completed: ` +
          "it returned neither a tool call nor a reply.";
        diag("llm", "multi_hop_executor_empty_response", { iteration: iter + 1 });
        onEvent?.({ type: "status", message: forcedStopReason });
        break;
      }

      let terminalOutputAppended = false;
      const appendTerminalOutputForContinuation = (): void => {
        if (terminalOutputAppended) return;
        pushModelTurn(turnResult.raw);
        terminalOutputAppended = true;
      };

      const rawIdLookupRoutingBlock = stopReason ? null : idLookupRoutingPolicy.terminalBlock();
      const idLookupRoutingBlock =
        includeIdLookupWorkload && rawIdLookupRoutingBlock?.issue === "id_lookup_required"
          ? null
          : rawIdLookupRoutingBlock;
      if (
        idLookupRoutingBlock &&
        iter < MAX_EXECUTOR_ITERATIONS - 1 &&
        idLookupRoutingReminderCount < MAX_EXECUTOR_ID_LOOKUP_ROUTING_REMINDERS
      ) {
        idLookupRoutingReminderCount += 1;
        appendTerminalOutputForContinuation();
        forcedToolChoiceName =
          idLookupRoutingBlock.nextTool === "id_lookup"
            ? requiredIdLookupToolName()
            : getExcelFinsterBrandContext().researchToolName;
        pushUser({
          type: "orchestrator_reminder",
          issue: idLookupRoutingBlock.issue,
          requestedFields: idLookupRoutingPolicy.requestedFields,
          pendingFallbacks: idLookupRoutingPolicy.pendingFallbacksSnapshot,
          instruction:
            idLookupRoutingBlock.nextTool === "id_lookup" &&
            includeIdLookupWorkload
              ? `Register and complete the worksheet lookup with ${requiredIdLookupToolName()}.`
              : idLookupRoutingBlock.instruction,
        });
        onEvent?.({
          type: "status",
          message:
            idLookupRoutingBlock.nextTool === "id_lookup"
              ? "Required FactSet company lookup before finishing."
              : "Required the field-aware identity fallback before finishing.",
        });
        diag("llm", "multi_hop_executor_id_lookup_routing_reminder", {
          iteration: iter + 1,
          issue: idLookupRoutingBlock.issue,
          reminderCount: idLookupRoutingReminderCount,
          forcedToolChoiceName,
        });
        continue;
      }
      if (idLookupRoutingBlock) {
        const reason =
          "Company identity routing did not complete after repeated lookup or fallback reminders.";
        if (planState.steps.length > 0) {
          terminalizeNonTerminalSteps(
            planState,
            accumulators,
            reason,
            session.chartStoppingEvidence,
          );
          onPlanStateChange(planState);
          emitProgressSnapshot();
        }
        session.skipFinalVerificationReason = reason;
        finalTextContent = JSON.stringify({
          mode: "partial",
          reply: reason,
          unresolved: idLookupRoutingPolicy.pendingFallbacksSnapshot.map(
            ({ searchText, missingFields }) => `${searchText}: ${missingFields.join(", ")}`
          ),
        });
        completed = true;
        onEvent?.({ type: "status", message: reason });
        break;
      }

      const executorWorkloadIssue = stopReason ? null : getExecutorWorkloadIssue();
      if (executorWorkloadIssue) {
        const progressVersion = executorWorkloadParticipant?.progressVersion ?? -1;
        if (
          !isWorkloadRegistrationRequired(executorWorkloadIssue) &&
          progressVersion !== executorWorkloadReminderProgressVersion
        ) {
          executorWorkloadReminderProgressVersion = progressVersion;
          executorWorkloadReminderCount = 0;
        }
        if (
          iter < MAX_EXECUTOR_ITERATIONS - 1 &&
          executorWorkloadReminderCount < MAX_EXECUTOR_WORKLOAD_REMINDERS
        ) {
          executorWorkloadReminderCount += 1;
          appendTerminalOutputForContinuation();
          forcedToolChoiceName = requiredExecutorWorkloadToolName();
          pushUser({
            type: "orchestrator_reminder",
            issue: executorWorkloadIssue.issue,
            workloadId: executorWorkloadIssue.workloadId,
            remainingRows: executorWorkloadIssue.remainingRows,
            nextRowNumbers: executorWorkloadIssue.nextRowNumbers,
            instruction:
              forcedToolChoiceName === "set_run_plan"
                ? `Create the workbook run plan now, then register the ${executorWorkloadLabel}.`
                : executorWorkloadIssue.instruction,
          });
          onEvent?.({
            type: "status",
            message:
              typeof executorWorkloadIssue.remainingRows === "number"
                ? `${executorWorkloadLabel} has ${executorWorkloadIssue.remainingRows} row(s) remaining.`
                : `Registering the ${executorWorkloadLabel} before finishing.`,
          });
          diag("llm", "multi_hop_executor_id_lookup_workload_reminder", {
            iteration: iter + 1,
            issue: executorWorkloadIssue.issue,
            remainingRows: executorWorkloadIssue.remainingRows,
            reminderCount: executorWorkloadReminderCount,
            progressVersion,
            forcedToolChoiceName,
          });
          continue;
        }

        const reason = executorWorkloadStoppedReason;
        if (planState.steps.length > 0) {
          terminalizeNonTerminalSteps(
            planState,
            accumulators,
            reason,
            session.chartStoppingEvidence,
          );
          onPlanStateChange(planState);
          emitProgressSnapshot();
        }
        session.skipFinalVerificationReason = reason;
        finalTextContent = JSON.stringify({
          mode: "partial",
          reply: reason,
          unresolved: executorWorkloadParticipant?.describeIncompleteRows() ?? [
            `The ${executorWorkloadLabel} was not registered.`,
          ],
        });
        completed = true;
        onEvent?.({ type: "status", message: reason });
        break;
      }

      const nonTerminalSteps =
        planState.steps.length > 0
          ? planState.steps.filter(
              (s) => s.status === "queued" || s.status === "running" || s.status === "verifying"
            )
          : [];
      if (nonTerminalSteps.length > 0 && stopReason) {
        terminalizeNonTerminalSteps(
          planState,
          accumulators,
          stopReason,
          session.chartStoppingEvidence,
        );
        onPlanStateChange(planState);
        emitProgressSnapshot();
      } else if (
        nonTerminalSteps.length > 0 &&
        iter < MAX_EXECUTOR_ITERATIONS - 1 &&
        nonTerminalReminderCount < MAX_EXECUTOR_NON_TERMINAL_REMINDERS
      ) {
        nonTerminalReminderCount += 1;
        const ids = nonTerminalSteps.map((s) => s.id).join(", ");
        diag("llm", "multi_hop_executor_premature_finish", {
          iteration: iter + 1,
          nonTerminalIds: ids,
          reminderCount: nonTerminalReminderCount,
        });
        appendTerminalOutputForContinuation();
        pushUser({
          type: "orchestrator_reminder",
          nonTerminalStepIds: nonTerminalSteps.map((s) => s.id),
          instruction:
            "You returned a final response but these steps are still non-terminal. " +
            "Either complete them with tools or mark each one with set_step_status " +
            "(success/failed/warning/paused) before returning the final JSON.",
        });
        onEvent?.({
          type: "status",
          message: `Reminded model: ${nonTerminalSteps.length} step(s) still non-terminal.`,
        });
        continue;
      } else if (nonTerminalSteps.length > 0) {
        const ids = nonTerminalSteps.map((s) => s.id).join(", ");
        diag("llm", "multi_hop_executor_incomplete_final", {
          iteration: iter + 1,
          nonTerminalIds: ids,
        });
        terminalizeNonTerminalSteps(
          planState,
          accumulators,
          "Step was not marked terminal before the final response.",
          session.chartStoppingEvidence,
        );
        onPlanStateChange(planState);
        emitProgressSnapshot();
      }

      let terminalTextContent = textContent;
      const researchRouteMissing = explicitResearchRouteRequired && !researchRoute && !stopReason;
      if (
        researchRouteMissing &&
        iter < MAX_EXECUTOR_ITERATIONS - 1 &&
        missingResearchRouteReminderCount < 2
      ) {
        missingResearchRouteReminderCount += 1;
        appendTerminalOutputForContinuation();
        pushUser({
          type: "orchestrator_reminder",
          issue: "selected_research_route_missing",
          instruction:
            "Call set_research_route now. Choose selected_sources if the " +
            "request needs external facts/content, otherwise choose workbook_only. " +
            "Do not return a final answer until the route is set.",
        });
        onEvent?.({
          type: "status",
          message: "Required selected-source routing before continuing.",
        });
        continue;
      }
      if (researchRouteMissing) {
        terminalTextContent = buildMissingResearchRouteTerminalText();
      }
      const scopedResearchMissing =
        !researchRouteMissing &&
        requiresSuccessfulResearch(effectiveToolCallContext.researchContext, researchRoute) &&
        researchAccounting.successfulResearchCallCount === 0 &&
        !stopReason;
      if (
        scopedResearchMissing &&
        iter < MAX_EXECUTOR_ITERATIONS - 1 &&
        missingSelectedFileResearchReminderCount < 2
      ) {
        missingSelectedFileResearchReminderCount += 1;
        appendTerminalOutputForContinuation();
        pushUser({
          type: "orchestrator_reminder",
          issue: "selected_research_source_not_used",
          instruction:
            "The request requires the selected uploaded files or named document " +
            "source. Call the research tool now and ground the answer or workbook " +
            "write in its result. Do not use workbook content or another source instead.",
        });
        onEvent?.({
          type: "status",
          message: "Required selected-file research before finalizing.",
        });
        continue;
      }
      if (scopedResearchMissing) {
        terminalTextContent = buildMissingRequiredResearchTerminalText();
      }

      const blockedOperationIssue: ExecutorVerificationIssue | null = blockedOperation
        ? buildBlockedOperationVerificationIssue(blockedOperation)
        : null;
      const executorVerificationIssue = buildExecutorVerificationIssue(
        verificationState,
        accumulators,
      );
      const verificationIssue = scopedResearchMissing
        ? null
        : (executorVerificationIssue ?? blockedOperationIssue);
      const manualReviewBlockedOperation =
        blockedOperation !== undefined &&
        !isReadbackRecoverableBlockedWrite(blockedOperation.toolName);
      const canRemindVerification =
        iter < MAX_EXECUTOR_ITERATIONS - 1 &&
        verificationState.reminderCount < MAX_EXECUTOR_VERIFICATION_REMINDERS &&
        (!manualReviewBlockedOperation || executorVerificationIssue !== null);
      if (verificationIssue && canRemindVerification) {
        verificationState.reminderCount += 1;
        diag("llm", "multi_hop_executor_verification_reminder", {
          iteration: iter + 1,
          reason: verificationIssue.reason,
          reminderCount: verificationState.reminderCount,
        });
        appendTerminalOutputForContinuation();
        pushUser({
          type: "orchestrator_reminder",
          issue: "workbook_output_verification",
          reason: verificationIssue.reason,
          hardErrors: verificationIssue.hardErrors,
          instruction: verificationIssue.instruction,
        });
        onEvent?.({
          type: "status",
          message: "Reminded model to verify workbook outputs before finishing.",
        });
        continue;
      }
      if (verificationIssue) {
        const terminalVerificationIssue =
          manualReviewBlockedOperation && blockedOperationIssue
            ? blockedOperationIssue
            : verificationIssue;
        if (manualReviewBlockedOperation) {
          clearContentReadbackVerification(verificationState);
        }
        // A continuation that spent its whole reminder budget without landing a
        // single new edit will not land one on the next click either. Retire the
        // outstanding content expectations and stop offering to continue, so the
        // user gets a definite outcome instead of an endless "continue" prompt.
        if (
          !manualReviewBlockedOperation &&
          resumeCheckpoint &&
          !madeNewConfirmedEdits()
        ) {
          continuationExhausted = true;
          clearContentReadbackVerification(verificationState);
          diag("llm", "multi_hop_executor_continuation_exhausted", {
            iteration: iter + 1,
            reason: verificationIssue.reason,
          });
        }

        markVerificationNeedsAttention(
          planState,
          accumulators,
          terminalVerificationIssue.reason,
          manualReviewBlockedOperation ? blockedOperation?.stepId : undefined,
        );
        onPlanStateChange(planState);
        emitProgressSnapshot();
        terminalTextContent = continuationExhausted
          ? buildStalledContinuationTerminalText(terminalVerificationIssue)
          : buildVerificationPartialTerminalText(terminalVerificationIssue);
      }

      diag("llm", "multi_hop_executor_complete", {
        iterations: iter + 1,
        textLen: terminalTextContent.length,
      });
      finalTextContent = terminalTextContent;
      completed = true;
      break;
    }

    // Continuation: replay model output, execute tools, append results
    pushModelTurn(turnResult.raw);

    /**
     * Records one completed research call on the operation's evidence.
     *
     * Captured here rather than at the end of the run because a run that pauses
     * or is cancelled is exactly the one whose findings must survive. Only this
     * call's own contribution is captured, so the pack never accumulates
     * citation metadata that some other tool put in the shared index.
     */
    const recordResearchEvidence = (
      call: AgentToolCall,
      parsedArgs: unknown,
      response: Record<string, unknown>,
      citations: readonly unknown[],
      citedValuesByUuid: Record<string, string>
    ): void => {
      if (!evidenceConfig) return;
      const question =
        typeof (parsedArgs as { query?: unknown } | undefined)?.query === "string"
          ? ((parsedArgs as { query: string }).query)
          : "";
      const answer = typeof response.answer === "string" ? response.answer : "";
      if (!question.trim() || !answer.trim()) return;
      const callCitationIndex: CitationNoteIndex = {};
      ingestAgentResponse(callCitationIndex, { citations });
      try {
        researchEvidence = appendExcelOperationEvidence({
          current: researchEvidence,
          operationId: evidenceConfig.operationId,
          policyFingerprint: evidenceConfig.policyFingerprint,
          tool: call.name,
          question,
          answer,
          ...(planState.currentStepId ? { stepId: planState.currentStepId } : {}),
          citations,
          sources: extractSourcesFromCitations(citations),
          citationNoteIndex: callCitationIndex,
          citedValuesByUuid,
        });
        evidenceConfig.onUpdate?.(researchEvidence);
      } catch (error) {
        // A rejected append means this run's policy is not the pack's. The run
        // continues on its own findings rather than corrupting the record.
        diag("tool", "multi_hop_executor_evidence_rejected", {
          reason: error instanceof Error ? error.message : String(error),
        });
      }
    };

    // Per-iteration tool-call helpers shared by the sequential and parallel
    // dispatch paths below.
    const emitToolStart = (
      call: AgentToolCall,
      parsedArgs: unknown,
      visible = true
    ): number => {
      diag("tool", "multi_hop_executor_tool_start", {
        iteration: iter + 1,
        name: call.name,
        callId: call.callId,
        args: parsedArgs,
      });
      if (visible) {
        onEvent?.({ type: "tool_call_started", name: call.name, args: parsedArgs, iteration: iter + 1, callId: call.callId });
      }
      return Date.now();
    };

    const emitToolComplete = (
      call: AgentToolCall,
      parsedArgs: unknown,
      startedAt: number,
      result: ToolCallDetailedResult,
      visible = true
    ): void => {
      const endedAt = Date.now();
      const elapsedMs = endedAt - startedAt;
      const toolOutput = result.output;
      diag("tool", "multi_hop_executor_tool_complete", {
        iteration: iter + 1,
        name: call.name,
        callId: call.callId,
        elapsedMs,
        output: toolOutput,
      });
      let toolStatus: "ok" | "error" = "ok";
      let parsedOutput: unknown = toolOutput;
      try {
        parsedOutput = JSON.parse(toolOutput);
        const o = parsedOutput as {
          ok?: unknown;
          applied?: unknown;
          error?: unknown;
        };
        if (o.ok === false || o.applied === false || o.error) {
          toolStatus = "error";
        }
      } catch {
        // non-JSON output is assumed successful
      }
      if (call.name === "generate_formula") {
        factSetFormatRegistry.recordGenerateFormulaOutput(parsedOutput);
      }
      const parsedResearchResponse =
        parsedOutput &&
        typeof parsedOutput === "object" &&
        !Array.isArray(parsedOutput)
          ? { ...(parsedOutput as Record<string, unknown>) }
          : {};
      const researchCitations = Array.isArray(result.researchCitations)
        ? result.researchCitations
        : Array.isArray(parsedResearchResponse.citations)
          ? parsedResearchResponse.citations
          : [];
      const researchCitedValuesByUuid =
        isAnyResearchToolName(call.name) && toolStatus === "ok"
          ? extractCitedValuesById({
              ...parsedResearchResponse,
              citations: researchCitations,
            })
          : {};
      recordRunPhase(`tool:${call.name}`, elapsedMs, { status: toolStatus });
      if (visible) {
        onEvent?.({
          type: "tool_call_completed",
          name: call.name,
          result: toolOutput,
          resultPreview: toolOutput.slice(0, 500),
          elapsedMs,
          callId: call.callId,
          status: toolStatus,
          iteration: iter + 1,
          ...(toolStatus === "ok" && researchCitations.length > 0
            ? { researchCitations }
            : {}),
          ...(Object.keys(researchCitedValuesByUuid).length > 0
            ? { researchCitedValuesByUuid }
            : {}),
          ...(result.observationMetadata
            ? { observationMetadata: result.observationMetadata }
            : {}),
        });
      }

      if (isAnyResearchToolName(call.name) && toolStatus === "ok") {
        ingestResearchToolResult(
          toolOutput,
          retrievedSources,
          retrievedCitations,
          accumulators,
          result.researchCitations
        );
      }
      // An answer served from the operation's own evidence is a replay of
      // research that was already counted and already recorded. Treating it as
      // a fresh call would append a duplicate of the entry it was read from —
      // growing the pack on every resume — and would credit the run with
      // research it never performed.
      const reusedFromCompletedResearch =
        parsedResearchResponse.reusedFromCompletedResearch === true;
      if (
        isAnyResearchToolName(call.name) &&
        toolStatus === "ok" &&
        !reusedFromCompletedResearch &&
        hasUsableResearchAnswer(parsedOutput)
      ) {
        researchAccounting.successfulResearchCallCount += 1;
        recordResearchEvidence(
          call,
          parsedArgs,
          parsedResearchResponse,
          researchCitations,
          researchCitedValuesByUuid
        );
      }
      if (
        call.name === START_TABULAR_QUERY_WORKLOAD_TOOL_NAME &&
        toolStatus === "ok"
      ) {
        // A successful start always owns a newly registered workload: starts
        // against an unfinished workload are rejected by the controller.
        researchAccounting.tabularResearchSuccessCounted = false;
      }
      if (
        call.name === COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME &&
        toolStatus === "ok" &&
        runtime.tabularQueryWorkload.hasUsableVerifiedResult &&
        !researchAccounting.tabularResearchSuccessCounted
      ) {
        researchAccounting.successfulResearchCallCount += 1;
        researchAccounting.tabularResearchSuccessCounted = true;
      }
      // Start is included because preflight can close every row terminally --
      // a cell-mode workload over the executable-row cap, for instance -- and
      // such a workload is never committed. Without this the owner step stays
      // open and the run offers to continue work that cannot succeed.
      if (
        (call.name === COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME ||
          call.name === START_TABULAR_QUERY_WORKLOAD_TOOL_NAME) &&
        toolStatus === "ok" &&
        runtime.tabularQueryWorkload.isComplete &&
        runtime.tabularQueryWorkload.failedResultCount > 0
      ) {
        const ownerStepId = runtime.tabularQueryWorkload.ownerStepId;
        if (ownerStepId) completedTerminalWorkloadStepIds.add(ownerStepId);
      }

      postExcelToolObservation({
        context: config.langfuseContext,
        phase: "excel_unified_agent",
        executorMode: "plan-protocol",
        toolName: call.name,
        ...(call.name === START_TABULAR_QUERY_WORKLOAD_TOOL_NAME ||
        call.name === NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME ||
        call.name === COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME
          ? { spanKey: call.callId }
          : {}),
        args: parsedArgs,
        result,
        startedAtMs: startedAt,
        endedAtMs: endedAt,
        success: toolStatus === "ok",
        parsedOutput,
      });

      updateExecutorVerificationStateFromToolCompletion(
        verificationState,
        call.name,
        parsedArgs,
        parsedOutput,
        accumulators
      );
      if (call.name === "set_run_plan" && recordValue(parsedOutput).ok === true) {
        const planInput = recordValue(parsedArgs);
        const planSteps = Array.isArray(planInput.steps) ? planInput.steps.map(recordValue) : [];
        const updatedIntent = resolveForwardPeIntentSignals(userRequest, [
          typeof planInput.summary === "string" ? planInput.summary : undefined,
          ...planSteps.flatMap((step) => [
            typeof step.description === "string" ? step.description : undefined,
            typeof step.action === "string" ? step.action : undefined,
          ]),
        ]);
        updateForwardPeChartVerificationIntent(forwardPeChartVerification, updatedIntent);
      }
      updateForwardPeChartVerificationState(
        forwardPeChartVerification,
        call.name,
        parsedArgs,
        parsedOutput,
        isRangeChartToolName(call.name)
          ? forwardPeRangeChartIntentForCall(parsedArgs)
          : undefined,
      );
      if (call.name === "read_range" && blockedOperation) {
        const blockedReadback = evaluateBlockedGranularWriteFromRead(
          blockedOperation,
          parsedArgs,
          parsedOutput,
          accumulators
        );
        if (blockedReadback.complete) {
          executedWriteFingerprints.add(
            stableWriteFingerprint(blockedOperation.toolName, blockedOperation.args)
          );
          blockedOperation = undefined;
        } else if (blockedReadback.readbackObserved) {
          blockedOperation = {
            ...blockedOperation,
            readbackObserved: true,
            unresolvedCells: blockedReadback.unresolvedCells,
            inFlightCells: undefined,
            confirmedEditedCells: Array.from(
              new Set([
                ...(blockedOperation.confirmedEditedCells ?? []),
                ...blockedReadback.confirmedEditedCells,
              ])
            ),
          };
        }
      }
      emitProgressSnapshot();
    };

    let stopInstructionQueued = false;
    let missingRunPlanReminderQueued = false;
    const sameTurnHasIdLookup = functionCalls.some(
      (call) =>
        call.name === ID_LOOKUP_TOOL_NAME || call.name === NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME
    );
    let idLookupRoutingReminderQueued = false;
    let executorWorkloadReminderQueued = false;
    let deferredResearchAfterLookup = false;
    let idLookupRoutingRelevantToolExecuted = false;
    const runCall = async (
      call: AgentToolCall,
      parsedArgs: unknown,
      researchExecutionMode: "stream" | "buffered" = "stream"
    ): Promise<ToolCallDetailedResult> => {
      // Hard-stop on cancellation before dispatching. This is what keeps a
      // queued stateful write from running after the user clicked Stop: by
      // the time a serialized later call reaches here, an aborted signal
      // short-circuits it instead of mutating the workbook.
      throwIfAborted(signal);
      if (stopReason) return { output: buildSkippedToolOutput(call.name, stopReason) };
      if (call.name === "set_research_route") {
        if (!explicitResearchRouteRequired) {
          return {
            output: JSON.stringify({
              ok: false,
              error: "A research route is not required for this run.",
            }),
          };
        }
        if (researchRoute) {
          return {
            output: JSON.stringify({
              ok: false,
              error: `The research route is already set to ${researchRoute}.`,
            }),
          };
        }
        const nextRoute = parseResearchRoute(parsedArgs);
        if (!nextRoute) {
          return {
            output: JSON.stringify({
              ok: false,
              error: "Invalid research route. Choose selected_sources or workbook_only.",
            }),
          };
        }
        researchRoute = nextRoute;
        return {
          output: JSON.stringify({ ok: true, route: nextRoute }),
        };
      }
      if (explicitResearchRouteRequired && !researchRoute) {
        return {
          output: JSON.stringify({
            ok: false,
            skipped: true,
            error: "Set the research route before calling other tools.",
            instruction:
              "Call set_research_route with selected_sources for external facts/content " +
              "or workbook_only for work based entirely on the workbook.",
          }),
        };
      }
      if (
        shouldDeferToolUntilSelectedResearch(
          call.name,
          researchRoute,
          researchAccounting.successfulResearchCallCount,
        )
      ) {
        return {
          output: JSON.stringify({
            ok: false,
            skipped: true,
            error: "Query the explicitly selected research sources before using this tool.",
            instruction:
              "Call the FactSetAI/Finster research tool first. After selected-source " +
              "research succeeds, ordinary Excel tools remain available if needed.",
          }),
        };
      }
      if (
        planState.steps.length === 0 &&
        isRunPlanRequiredToolCall(call.name, parsedArgs)
      ) {
        return { output: buildMissingRunPlanSkippedToolOutput(call.name) };
      }
      if (isRangeChartToolName(call.name)) {
        const forwardPeBlock = getForwardPeRangeChartBlock(
          forwardPeChartVerification,
          parsedArgs,
          forwardPeRangeChartIntentForCall(parsedArgs),
        );
        if (forwardPeBlock) {
          return { output: JSON.stringify(forwardPeBlock) };
        }
      }
      if (call.name === "set_step_status" && isTerminalWorkloadStatus(parsedArgs)) {
        const workloadIssue = getExecutorWorkloadIssue();
        if (workloadIssue) {
          executorWorkloadReminderQueued = true;
          return {
            output: JSON.stringify({
              ok: false,
              skipped: true,
              error: `The ${executorWorkloadLabel} is not complete, so the step cannot be marked terminal yet.`,
              retryable: true,
              retry_with: workloadIssue.nextTool,
              instruction: workloadIssue.instruction,
            }),
          };
        }
      }
      const tabularWorkloadIssue = includeTabularQueryWorkload
        ? getExecutorWorkloadIssue()
        : null;
      if (tabularWorkloadIssue && isAnyResearchToolName(call.name)) {
        executorWorkloadReminderQueued = true;
        return {
          output: JSON.stringify({
            ok: false,
            skipped: true,
            error: "The aligned worksheet-column request is owned by the tabular workload.",
            retryable: true,
            retry_with: tabularWorkloadIssue.nextTool,
            instruction: tabularWorkloadIssue.instruction,
          }),
        };
      }
      let researchDecision: IdLookupResearchDecision | null = null;
      if (isAnyResearchToolName(call.name)) {
        researchDecision = idLookupRoutingPolicy.evaluateResearchCall(parsedArgs, {
          sameTurnHasLookup: sameTurnHasIdLookup,
        });
        if (researchDecision.kind === "blocked") {
          idLookupRoutingReminderQueued = true;
          deferredResearchAfterLookup ||= researchDecision.nextTool === "research";
          diag("tool", "multi_hop_executor_id_lookup_research_blocked", {
            iteration: iter + 1,
            callId: call.callId,
            reason: researchDecision.reason,
            nextTool: researchDecision.nextTool,
          });
          return {
            output: buildBlockedIdLookupResearchOutput(
              researchDecision,
              researchDecision.nextTool === "id_lookup"
                ? ID_LOOKUP_TOOL_NAME
                : researchDecision.nextTool === "research"
                  ? getExcelFinsterBrandContext().researchToolName
                  : null
            ),
          };
        }
        if (researchDecision.kind === "fallback") {
          // Reserve before the first await so duplicate calls from this model
          // turn cannot race the same pending fallback.
          idLookupRoutingRelevantToolExecuted = true;
          idLookupRoutingPolicy.recordResearchAttempt(researchDecision);
        } else if (
          researchDecision.kind === "normal" &&
          idLookupRoutingPolicy.pendingFallbackCount > 0
        ) {
          // Unrelated research never consumes a pending identity fallback.
          idLookupRoutingRelevantToolExecuted = true;
        }
      }

      const formulaCachePlan =
        call.name === "generate_formula"
          ? prepareGenerateFormulaCachePlan(
              parsedArgs,
              formulaResolutionCache,
              formulaResolutionAttempts
            )
          : null;
      if (formulaCachePlan && formulaCachePlan.missingQueries.length === 0) {
        return mergeGenerateFormulaCacheResult(
          formulaCachePlan,
          null,
          formulaResolutionCache,
          formulaResolutionAttempts
        );
      }
      if (formulaCachePlan) {
        for (const query of Array.from(new Set(formulaCachePlan.missingQueries))) {
          if (!query) continue;
          const nextAttempt = (formulaResolutionAttempts.get(query) ?? 0) + 1;
          formulaResolutionAttempts.delete(query);
          formulaResolutionAttempts.set(query, nextAttempt);
        }
      }
      // Research this operation already completed is answered from its own
      // record rather than paid for again. Only an exact repeat of the same
      // question under the same policy qualifies; anything the record does not
      // cover still runs, because deciding what counts as a genuinely new
      // factual gap is the model's judgment, not a string comparison.
      if (isAnyResearchToolName(call.name)) {
        const reusable = findReusableExcelEvidence(researchEvidence, {
          tool: call.name,
          question:
            typeof recordValue(parsedArgs).query === "string"
              ? (recordValue(parsedArgs).query as string)
              : "",
        });
        if (reusable) {
          return {
            output: JSON.stringify({
              answer: reusable.answer,
              reusedFromCompletedResearch: true,
            }),
          };
        }
      }
      if (
        isReplayGuardedWriteToolCall(call.name, parsedArgs) &&
        blockedOperationExactlyMatches(
          blockedOperation,
          call.name,
          parsedArgs,
        )
      ) {
        return {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "partial_write_replay_blocked",
            editedCells: blockedOperation?.confirmedEditedCells,
            error:
              "This exact write may have partially applied before the prior run stopped and was not replayed.",
            instruction:
              blockedOperation &&
              isReadbackRecoverableBlockedWrite(blockedOperation.toolName)
                ? "Read back the original targets, then issue a narrower exact-shaped write only for coordinates still missing."
                : "Do not replay this formatting, mixed, or structural write. Mark the affected step partial or failed with the host evidence.",
          }),
        };
      }
      const writeFingerprint = isReplayGuardedWriteToolCall(
        call.name,
        parsedArgs,
      )
        ? stableWriteFingerprint(call.name, parsedArgs)
        : undefined;
      const deduplicateConfirmedWrite = isConfirmedWriteDeduplicationCandidate(
        call.name,
        parsedArgs,
      );
      if (
        deduplicateConfirmedWrite &&
        writeFingerprint &&
        executedWriteFingerprints.has(writeFingerprint)
      ) {
        if (blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)) {
          blockedOperation = undefined;
        }
        return {
          output: JSON.stringify({
            ok: true,
            applied: true,
            replayPrevented: true,
            writeFingerprint,
            message:
              "This exact workbook write was already confirmed in the prior run and was not replayed.",
          }),
        };
      }
      if (
        !deduplicateConfirmedWrite &&
        writeFingerprint &&
        resumedRepeatableWriteFingerprints.has(writeFingerprint) &&
        !repeatableWriteInspectionObserved
      ) {
        return {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            retryable: true,
            code: "continuation_inspection_required",
            error:
              "This structural write was already confirmed before continuation and was not blindly replayed.",
            instruction:
              "Inspect the current workbook state with list_sheets or a targeted read, then repeat the operation only if another mutation is still required.",
          }),
        };
      }
      const remediationCells =
        isReplayGuardedWriteToolCall(call.name, parsedArgs) &&
        blockedOperation
          ? narrowGranularRemediationCells(
              blockedOperation,
              call.name,
              parsedArgs,
            )
          : undefined;
      if (
        isReplayGuardedWriteToolCall(call.name, parsedArgs) &&
        blockedOperation &&
        !remediationCells &&
        writeOverlapsBlockedOperation(blockedOperation, call.name, parsedArgs)
      ) {
        return {
          output: JSON.stringify({
            ok: false,
            applied: false,
            replayPrevented: true,
            code: "blocked_write_requires_resolution",
            error:
              blockedOperation &&
              isReadbackRecoverableBlockedWrite(blockedOperation.toolName)
                ? "This write targets cells whose prior write still has an unresolved host acknowledgement, so it was not dispatched."
                : "A prior workbook write could not be safely confirmed, so this later write was not dispatched.",
            instruction:
              blockedOperation &&
              isReadbackRecoverableBlockedWrite(blockedOperation.toolName)
                ? "Resolve the blocked target by exact read-back and, only when needed, narrower exact-shaped granular writes before writing these cells again."
                : "Do not replay the blocked formatting, mixed, or structural write. Mark the affected step partial or failed with the host evidence.",
          }),
        };
      }
      const exhaustedGranularRecoveryKey = granularRecoveryKey(call.name, parsedArgs);
      if (exhaustedGranularRecoveryKey && granularRecoveryKeys.has(exhaustedGranularRecoveryKey)) {
        return {
          output: JSON.stringify({
            ok: false,
            applied: false,
            recoveryExhausted: true,
            retryable: false,
            code: "range_write_recovery_exhausted",
            error:
              "The one readback-first recovery round for this exact range write is already exhausted.",
            instruction:
              "Do not replay the original write. Read the target and issue only narrower exact-shaped writes for proven missing coordinates.",
          }),
        };
      }
      const shapeRetryBlock = consumePendingShapeCorrectionAttempt(
        verificationState,
        call.name,
        parsedArgs
      );
      if (shapeRetryBlock) {
        return { output: shapeRetryBlock };
      }
      if (blockedOperation && remediationCells) {
        const inFlight = new Set(
          (blockedOperation.inFlightCells ?? []).map(normalizeLocalAddressKey)
        );
        for (const address of remediationCells) {
          inFlight.add(normalizeLocalAddressKey(address));
        }
        const moved = new Set(remediationCells.map(normalizeLocalAddressKey));
        blockedOperation = {
          ...blockedOperation,
          unresolvedCells: (blockedOperation.unresolvedCells ?? []).filter(
            (address) => !moved.has(normalizeLocalAddressKey(address))
          ),
          inFlightCells: Array.from(inFlight),
        };
        emitProgressSnapshot();
      }
      let provisionalWriteBlockSet = false;
      if (writeFingerprint && !blockedOperation) {
        blockedOperation = {
          toolName: call.name,
          args: parsedArgs,
          ...(planState.currentStepId
            ? { stepId: planState.currentStepId }
            : {}),
          reason:
            "Workbook write dispatch started but no host acknowledgement has been recorded yet.",
        };
        provisionalWriteBlockSet = true;
        emitProgressSnapshot();
      }
      let result: ToolCallDetailedResult;
      try {
        const currentToolCallContext =
          buildCurrentEffectiveToolCallContext();
        const dispatchedResult = await dispatchToolCall(
          call.name,
          formulaCachePlan?.dispatchArgs ?? parsedArgs,
          runtime.executors,
          signal,
          isAnyResearchToolName(call.name)
            ? {
                ...currentToolCallContext,
                researchExecutionMode,
                onResearchSubEvent:
                  researchExecutionMode === "stream"
                    ? (event) =>
                        onEvent?.({
                          type: "research_sub_event",
                          callId: call.callId,
                          event,
                        })
                    : undefined,
              }
            : currentToolCallContext,
          {
            callId: call.callId,
            iteration: iter + 1,
            reportWorkloadProgress: (progress) => {
              try {
                onEvent?.({
                  type: "workload_progress",
                  callId: call.callId,
                  name: call.name,
                  iteration: iter + 1,
                  ...progress,
                });
              } catch {
                // Display-only progress must never change workload execution.
              }
            },
          }
        );
        const recovered = await recoverAmbiguousGranularRangeWrite({
          toolName: call.name,
          parsedArgs,
          result: dispatchedResult,
          executors: runtime.executors,
          signal,
          pinnedWorkbookName: session.pinnedWorkbookName,
          recoveryKeys: granularRecoveryKeys,
          accumulators,
          onEvent,
        });
        if (writeFingerprint) {
          const parsed = recordValue(safeParseJson(recovered.output));
          if (isConfirmedWriteResult(parsed)) {
            if (deduplicateConfirmedWrite) {
              executedWriteFingerprints.add(writeFingerprint);
            } else {
              confirmedRepeatableWriteFingerprints.add(writeFingerprint);
              confirmedRepeatableWriteCount += 1;
            }
            if (blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)) {
              blockedOperation = undefined;
            }
          } else if (shouldCheckpointBlockedWrite(parsed)) {
            const confirmedEditedCells = Array.isArray(parsed.editedCells)
              ? parsed.editedCells.filter(
                  (address): address is string =>
                    typeof address === "string" && address.trim().length > 0
                )
              : [];
            if (
              !blockedOperation ||
              blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)
            ) {
              const recoveredReason = extractBlockedWriteReason(parsed);
              const granularEvidence = blockedGranularEvidenceFromResult(
                call.name,
                parsedArgs,
                parsed
              );
              blockedOperation = {
                toolName: call.name,
                args: parsedArgs,
                ...(planState.currentStepId
                  ? { stepId: planState.currentStepId }
                  : {}),
                ...(recoveredReason ? { reason: recoveredReason } : {}),
                ...(confirmedEditedCells.length > 0 ? { confirmedEditedCells } : {}),
                ...granularEvidence,
              };
            }
          } else if (remediationCells && blockedOperation) {
            const restored = new Set(
              (blockedOperation.unresolvedCells ?? []).map(normalizeLocalAddressKey)
            );
            for (const address of remediationCells) {
              restored.add(normalizeLocalAddressKey(address));
            }
            const moved = new Set(remediationCells.map(normalizeLocalAddressKey));
            blockedOperation = {
              ...blockedOperation,
              unresolvedCells: Array.from(restored),
              inFlightCells: (blockedOperation.inFlightCells ?? []).filter(
                (address) => !moved.has(normalizeLocalAddressKey(address))
              ),
            };
          } else if (
            provisionalWriteBlockSet &&
            blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)
          ) {
            blockedOperation = undefined;
          }
          emitProgressSnapshot();
        }
        result = formulaCachePlan
          ? mergeGenerateFormulaCacheResult(
              formulaCachePlan,
              recovered,
              formulaResolutionCache,
              formulaResolutionAttempts
            )
          : recovered;
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (err instanceof ExecutorWorkbookHandoffError) {
          stopReason =
            `Excel changed the active workbook, but the conversation could not ` +
            `move to "${err.targetWorkbookName}". The run stopped to avoid ` +
            "continuing against split workbook and chat state.";
          stopInstructionQueued = true;
          session.skipFinalVerificationReason = stopReason;
          return {
            output: JSON.stringify({
              ok: false,
              code: "workbook_handoff_failed",
              error: err.message,
              targetWorkbookName: err.targetWorkbookName,
              hostMutationSucceeded: true,
              retryable: false,
            }),
          };
        }
        if (
          err instanceof TabularQueryExecutionError &&
          err.kind === "aborted"
        ) {
          throw new DOMException("Tabular query workload cancelled.", "AbortError");
        }
        if (err instanceof TabularQueryWorkloadSuspensionError) throw err;
        const msg = err instanceof Error ? err.message : String(err);
        if (isReplayGuardedWriteToolCall(call.name, parsedArgs)) {
          const ambiguousResult =
            await recoverAmbiguousGranularRangeWrite({
              toolName: call.name,
              parsedArgs,
              result: {
                output: JSON.stringify({
                  ok: false,
                  applied: false,
                  ambiguousWrite: true,
                  failedActions: ["tool dispatch acknowledgement"],
                  error: msg,
                }),
              },
              executors: runtime.executors,
              signal,
              pinnedWorkbookName: session.pinnedWorkbookName,
              recoveryKeys: granularRecoveryKeys,
              accumulators,
              onEvent,
            });
          const parsed = recordValue(safeParseJson(ambiguousResult.output));
          if (writeFingerprint && isConfirmedWriteResult(parsed)) {
            if (deduplicateConfirmedWrite) {
              executedWriteFingerprints.add(writeFingerprint);
            } else {
              confirmedRepeatableWriteFingerprints.add(writeFingerprint);
              confirmedRepeatableWriteCount += 1;
            }
            if (blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)) {
              blockedOperation = undefined;
            }
          } else if (
            !blockedOperation ||
            blockedOperationExactlyMatches(blockedOperation, call.name, parsedArgs)
          ) {
            const confirmedEditedCells = Array.isArray(parsed.editedCells)
              ? parsed.editedCells.filter(
                  (address): address is string =>
                    typeof address === "string" && address.trim().length > 0
                )
              : [];
            const granularEvidence = blockedGranularEvidenceFromResult(
              call.name,
              parsedArgs,
              parsed
            );
            blockedOperation = {
              toolName: call.name,
              args: parsedArgs,
              ...(planState.currentStepId
                ? { stepId: planState.currentStepId }
                : {}),
              reason: extractBlockedWriteReason(parsed) ?? msg,
              ...(confirmedEditedCells.length > 0 ? { confirmedEditedCells } : {}),
              ...granularEvidence,
            };
          }
          emitProgressSnapshot();
          result = ambiguousResult;
        } else {
          result = {
            output: JSON.stringify({
              ok: false,
              error: msg,
            }),
          };
        }
      }

      if (call.name === NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME) {
        const lookupWaveOutput = recordValue(safeParseJson(result.output));
        if (lookupWaveOutput.reauthenticationRequired === true) {
          const errorCode =
            typeof lookupWaveOutput.errorCode === "string"
              ? lookupWaveOutput.errorCode
              : "factset_reauthentication_required";
          const remainingRows = Number.isInteger(lookupWaveOutput.remainingRows)
            ? (lookupWaveOutput.remainingRows as number)
            : null;
          stopReason =
            "FactSet authentication expired, so the worksheet lookup was paused without " +
            "writing the failed wave. Reconnect FactSet, then rerun the same request; " +
            "existing helper results will be skipped.";
          stopInstructionQueued = true;
          session.skipFinalVerificationReason = stopReason;
          diag("llm", "multi_hop_executor_id_lookup_reauthentication_stop", {
            iteration: iter + 1,
            errorCode,
            remainingRows,
          });
        }
      }

      if (researchDecision?.kind === "fallback") {
        let parsedResearchOutput: unknown;
        try {
          parsedResearchOutput = JSON.parse(result.output);
        } catch {
          parsedResearchOutput = null;
        }
        const parsedResearchRecord =
          parsedResearchOutput &&
          typeof parsedResearchOutput === "object" &&
          !Array.isArray(parsedResearchOutput)
            ? (parsedResearchOutput as {
                ok?: unknown;
                error?: unknown;
              })
            : null;
        const usableAnswer =
          parsedResearchRecord?.ok !== false &&
          !parsedResearchRecord?.error &&
          hasUsableResearchAnswer(parsedResearchOutput);
        idLookupRoutingPolicy.recordResearchResult(researchDecision, usableAnswer);
        if (usableAnswer) idLookupRoutingReminderCount = 0;
      }

      if (call.name === ID_LOOKUP_TOOL_NAME) {
        idLookupRoutingRelevantToolExecuted = true;
        const assessment = idLookupRoutingPolicy.recordLookupResult(parsedArgs, result.output);
        if (assessment.kind !== "retry_lookup") {
          idLookupRoutingReminderCount = 0;
        }
        diag("tool", "multi_hop_executor_id_lookup_policy_assessment", {
          iteration: iter + 1,
          callId: call.callId,
          assessment,
        });
      } else if (call.name === NEXT_ID_LOOKUP_WORKLOAD_WAVE_TOOL_NAME) {
        const routingRecords = runtime.idLookupWorkload.takeRoutingRecords();
        if (routingRecords.length > 0) {
          idLookupRoutingRelevantToolExecuted = true;
        }
        for (const routingRecord of routingRecords) {
          const assessment = idLookupRoutingPolicy.recordLookupResult(
            routingRecord.args,
            routingRecord.output,
            { registerFallback: false }
          );
          if (assessment.kind !== "retry_lookup") {
            idLookupRoutingReminderCount = 0;
          }
          diag("tool", "multi_hop_executor_id_lookup_workload_assessment", {
            iteration: iter + 1,
            callId: call.callId,
            rowNumbers: routingRecord.rowNumbers,
            assessment,
          });
        }
      }
      if (
        CONTINUATION_REPEAT_INSPECTION_TOOLS.has(call.name) &&
        !extractToolError(result.output)
      ) {
        repeatableWriteInspectionObserved = true;
      }
      return result;
    };

    const remindOnMissingRunStepsAction = (result: ToolCallDetailedResult): void => {
      const error = extractToolError(result.output);
      if (
        (!isHardMissingRunStepsWriteError(error) && !isMissingRunPlanSkippedWrite(result.output)) ||
        stopReason
      )
        return;
      missingRunPlanReminderQueued = true;
      onEvent?.({
        type: "status",
        message: "Reminded model to create run steps before retrying the skipped action.",
      });
      diag("llm", "multi_hop_executor_missing_plan_write_reminder", { error });
    };

    const remindOnMissingRunStepsStatus = (
      call: AgentToolCall,
      result: ToolCallDetailedResult,
    ): void => {
      if (call.name !== "set_step_status" || planState.steps.length > 0 || stopReason) return;
      const error = extractToolError(result.output);
      if (!isMissingRunStepsStatusError(error)) return;
      missingRunPlanReminderQueued = true;
      onEvent?.({
        type: "status",
        message: "Reminded model to create run steps before updating status.",
      });
      diag("llm", "multi_hop_executor_missing_plan_status_reminder", { error });
    };

    const processTemplateOutcomesNow = async (): Promise<void> => {
      if (stopReason || applyTemplateOutcomes.length === 0) return;
      const previousPinnedWorkbookName = session.pinnedWorkbookName;
      const templateResolution = await processApplyTemplateOutcomes(
        applyTemplateOutcomes,
        session,
        {
          probeActiveContext,
          bridgeAvailable: bridgeAvailable ?? false,
          onWorkbookHandoff,
        },
        onEvent,
      );
      if (session.pinnedWorkbookName !== previousPinnedWorkbookName) {
        resetWorkbookScopedRunAccumulators(accumulators);
      }
      applyTemplateOutcomes.length = 0;
      if (templateResolution.shouldStop) {
        stopReason = templateResolution.reason;
        terminalizeNonTerminalSteps(
          planState,
          accumulators,
          templateResolution.reason,
          session.chartStoppingEvidence,
        );
        onPlanStateChange(planState);
        stopInstructionQueued = true;
        emitProgressSnapshot();
        diag("llm", "multi_hop_executor_template_stop", {
          reason: templateResolution.reason,
        });
      }
    };

    if (functionCalls.some((c) => c.name === "apply_template")) {
      // Sequential path: apply_template can open a new workbook, so its
      // outcome must be processed (and the workbook re-pinned) before any
      // later call in the same turn runs against the stale workbook.
      for (const call of functionCalls) {
        throwIfAborted(signal);
        const parsedArgs = safeParseJson(call.arguments);
        const visible = isClientVisibleExecutorTool(call.name);
        const startedAt = emitToolStart(call, parsedArgs, visible);
        const result = await runCall(call, parsedArgs);
        emitToolComplete(call, parsedArgs, startedAt, result, visible);
        remindOnMissingRunStepsAction(result);
        remindOnMissingRunStepsStatus(call, result);
        pushToolResult(call.callId, result.output);
        await processTemplateOutcomesNow();
      }
    } else if (
      functionCalls.some((call) =>
        isStatefulToolCall(call.name, safeParseJson(call.arguments)),
      )
    ) {
      // Mixed / stateful batch: execute sequentially in emission order so
      // writes apply deterministically and any same-turn read after a write
      // observes the post-write state instead of racing ahead of it. The
      // per-call `throwIfAborted` (here and inside `runCall`) ensures queued
      // calls do not run once the run is cancelled.
      for (const call of functionCalls) {
        throwIfAborted(signal);
        const parsedArgs = safeParseJson(call.arguments);
        const visible = isClientVisibleExecutorTool(call.name);
        const startedAt = emitToolStart(call, parsedArgs, visible);
        const result = await runCall(call, parsedArgs);
        emitToolComplete(call, parsedArgs, startedAt, result, visible);
        remindOnMissingRunStepsAction(result);
        remindOnMissingRunStepsStatus(call, result);
        pushToolResult(call.callId, result.output);
      }
    } else {
      // Read-only batches have no workbook ordering hazard, but some read-only
      // tools can fan out into many HTTP submit/poll cycles. Keep those batches
      // bounded so later verification/remediation turns do not overwhelm the
      // local proxy and make Excel appear hung.
      throwIfAborted(signal);
      const isParallelResearchGroup =
        functionCalls.length > 1 &&
        functionCalls.every((call) => isAnyResearchToolName(call.name));
      const researchBrandName = isParallelResearchGroup
        ? getExcelFinsterBrandContext().publicBrandName
        : "";
      const runReadOnlyCall = (
        call: AgentToolCall,
        options?: {
          researchExecutionMode?: "stream" | "buffered";
          visible?: boolean;
        },
      ): Promise<{ call: AgentToolCall; output: string }> => {
        const parsedArgs = safeParseJson(call.arguments);
        const visible = options?.visible ?? isClientVisibleExecutorTool(call.name);
        const startedAt = emitToolStart(call, parsedArgs, visible);
        return runCall(call, parsedArgs, options?.researchExecutionMode ?? "stream").then(
          (result) => {
            emitToolComplete(call, parsedArgs, startedAt, result, visible);
            return { call, output: result.output };
          }
        );
      };
      const shouldSerializeReadOnlyBatch = functionCalls.some(
        (call) => call.name === "generate_formula" || isAnyResearchToolName(call.name)
      );
      const batchResults: Array<{ call: AgentToolCall; output: string }> = [];
      if (isParallelResearchGroup) {
        const groupId = `research-group-${iter + 1}`;
        const groupName = functionCalls[0].name;
        let completedRequests = 0;
        const emitResearchProgress = () =>
          onEvent?.({
            type: "research_group_progress",
            groupId,
            name: groupName,
            iteration: iter + 1,
            completedRequests,
            totalRequests: functionCalls.length,
          });
        emitResearchProgress();
        onEvent?.({
          type: "status",
          message: `${researchBrandName} parallel research: dispatching ${functionCalls.length} request(s).`,
        });
        batchResults.push(
          ...(await Promise.all(
            functionCalls.map(async (call) => {
              const result = await runReadOnlyCall(call, {
                researchExecutionMode: "buffered",
                visible: false,
              });
              completedRequests += 1;
              emitResearchProgress();
              return result;
            })
          ))
        );
      } else if (shouldSerializeReadOnlyBatch) {
        for (const call of functionCalls) {
          throwIfAborted(signal);
          batchResults.push(await runReadOnlyCall(call));
        }
      } else {
        batchResults.push(
          ...(await mapWithConcurrencyLimit(
            functionCalls,
            MAX_PARALLEL_READ_ONLY_TOOL_CALLS,
            runReadOnlyCall
          ))
        );
      }

      for (const { call, output } of batchResults) {
        remindOnMissingRunStepsAction({ output });
        pushToolResult(call.callId, output);
      }
      if (isParallelResearchGroup) {
        const wave = summarizeResearchWave(batchResults.map(({ output }) => output));
        onEvent?.({
          type: "status",
          message:
            `${researchBrandName} parallel research: ${batchResults.length} request(s) complete, ` +
            `${wave.requestsWithoutUsableAnswer} without a usable answer ` +
            `(answer sizes ${wave.minAnswerChars}-${wave.maxAnswerChars} chars).`,
        });
        pushUser({
          type: "orchestrator_reminder",
          issue: "parallel_research_complete",
          requestCount: batchResults.length,
          requestsWithoutUsableAnswer: wave.requestsWithoutUsableAnswer,
          instruction:
            "Treat this completed research wave as best effort. Use every usable answer, " +
            "mark rows without usable evidence as Unable to verify or Needs attention, " +
            "and continue the requested workbook write. Do not fail the whole step or " +
            "start another FactSet AI wave for the same known workload.",
        });
      }
    }

    const idLookupRoutingContinuation =
      !stopReason && (idLookupRoutingRelevantToolExecuted || idLookupRoutingReminderQueued)
        ? idLookupRoutingPolicy.terminalBlock()
        : null;
    const routingNextToolName = idLookupRoutingContinuation
      ? idLookupRoutingContinuation.nextTool === "id_lookup"
        ? requiredIdLookupToolName()
        : getExcelFinsterBrandContext().researchToolName
      : deferredResearchAfterLookup
        ? getExcelFinsterBrandContext().researchToolName
        : null;
    // Forcing shares the reminder budget, which is why the assignment lives
    // inside the guarded block below. A model that keeps sending arguments the
    // tool rejects would otherwise be forced back into the same tool every
    // iteration until the hard loop cap, with no new instruction to act on.
    const routingReminderQueuedThisIteration =
      !stopReason &&
      (idLookupRoutingContinuation || idLookupRoutingReminderQueued) &&
      idLookupRoutingReminderCount < MAX_EXECUTOR_ID_LOOKUP_ROUTING_REMINDERS;
    if (routingReminderQueuedThisIteration) {
      idLookupRoutingReminderCount += 1;
      if (routingNextToolName) forcedToolChoiceName = routingNextToolName;
      pushUser({
        type: "orchestrator_reminder",
        issue:
          idLookupRoutingContinuation?.issue ?? "id_lookup_research_suppressed",
        requestedFields: idLookupRoutingPolicy.requestedFields,
        pendingFallbacks: idLookupRoutingPolicy.pendingFallbacksSnapshot,
        instruction:
          idLookupRoutingContinuation?.nextTool === "id_lookup" &&
          includeIdLookupWorkload
            ? `Continue the registered worksheet lookup with ${requiredIdLookupToolName()} until every row is written and verified.`
            : idLookupRoutingContinuation?.instruction ??
              (deferredResearchAfterLookup
                ? "The unrelated research call was deferred until the same-turn id_lookup result was available. Retry that research now."
                : "Use the completed id_lookup result for the identity request. Do not retry skipped identity research unless a requested field remains pending."),
      });
      diag("llm", "multi_hop_executor_id_lookup_routing_continuation", {
        iteration: iter + 1,
        issue: idLookupRoutingContinuation?.issue ?? "id_lookup_research_suppressed",
        reminderCount: idLookupRoutingReminderCount,
        forcedToolChoiceName,
      });
    }

    const postToolWorkloadIssue = stopReason ? null : getExecutorWorkloadIssue();
    if (postToolWorkloadIssue) {
      const progressVersion = executorWorkloadParticipant?.progressVersion ?? -1;
      if (
        !isWorkloadRegistrationRequired(postToolWorkloadIssue) &&
        progressVersion !== executorWorkloadReminderProgressVersion
      ) {
        executorWorkloadReminderProgressVersion = progressVersion;
        executorWorkloadReminderCount = 0;
      }
      if (
        !routingReminderQueuedThisIteration &&
        executorWorkloadReminderCount < MAX_EXECUTOR_WORKLOAD_REMINDERS
      ) {
        executorWorkloadReminderCount += 1;
        executorWorkloadReminderQueued = true;
        forcedToolChoiceName = requiredExecutorWorkloadToolName();
        pushUser({
          type: "orchestrator_reminder",
          issue: postToolWorkloadIssue.issue,
          workloadId: postToolWorkloadIssue.workloadId,
          remainingRows: postToolWorkloadIssue.remainingRows,
          nextRowNumbers: postToolWorkloadIssue.nextRowNumbers,
          instruction:
            forcedToolChoiceName === "set_run_plan"
              ? `Create the run plan now, then register the ${executorWorkloadLabel}.`
              : postToolWorkloadIssue.instruction,
        });
        diag("llm", "multi_hop_executor_id_lookup_workload_continuation", {
          iteration: iter + 1,
          issue: postToolWorkloadIssue.issue,
          reminderCount: executorWorkloadReminderCount,
          progressVersion,
          forcedToolChoiceName,
        });
      } else if (
        !routingReminderQueuedThisIteration &&
        executorWorkloadReminderCount >= MAX_EXECUTOR_WORKLOAD_REMINDERS
      ) {
        stopReason = executorWorkloadStoppedReason;
        stopInstructionQueued = true;
        session.skipFinalVerificationReason = stopReason;
      }
    } else if (executorWorkloadReminderQueued) {
      executorWorkloadReminderCount = 0;
    }

    if (stopReasonAtRequestStart && stopReason) {
      terminalizeNonTerminalSteps(
        planState,
        accumulators,
        stopReason,
        session.chartStoppingEvidence,
      );
      onPlanStateChange(planState);
      emitProgressSnapshot();
      finalTextContent = JSON.stringify({
        mode: "partial",
        reply: stopReason,
      });
      completed = true;
      onEvent?.({
        type: "status",
        message: "Stopped after the model ignored the final stop instruction.",
      });
      break;
    }

    if (stopInstructionQueued && stopReason) {
      pushUser({
        type: "orchestrator_stop",
        reason: stopReason,
        instruction:
          "Do not call more tools. Return the final JSON response now with mode 'partial' and a concise reply summarizing completed work plus the remaining issue.",
      });
    } else if (
      missingRunPlanReminderQueued &&
      planState.steps.length === 0 &&
      missingRunPlanReminderCount < MAX_EXECUTOR_MISSING_PLAN_REMINDERS
    ) {
      missingRunPlanReminderCount += 1;
      pushUser({
        type: "orchestrator_reminder",
        issue: "missing_run_plan",
        instruction:
          "The previous status/write tool call failed because no run steps exist. Any attempted workbook write was skipped and did not apply. " +
          "If the user request requires workbook edits, call set_run_plan as your next tool call before any further list_sheets, read_range, set_step_status, generate_formula, write, or chart tools. " +
          "Do not return a final failed response for this correctable sequencing issue.",
      });
    } else if (missingRunPlanReminderQueued && planState.steps.length === 0) {
      stopReason = "Workbook edits were skipped because no run plan was created first.";
      pushUser({
        type: "orchestrator_stop",
        reason: stopReason,
        instruction:
          "Do not call more tools. Return the final JSON response now with mode 'partial' and explain that no workbook changes were applied because run steps were not created first.",
      });
      onEvent?.({
        type: "status",
        message: "Stopped after repeated workbook writes before creating run steps.",
      });
    }

    // A model that re-issues the same calls while nothing in the run changes
    // will keep doing so until the iteration cap, spending the whole budget on
    // a loop that cannot finish. `stopReason` runs are exempt: they have
    // already been told to return their final response and are bounded by the
    // check at the top of the next iteration. Iterations in which the runner
    // pushed a reminder of its own are not stagnant either - that is counted as
    // progress by the signature below.
    if (!stopReason && functionCalls.length > 0) {
      const stagnant = stagnationBudget.record(
        normalizeExecutorToolCalls(functionCalls),
        executorProgressSignature({
          planSteps: planState.steps.length,
          stepStatuses: accumulators.stepStatuses,
          editedCells: accumulators.editedCells.length,
          researchAnswers: researchAccounting.successfulResearchCallCount,
          workloadProgress:
            executorWorkloadParticipant?.progressVersion ?? 0,
          orchestratorMessages: orchestratorMessageCount,
        }),
      );
      if (stagnant) {
        forcedStopReason =
          `Executor stopped after ${MAX_STAGNANT_EXECUTOR_ITERATIONS} iterations that repeated ` +
          "the same tool calls without making progress.";
        diag("llm", "multi_hop_executor_no_progress_stop", {
          iteration: iter + 1,
          toolNames: functionCalls.map((call) => call.name),
        });
        onEvent?.({ type: "status", message: forcedStopReason });
        break;
      }
    }

    // If we reached the last iteration without terminal text, log a warning
    if (iter === MAX_EXECUTOR_ITERATIONS - 1) {
      onEvent?.({
        type: "status",
        message: `Tool loop hit iteration cap (${MAX_EXECUTOR_ITERATIONS}).`,
      });
      diag("llm", "multi_hop_executor_iteration_cap", { iterations: MAX_EXECUTOR_ITERATIONS });
    }
  }

  if (!completed) {
    const iterationCapLookupBlock = idLookupRoutingPolicy.terminalBlock();
    const iterationCapWorkloadIssue = getExecutorWorkloadIssue();
    const iterationCapIdentityFailure = resolveIdLookupIterationCapFailure({
      workloadIssue: iterationCapWorkloadIssue,
      workloadKind: includeTabularQueryWorkload ? "tabular_query" : "id_lookup",
      workloadIncompleteRows:
        iterationCapWorkloadIssue !== null &&
        iterationCapWorkloadIssue.workloadId !== null &&
        !executorWorkloadParticipant?.isComplete
          ? (executorWorkloadParticipant?.describeIncompleteRows() ?? [])
          : [],
      routingBlock: iterationCapLookupBlock,
      routingUnresolved: idLookupRoutingPolicy.pendingFallbacksSnapshot.map(
        ({ searchText, missingFields }) => `${searchText}: ${missingFields.join(", ")}`
      ),
    });
    const reason =
      iterationCapIdentityFailure?.reason ??
      forcedStopReason ??
      `Executor stopped at iteration cap (${MAX_EXECUTOR_ITERATIONS}) before a final response.`;
    if (planState.steps.length > 0) {
      terminalizeNonTerminalSteps(
        planState,
        accumulators,
        reason,
        session.chartStoppingEvidence,
      );
    }
    session.skipFinalVerificationReason = reason;
    onPlanStateChange(planState);
    emitProgressSnapshot();
    onEvent?.({ type: "status", message: reason });
    diag("llm", "multi_hop_executor_cap_partial", {
      iterations: iterationsCompleted,
      forcedStopReason,
      stepResults: accumulators.stepResults,
      stepStatuses: accumulators.stepStatuses,
    });
    const requiredResearchMissing =
      requiresSuccessfulResearch(effectiveToolCallContext.researchContext, researchRoute) &&
      researchAccounting.successfulResearchCallCount === 0;
    finalTextContent =
      requiredResearchMissing && !iterationCapIdentityFailure
        ? buildMissingRequiredResearchTerminalText()
        : JSON.stringify({
            mode: "failed",
            reply:
              "I couldn't safely finish the request before the execution limit, so I did not report it as complete.",
            unresolved: iterationCapIdentityFailure?.unresolved ?? [reason],
          });
  }

  if (!signal?.aborted && accumulators.workbookEditAttempted) {
    const formatSafety = await executeFactSetFormatSafetyPass(runtimeCtx);
    if (formatSafety.corrected > 0) {
      onEvent?.({
        type: "status",
        message: `Corrected ${formatSafety.corrected} FactSet percentage format(s).`,
      });
    }
    if (formatSafety.error) {
      const warning =
        `FactSet percentage formatting could not be fully verified: ` + formatSafety.error;
      onEvent?.({ type: "status", message: warning });
      const stepIndex = [...planState.steps]
        .map((step, index) => ({ step, index }))
        .reverse()
        .find(({ step }) => step.status !== "failed" && step.status !== "paused")?.index;
      if (stepIndex !== undefined) {
        const step = planState.steps[stepIndex];
        step.status = "warning";
        step.note = warning;
        accumulators.stepStatuses[stepIndex] = "warning";
        accumulators.stepDisplayResults[stepIndex] =
          `${step.planStep.description} (warning: ${warning})`;
        setStepResultLine(
          accumulators,
          step.id,
          `${step.planStep.step}. needs attention: ${warning}`
        );
        onPlanStateChange(planState);
        emitProgressSnapshot();
      } else {
        accumulators.stepResults.push(`needs attention: ${warning}`);
      }
    }
  }

  emitRunTimingSummary();

  // -------------------------------------------------------------------------
  // Assemble result
  // -------------------------------------------------------------------------

  const terminal = parseTerminalResponse(finalTextContent);
  const finalPlan = buildCurrentPlan();
  emitProgressSnapshot();

  const hasWorkbookEvidence =
    accumulators.workbookEditAttempted ||
    accumulators.editedCells.length > 0 ||
    accumulators.lastModifications !== null ||
    accumulators.fdsRefreshSheets.size > 0 ||
    accumulators.deferredRefreshAllRequested ||
    accumulators.deferredRecalcMode !== null;
  const terminalMode = terminal.mode ?? (hasWorkbookEvidence ? "workbook_edit" : "answer");
  const replyCitedValuesByUuid = extractCitedValuesFromText(
    terminal.reply || finalTextContent || "",
  );
  const finalCitedValuesByUuid = mergeCitedValuesByUuid(
    accumulators.retrievedCitedValuesByUuid,
    replyCitedValuesByUuid,
  );
  const retrievedCitedValuesByUuid =
    Object.keys(finalCitedValuesByUuid).length > 0
      ? finalCitedValuesByUuid
      : undefined;
  const finalCheckpoint = buildCheckpoint() ?? undefined;
  const hasActionableTabularCheckpoint =
    finalCheckpoint?.tabularQueryWorkload !== undefined &&
    hasActionableExcelCheckpointWork(finalCheckpoint);
  if (
    resumeCheckpoint &&
    !continuationExhausted &&
    finalCheckpoint &&
    hasActionableExcelCheckpointWork(finalCheckpoint) &&
    !madeNewConfirmedEdits() &&
    checkpointOutstandingWorkSignature(finalCheckpoint) === resumedOutstandingSignature
  ) {
    continuationExhausted = true;
    diag("llm", "multi_hop_executor_unchanged_continuation_exhausted", {
      outstandingSignature: resumedOutstandingSignature,
    });
  }

  if (
    !hasWorkbookEvidence &&
    !hasActionableTabularCheckpoint &&
    terminalMode !== "workbook_edit" &&
    terminalMode !== "partial"
  ) {
    const normalizedMode =
      terminalMode === "clarification"
        ? "clarification"
        : terminalMode === "failed"
          ? "failed"
          : "answer";
    return finalizeWithWorkloadReset({
      mode: normalizedMode,
      reply: terminal.reply || finalTextContent || "Done.",
      retrievedSources: dedupeSources(retrievedSources),
      retrievedCitations: retrievedCitations.length > 0 ? retrievedCitations : undefined,
      retrievedCitedValuesByUuid,
      workbookEditAttempted: false,
    });
  }

  if (!finalPlan) {
    return finalizeWithWorkloadReset({
      mode: "failed",
      reply:
        terminal.reply ||
        "I couldn't complete that safely because the run attempted workbook work without establishing run steps.",
      retrievedSources: dedupeSources(retrievedSources),
      retrievedCitations: retrievedCitations.length > 0 ? retrievedCitations : undefined,
      retrievedCitedValuesByUuid,
      workbookEditAttempted: false,
    });
  }

  return finalizeWithWorkloadReset({
    mode:
      terminalMode === "partial" || terminalMode === "failed"
        ? terminalMode
        : hasActionableTabularCheckpoint
          ? "partial"
          : "workbook_edit",
    reply: terminal.reply || finalTextContent || undefined,
    retrievedSources: dedupeSources(retrievedSources),
    retrievedCitations: retrievedCitations.length > 0 ? retrievedCitations : undefined,
    retrievedCitedValuesByUuid,
    plan: finalPlan,
    stepResults: accumulators.stepResults,
    stepDisplayResults: accumulators.stepDisplayResults,
    stepStatuses: accumulators.stepStatuses,
    lastModifications: accumulators.lastModifications,
    editedCells: accumulators.editedCells,
    formulaWriteTargets: accumulators.formulaWriteTargets,
    autofitSheetNames: accumulators.autofitSheetNames,
    autofitTargets: accumulators.autofitTargets,
    columnSizingHints: accumulators.columnSizingHints,
    explicitColumnWidthKeys: accumulators.explicitColumnWidthKeys,
    fdsRefreshSheets: accumulators.fdsRefreshSheets,
    deferredRefreshAllRequested: accumulators.deferredRefreshAllRequested,
    deferredRecalcMode: accumulators.deferredRecalcMode,
    sessionLockSheet: session.sessionLockSheet,
    workbookEditAttempted: accumulators.workbookEditAttempted,
    skipFinalVerificationReason: session.skipFinalVerificationReason,
    finalPinnedWorkbookName: session.pinnedWorkbookName,
    checkpoint: finalCheckpoint,
    ...(continuationExhausted ? { continuationExhausted: true } : {}),
  });
}

// ---------------------------------------------------------------------------
// Tool dispatch
// ---------------------------------------------------------------------------

/**
 * Dispatches a tool call to the appropriate executor. Run-scoped tools
 * (set_step_status, revise_plan, modify_workbook) are tried first via the
 * runtime executor map. If not found, falls back to the global
 * `executeToolCallDetailed` which handles stateless tools (list_sheets,
 * read_range, list_chart_types, generate_formula, get_skill, retrieve_template).
 *
 * @param name - Tool name from the model's tool call.
 * @param args - Parsed arguments.
 * @param runtimeExecutors - Run-scoped executor map from `createExecutorToolRuntime`.
 * @param signal - Optional abort signal.
 * @param context - Optional tool call context for stateless tools.
 * @returns Detailed result `{ output, observationMetadata? }`; `output` is the
 *   JSON string returned to the model, `observationMetadata` carries trace-only
 *   correlation fields (FactSet session / chain id) for stateless tools.
 */
async function dispatchToolCall(
  name: string,
  args: unknown,
  runtimeExecutors: Record<string, RuntimeToolExecutor>,
  signal?: AbortSignal,
  context?: ToolCallContext,
  runtimeCallContext?: RuntimeToolCallContext
): Promise<ToolCallDetailedResult> {
  const runtimeExecutor = runtimeExecutors[name];
  if (runtimeExecutor) {
    const result = await runtimeExecutor(args, signal, runtimeCallContext);
    return typeof result === "string" ? { output: result } : result;
  }
  return executeToolCallDetailed(name, args, signal, context);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts mutable executor plan state back into the persisted `ExecutionPlan`
 * shape returned to the existing Excel finalization path.
 */
function buildExecutionPlanFromState(
  planState: ExecutorPlanState,
  sourcePlan: ExecutionPlan
): ExecutionPlan {
  return {
    summary: planState.summary,
    steps: planState.steps.map((s) => s.planStep),
    estimatedComplexity: planState.estimatedComplexity ?? sourcePlan.estimatedComplexity,
    selectedSkills: sourcePlan.selectedSkills,
  };
}

function isTerminalExecutorStepStatus(
  status: ExecutorPlanState["steps"][number]["status"]
): boolean {
  return (
    status === "success" ||
    status === "warning" ||
    status === "failed" ||
    status === "recovered" ||
    status === "paused"
  );
}

function isCompletedCheckpointStep(status: ExecutorPlanState["steps"][number]["status"]): boolean {
  return status === "success" || status === "recovered";
}

function checkpointOutstandingWorkSignature(checkpoint: ExecutorRunCheckpoint | undefined): string {
  if (!checkpoint) return "";
  const completed = new Set(checkpoint.completedStepIds);
  const verification = checkpoint.verification;
  return canonicalJson({
    incompleteStepIds: checkpoint.stepStates
      .filter((step) => !completed.has(step.id))
      .map((step) => step.id)
      .sort(),
    blockedOperation: checkpoint.blockedOperation
      ? {
          toolName: checkpoint.blockedOperation.toolName,
          stepId: checkpoint.blockedOperation.stepId,
          unresolvedCells: (checkpoint.blockedOperation.unresolvedCells ?? [])
            .map(normalizeLocalAddressKey)
            .sort(),
          inFlightCells: (checkpoint.blockedOperation.inFlightCells ?? [])
            .map(normalizeLocalAddressKey)
            .sort(),
          readbackObserved: checkpoint.blockedOperation.readbackObserved === true,
        }
      : null,
    fdsRefreshSheets: checkpoint.fdsRefreshSheets
      .map((sheetName) => sheetName.trim().toLowerCase())
      .sort(),
    deferredRefreshAllRequested: checkpoint.deferredRefreshAllRequested,
    deferredRecalcMode: checkpoint.deferredRecalcMode,
    forwardPeChartVerification: checkpoint.forwardPeChartVerification?.required
      ? {
          required: checkpoint.forwardPeChartVerification.required,
          requestedWindow:
            checkpoint.forwardPeChartVerification.requestedWindow ?? null,
          formulaStatus:
            checkpoint.forwardPeChartVerification.formulaStatus,
          formulaWritten:
            checkpoint.forwardPeChartVerification.formulaWritten,
          refreshObserved:
            checkpoint.forwardPeChartVerification.refreshObserved,
          recalcObserved:
            checkpoint.forwardPeChartVerification.recalcObserved,
          verifiedSourceRanges:
            checkpoint.forwardPeChartVerification.verifiedSourceRanges
              .map(
                (range) =>
                  `${range.sheetName}!${normalizeLocalAddressKey(range.address)}`,
              )
              .sort(),
          chartCreated:
            checkpoint.forwardPeChartVerification.chartCreated,
        }
      : null,
    tabularQueryWorkload: checkpoint.tabularQueryWorkload
      ? {
          workloadId: checkpoint.tabularQueryWorkload.workload.id,
          mode: checkpoint.tabularQueryWorkload.workload.mode,
          waveInterrupted:
            checkpoint.tabularQueryWorkload.workload.waveInterrupted,
          activeWaveRows:
            checkpoint.tabularQueryWorkload.workload.activeWaveRows.slice(),
          inFlightExecutionKeys:
            checkpoint.tabularQueryWorkload.workload.inFlightExecutionKeys
              .slice()
              .sort(),
          rows: checkpoint.tabularQueryWorkload.workload.rows.map((row) => ({
            rowNumber: row.rowNumber,
            status: row.status,
            failureCode: row.failureCode ?? null,
            writeAttempted: row.writeAttempted === true,
            verifiedUsable: row.verifiedUsable === true,
            hasResult: row.result !== undefined,
            committedValue:
              row.committedValue === undefined ? null : row.committedValue,
          })),
          identityCacheKeys:
            checkpoint.tabularQueryWorkload.workload.identityCache
              .map(([key]) => key)
              .sort(),
          executionCache: checkpoint.tabularQueryWorkload.workload.executionCache
            .map(([key, outcome]) => ({
              key,
              failureCode: outcome.failureCode ?? null,
              hasResult: outcome.result !== undefined,
            }))
            .sort((left, right) => left.key.localeCompare(right.key)),
        }
      : null,
    verification: {
      requiresVerification: verification.requiresVerification,
      refreshRequired: verification.refreshRequired,
      recalcRequired: verification.recalcRequired,
      refreshObserved: verification.refreshObserved,
      recalcObserved: verification.recalcObserved,
      cleanReadbackAfterRecalc: verification.cleanReadbackAfterRecalc,
      hardErrors: verification.hardErrors
        .map((error) => `${error.address.toUpperCase()}|${error.token}`)
        .sort(),
      pendingReadbackSheetNames: verification.pendingReadbackSheetNames.slice().sort(),
      verifiedReadbackSheetNames: verification.verifiedReadbackSheetNames.slice().sort(),
      pendingReadbackTargets: verification.pendingReadbackTargets
        .map((target) => `${target.sheetName}!${normalizeLocalAddressKey(target.address)}`)
        .sort(),
      verifiedReadbackTargets: verification.verifiedReadbackTargets
        .map((target) => `${target.sheetName}!${normalizeLocalAddressKey(target.address)}`)
        .sort(),
      pendingContentReadbackTargets: verification.pendingContentReadbackTargets
        .map((target) => `${target.sheetName}!${normalizeLocalAddressKey(target.address)}`)
        .sort(),
      contentReadbackFailures: verification.contentReadbackFailures
        .map((failure) => `${failure.key}|${failure.reason}`)
        .sort(),
      pendingShapeCorrections: verification.pendingShapeCorrections
        .map(
          (correction) =>
            `${correction.toolName}|${correction.sheetName ?? ""}|` +
            `${normalizeLocalAddressKey(correction.address)}|${correction.attempted}`
        )
        .sort(),
    },
  });
}

export interface ExecutorVerificationState {
  requiresVerification: boolean;
  refreshRequired: boolean;
  recalcRequired: boolean;
  refreshObserved: boolean;
  recalcObserved: boolean;
  cleanReadbackAfterRecalc: boolean;
  hardErrors: ExcelHardErrorCell[];
  pendingReadbackSheetNames: string[];
  verifiedReadbackSheetNames: string[];
  pendingReadbackTargets: ExecutorReadbackTarget[];
  verifiedReadbackTargets: ExecutorReadbackTarget[];
  pendingContentReadbackTargets: ExecutorContentReadbackTarget[];
  verifiedContentReadbackTargetKeys: string[];
  contentReadbackFailures: Array<{ key: string; reason: string }>;
  pendingShapeCorrections: PendingShapeCorrection[];
  reminderCount: number;
}

interface ExecutorReadbackTarget {
  sheetName: string;
  address: string;
}

export type ExecutorExpectedContentCell = WorksheetExpectedContentCell;
export type ExecutorContentReadbackTarget = WorksheetContentReadbackTarget;

interface PendingShapeCorrection {
  toolName: string;
  stepId: string | null;
  sheetName?: string;
  address: string;
  matrixField?: "values" | "formulas";
  attempted: boolean;
}

export type ExecutorContentReadbackEvaluation = WorksheetContentReadbackEvaluation;

/** Backward-compatible executor name for the shared worksheet verifier. */
export const evaluateExecutorContentReadback = evaluateWorksheetContentReadback;

export interface ExecutorVerificationIssue {
  reason: string;
  instruction: string;
  hardErrors: ExcelHardErrorCell[];
}

export function createExecutorVerificationState(): ExecutorVerificationState {
  return {
    requiresVerification: false,
    refreshRequired: false,
    recalcRequired: false,
    refreshObserved: false,
    recalcObserved: false,
    cleanReadbackAfterRecalc: false,
    hardErrors: [],
    pendingReadbackSheetNames: [],
    verifiedReadbackSheetNames: [],
    pendingReadbackTargets: [],
    verifiedReadbackTargets: [],
    pendingContentReadbackTargets: [],
    verifiedContentReadbackTargetKeys: [],
    contentReadbackFailures: [],
    pendingShapeCorrections: [],
    reminderCount: 0,
  };
}

/**
 * Rebuild verification state for a resumed run.
 *
 * Content read-back debt is deliberately NOT carried across a resume. It
 * describes writes a previous run already made, and re-verifying them buys
 * nothing that `executedWriteFingerprints` and `blockedOperation` do not
 * already guarantee. Carrying it forward is what turns one unverifiable target
 * into a continuation loop: every resume inherits the same unsatisfiable
 * expectation, reports the same "not fully verified" warning, and offers the
 * same button again. The reminder budget resets for the same reason.
 */
function restoreExecutorVerificationState(
  checkpoint?: ExecutorRunCheckpoint["verification"]
): ExecutorVerificationState {
  if (!checkpoint) return createExecutorVerificationState();
  return {
    ...checkpoint,
    hardErrors: checkpoint.hardErrors.map((error) => ({ ...error })),
    pendingReadbackSheetNames: checkpoint.pendingReadbackSheetNames.slice(),
    verifiedReadbackSheetNames: checkpoint.verifiedReadbackSheetNames.slice(),
    pendingReadbackTargets: checkpoint.pendingReadbackTargets.map((target) => ({
      ...target,
    })),
    verifiedReadbackTargets: checkpoint.verifiedReadbackTargets.map((target) => ({
      ...target,
    })),
    pendingContentReadbackTargets: [],
    verifiedContentReadbackTargetKeys: [],
    contentReadbackFailures: [],
    pendingShapeCorrections: checkpoint.pendingShapeCorrections.map((correction) => ({
      ...correction,
    })),
    reminderCount: 0,
  };
}

/**
 * Serialize verification state for the checkpoint. Content read-back debt is
 * omitted because {@link restoreExecutorVerificationState} never replays it,
 * and its per-coordinate expectations are by far the largest part of a
 * checkpoint for a wide table write.
 */
function snapshotExecutorVerificationState(
  state: ExecutorVerificationState
): ExecutorRunCheckpoint["verification"] {
  return {
    ...state,
    hardErrors: state.hardErrors.map((error) => ({ ...error })),
    pendingReadbackSheetNames: state.pendingReadbackSheetNames.slice(),
    verifiedReadbackSheetNames: state.verifiedReadbackSheetNames.slice(),
    pendingReadbackTargets: state.pendingReadbackTargets.map((target) => ({
      ...target,
    })),
    verifiedReadbackTargets: state.verifiedReadbackTargets.map((target) => ({
      ...target,
    })),
    pendingContentReadbackTargets: [],
    verifiedContentReadbackTargetKeys: [],
    contentReadbackFailures: [],
    pendingShapeCorrections: state.pendingShapeCorrections.map((correction) => ({ ...correction })),
  };
}

export function updateExecutorVerificationStateFromToolCompletion(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown,
  accumulators: ExecutorRunAccumulators
): void {
  const hasRefreshDebt =
    accumulators.fdsRefreshSheets.size > 0 || accumulators.deferredRefreshAllRequested;
  const hasRecalcDebt = hasRefreshDebt || accumulators.deferredRecalcMode !== null;

  if (isWriteToolCall(toolName, parsedArgs)) {
    updatePendingShapeCorrections(state, toolName, parsedArgs, parsedOutput);
    const contentTargets = readbackContentTargetsFromWriteCompletion(
      toolName,
      parsedArgs,
      parsedOutput
    );
    supersedeContentReadbackCells(state, toolName, parsedArgs, parsedOutput, contentTargets);
    if (
      contentTargets.length > 0 &&
      requiresContentReadbackVerification(toolName, parsedArgs, parsedOutput)
    ) {
      mergePendingContentReadbackTargets(state, contentTargets);
      state.requiresVerification = true;
    }
  }

  if (
    isWriteToolCall(toolName, parsedArgs) &&
    (hasRefreshDebt || hasRecalcDebt)
  ) {
    state.requiresVerification = true;
    state.refreshRequired = state.refreshRequired || hasRefreshDebt;
    state.recalcRequired = state.recalcRequired || hasRecalcDebt;
    state.refreshObserved = false;
    state.recalcObserved = false;
    state.cleanReadbackAfterRecalc = false;
    state.hardErrors = [];
    mergePendingReadbackSheets(state, Array.from(accumulators.fdsRefreshSheets));
    mergePendingReadbackTargets(state, readbackFormulaTargetsFromWriteArgs(parsedArgs));
    if (accumulators.deferredRefreshAllRequested || accumulators.deferredRecalcMode !== null) {
      mergePendingReadbackSheets(state, readbackSheetsFromWriteArgs(parsedArgs, accumulators));
    }
  }

  if (toolName === "refresh_factset_data") {
    const result = parsedOutput as {
      ok?: unknown;
      refreshed?: unknown;
      scope?: unknown;
      scopeConfirmed?: unknown;
      refreshedSheetNames?: unknown;
    };
    const refreshRequestSatisfied =
      result.scopeConfirmed === true || (result.scopeConfirmed === undefined && !hasRefreshDebt);
    if (result?.ok === true && result.refreshed === true && refreshRequestSatisfied) {
      state.requiresVerification = true;
      state.refreshObserved = !hasRefreshDebt;
      state.refreshRequired = hasRefreshDebt;
      state.recalcRequired = true;
      state.cleanReadbackAfterRecalc = false;
      mergePendingReadbackSheets(
        state,
        readbackSheetsFromRefreshResult(parsedArgs, parsedOutput, accumulators, state)
      );
    }
  }

  if (toolName === "recalculate_workbook") {
    const result = parsedOutput as { ok?: unknown; recalculated?: unknown };
    if (result?.ok === true && result.recalculated === true) {
      state.requiresVerification = true;
      state.recalcObserved = true;
      state.recalcRequired = false;
      state.cleanReadbackAfterRecalc = false;
    }
  }

  if (toolName === "read_range") {
    updateContentReadbackVerification(state, parsedArgs, parsedOutput);
    if (state.recalcObserved) {
      const sheetName = sheetNameFromReadRange(parsedArgs, parsedOutput);
      if (!isReadbackSheetRelevant(state, sheetName)) return;
      if (!readRangeReturnedData(parsedOutput)) return;
      const readAddress = readRangeAddress(parsedArgs, parsedOutput);
      const hardErrors = classifyRelevantReadbackHardErrors(
        state,
        sheetName,
        readAddress,
        parsedOutput
      );
      if (hardErrors.length > 0) {
        recordUnavailableFdsTargetsAfterCorrection(state, accumulators, hardErrors);
        state.requiresVerification = true;
        state.hardErrors = mergeHardErrors(
          hardErrorsNotCoveredByCleanRead(state.hardErrors, sheetName, readAddress),
          hardErrors
        );
        state.cleanReadbackAfterRecalc = false;
        return;
      }

      // A clean overlapping read is authoritative for previously observed
      // errors even when it does not also contain one of the original FDS
      // write targets. Keep target-verification debt separate so remediated
      // helper/output formulas do not remain as stale customer-facing errors.
      const remainingHardErrors = hardErrorsNotCoveredByCleanRead(
        state.hardErrors,
        sheetName,
        readAddress
      );
      state.hardErrors = remainingHardErrors;
      if (!markReadbackTargetVerified(state, sheetName, readAddress)) {
        state.cleanReadbackAfterRecalc = false;
        return;
      }
      if (!markReadbackSheetVerified(state, sheetName)) return;
      if (remainingHardErrors.length > 0) {
        state.requiresVerification = true;
        state.cleanReadbackAfterRecalc = false;
        return;
      }
      state.cleanReadbackAfterRecalc =
        allPendingReadbackSheetsVerified(state) && allPendingReadbackTargetsVerified(state);
      state.requiresVerification = !state.cleanReadbackAfterRecalc;
      return;
    }
  }

  if (hasRefreshDebt || hasRecalcDebt) {
    state.requiresVerification = true;
    state.refreshRequired = state.refreshRequired || hasRefreshDebt;
    state.recalcRequired = state.recalcRequired || hasRecalcDebt;
    if (hasRefreshDebt) state.cleanReadbackAfterRecalc = false;
  }
  if (!allPendingContentReadbackTargetsVerified(state)) {
    state.requiresVerification = true;
  } else if (
    !hasRefreshDebt &&
    !hasRecalcDebt &&
    !state.refreshRequired &&
    !state.recalcRequired &&
    !state.recalcObserved &&
    state.pendingShapeCorrections.length === 0
  ) {
    state.requiresVerification = false;
  }
}

export function buildExecutorVerificationIssue(
  state: ExecutorVerificationState,
  accumulators: ExecutorRunAccumulators
): ExecutorVerificationIssue | null {
  if (state.pendingShapeCorrections.length > 0) {
    const correction = state.pendingShapeCorrections[0];
    return {
      reason:
        `A range write still has an unresolved matrix-shape correction for ` +
        `${correction.address}.`,
      instruction: correction.attempted
        ? "The one allowed corrected same-tool call did not succeed. Mark the affected step failed or partial with the range evidence; do not retry another write path."
        : `Correct the matrix dimensions or narrow the address, then call ${correction.toolName} exactly once before finalizing.`,
      hardErrors: [],
    };
  }

  if (!allPendingContentReadbackTargetsVerified(state)) {
    const pending = unverifiedContentReadbackTargets(state);
    const failureByKey = new Map(
      state.contentReadbackFailures.map((failure) => [failure.key, failure.reason])
    );
    const details = pending
      .slice(0, 4)
      .map((target) => {
        const key = contentReadbackTargetKey(target);
        return (
          failureByKey.get(key) ?? `${target.sheetName}!${target.address} has not been read back`
        );
      })
      .join("; ");
    return {
      reason: `Written workbook content is not fully verified: ${details}`,
      instruction:
        'Read each exact written target at full shape with format "json" and truncated=false. ' +
        "For formula targets pass includeFormulas:true and confirm formula text at every requested coordinate; " +
        "for static targets confirm equivalent values at every requested coordinate, including intentional blanks. " +
        "If a bounded recovery already failed, mark the affected step partial/failed instead of reporting success.",
      hardErrors: [],
    };
  }

  const refreshDebt =
    state.refreshRequired ||
    accumulators.fdsRefreshSheets.size > 0 ||
    accumulators.deferredRefreshAllRequested;
  const recalcDebt =
    state.recalcRequired || refreshDebt || accumulators.deferredRecalcMode !== null;

  if (state.hardErrors.length > 0) {
    const evidence = summarizeHardErrors(state.hardErrors);
    return {
      reason: `Formula verification still shows hard Excel errors: ${evidence}`,
      instruction:
        "You returned a final response while verified workbook cells still show hard Excel errors. " +
        "Read the formulas for the error cells, fix the formula parameters or formulas, refresh FactSet data if needed, recalculate the workbook, " +
        "then read back the corrected cells with structured values before returning final JSON. " +
        "If the next bounded remediation attempt still leaves errors, mark the affected step warning with the unresolved cell evidence.",
      hardErrors: state.hardErrors,
    };
  }

  if (refreshDebt && !state.refreshObserved) {
    return {
      reason: "FactSet formula outputs still need a refresh before completion.",
      instruction:
        "You returned a final response before refreshing the FactSet-backed formulas written in this run. " +
        "Call refresh_factset_data once with scope 'worksheets' and every touched formula sheet, or scope 'workbook' when every worksheet needs refreshing. " +
        "Then call recalculate_workbook with mode 'full' and read back the affected ranges with structured values.",
      hardErrors: [],
    };
  }

  if (recalcDebt && !state.recalcObserved) {
    return {
      reason: "Workbook formulas still need recalculation before completion.",
      instruction:
        "You returned a final response before recalculating formula outputs. " +
        "Call recalculate_workbook, then read back the affected ranges with structured values and resolve any hard Excel errors before final JSON.",
      hardErrors: [],
    };
  }

  if (state.requiresVerification && !state.cleanReadbackAfterRecalc) {
    return {
      reason: "Formula outputs still need a verified clean read after refresh and recalculation.",
      instruction:
        "You returned a final response before reading back formula outputs after refresh/recalculation. " +
        "Read the affected ranges with structured values. If they contain #NUM!, #N/A, #VALUE!, #REF!, #DIV/0!, #NAME?, #CALC!, or #SPILL!, fix and verify them before final JSON.",
      hardErrors: [],
    };
  }

  return null;
}

function markVerificationNeedsAttention(
  planState: ExecutorPlanState,
  accumulators: ExecutorRunAccumulators,
  reason: string,
  targetStepId?: string,
): void {
  if (planState.steps.length === 0) return;
  // A step that never ran is still resumable work. Letting the fallback land on
  // one would both misattribute the warning and retire the continuation for
  // work this run never attempted, so only started steps are eligible.
  const startedSteps = planState.steps.filter(
    (step) => step.status !== "queued" && step.status !== "paused"
  );
  const target = targetStepId
    ? planState.steps.find((step) => step.id === targetStepId)
    : (planState.currentStepId &&
        planState.steps.find((step) => step.id === planState.currentStepId)) ||
      startedSteps[startedSteps.length - 1];
  if (!target) return;
  const index = planState.steps.findIndex((step) => step.id === target.id);
  const label = target.planStep.step || index + 1;
  target.status = "warning";
  target.note = reason;
  if (planState.currentStepId === target.id) {
    planState.currentStepId = undefined;
  }

  if (index >= 0) {
    accumulators.stepStatuses[index] = "warning";
    accumulators.stepDisplayResults[index] = `${target.planStep.description} (warning: ${reason})`;
  }
  setStepResultLine(accumulators, target.id, `${label}. needs attention: ${reason}`);
}

export function buildBlockedOperationVerificationIssue(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>
): ExecutorVerificationIssue {
  const operation = describeBlockedWorkbookOperation(blockedOperation);
  const evidence = blockedOperation.reason?.trim();
  const evidenceSuffix = evidence ? ` Host evidence: ${evidence}` : "";
  if (isReadbackRecoverableBlockedWrite(blockedOperation.toolName)) {
    return {
      reason:
        `The ${operation} still has an unresolved host acknowledgement.` +
        evidenceSuffix,
      instruction:
        "Read back the exact original range as dense JSON with includeFormulas:true. " +
        "If it is incomplete, write only proven missing coordinates in exact-shaped ranges, " +
        "then read the original range again.",
      hardErrors: [],
    };
  }
  return {
    reason:
      `The ${operation} could not be safely confirmed and needs manual review.` +
      evidenceSuffix,
    instruction:
      "Do not replay the mixed, formatting, or structural write payload. " +
      "Mark the affected step partial or failed with the host evidence.",
    hardErrors: [],
  };
}

function describeBlockedWorkbookOperation(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>
): string {
  const args = recordValue(blockedOperation.args);
  let sheetName = stringValue(args.sheetName) ?? stringValue(args.targetSheet);
  let address = stringValue(args.address);
  const modifications = recordValue(args.modifications);
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  if ((!sheetName || !address) && worksheets.length > 0) {
    const worksheet = recordValue(worksheets[0]);
    sheetName ??= stringValue(worksheet.sheetName);
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    const cells = Array.isArray(worksheet.cells) ? worksheet.cells : [];
    address ??=
      (ranges.length > 0 ? stringValue(recordValue(ranges[0]).address) : undefined) ??
      (cells.length > 0 ? stringValue(recordValue(cells[0]).address) : undefined);
  }
  const location =
    sheetName && address ? `${sheetName}!${address}` : (sheetName ?? address);
  return location
    ? `${blockedOperation.toolName} write to ${location}`
    : `${blockedOperation.toolName} workbook write`;
}

function buildVerificationPartialTerminalText(issue: ExecutorVerificationIssue): string {
  return JSON.stringify({
    mode: "partial",
    reply:
      "I completed the workbook work, but some workbook outputs still need attention before this can be treated as fully complete.",
    unresolved: [issue.reason],
  });
}

/**
 * Terminal text for a continuation that produced no new edits. It reads as a
 * final answer rather than an invitation to retry, because retrying is exactly
 * what has already been shown not to help.
 */
function buildStalledContinuationTerminalText(issue: ExecutorVerificationIssue): string {
  return JSON.stringify({
    mode: "partial",
    reply:
      "I continued the run but couldn't make any further progress on the workbook, " +
      "so I've stopped here. The work that did land is in the workbook; the item " +
      "below needs a manual look.",
    unresolved: [issue.reason],
  });
}

export function buildMissingRequiredResearchTerminalText(): string {
  return JSON.stringify({
    mode: "failed",
    reply:
      "I couldn't retrieve the required selected source, so I did not answer from the workbook or another source.",
    unresolved: ["The required selected source was not queried successfully."],
  });
}

/**
 * Build the canonical failed terminal envelope for a missing route decision.
 *
 * @returns Serialized executor terminal content.
 */
export function buildMissingResearchRouteTerminalText(): string {
  return JSON.stringify({
    mode: "failed",
    reply:
      "I couldn't safely determine whether this request required the selected research sources, so I did not continue with another data path.",
    unresolved: ["The selected-source routing decision was not completed."],
  });
}

function consumePendingShapeCorrectionAttempt(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown
): string | undefined {
  const args = recordValue(parsedArgs);
  const stepId = typeof args.stepId === "string" ? args.stepId : null;
  const correction = findMatchingShapeCorrection(
    state.pendingShapeCorrections,
    toolName,
    stepId,
    parsedArgs
  );
  if (!correction) return undefined;
  if (correction.attempted) {
    return JSON.stringify({
      ok: false,
      applied: false,
      code: "range_matrix_shape_retry_exhausted",
      retryable: false,
      error:
        `The one allowed ${toolName} matrix-shape correction for ` +
        `${correction.address} has already been attempted.`,
      instruction:
        "Do not retry another write path. Mark the affected step failed or partial with the range evidence.",
    });
  }
  correction.attempted = true;
  return undefined;
}

function findMatchingShapeCorrection(
  corrections: readonly PendingShapeCorrection[],
  toolName: string,
  stepId: string | null,
  parsedArgs: unknown
): PendingShapeCorrection | undefined {
  const candidates = corrections.filter(
    (candidate) => candidate.toolName === toolName && candidate.stepId === stepId
  );
  if (candidates.length === 0) return undefined;
  const targets = readbackContentTargetsFromWriteArgs(toolName, parsedArgs);
  const matches = (correction: PendingShapeCorrection): boolean => {
    const correctionRange = parseA1Range(correction.address);
    if (!correctionRange) return false;
    return targets.some((target) => {
      if (
        correction.sheetName &&
        normalizeSheetName(correction.sheetName) !== normalizeSheetName(target.sheetName)
      ) {
        return false;
      }
      const targetRange = parseA1Range(target.address);
      if (!targetRange || !rangeContains(correctionRange, targetRange)) {
        return false;
      }
      return (
        !correction.matrixField ||
        matrixFieldsForWriteTarget(toolName, parsedArgs, target.sheetName, target.address).has(
          correction.matrixField
        )
      );
    });
  };
  return candidates.find(matches);
}

function matrixFieldsForWriteTarget(
  toolName: string,
  parsedArgs: unknown,
  sheetName: string,
  address: string
): Set<"values" | "formulas"> {
  if (toolName === "set_range_values") {
    return new Set<"values" | "formulas">(["values"]);
  }
  if (toolName === "set_range_formulas") {
    return new Set<"values" | "formulas">(["formulas"]);
  }
  const fields = new Set<"values" | "formulas">();
  const args = recordValue(parsedArgs);
  const modifications = recordValue(args.modifications);
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const candidateSheet = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    if (normalizeSheetName(candidateSheet) !== normalizeSheetName(sheetName)) {
      continue;
    }
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    for (const rangeValue of ranges) {
      const range = recordValue(rangeValue);
      if (
        range.action !== "fill" ||
        normalizeLocalAddressKey(stringValue(range.address) ?? "") !==
          normalizeLocalAddressKey(address)
      ) {
        continue;
      }
      if (range.values !== undefined) fields.add("values");
      if (range.formulas !== undefined) fields.add("formulas");
    }
  }
  return fields;
}

export interface AmbiguousGranularRecoveryOptions {
  toolName: string;
  parsedArgs: unknown;
  result: ToolCallDetailedResult;
  executors: Record<string, RuntimeToolExecutor>;
  signal?: AbortSignal;
  pinnedWorkbookName?: string;
  recoveryKeys: Set<string>;
  accumulators: ExecutorRunAccumulators;
  onEvent?: (event: ExecutorRunEvent) => void;
}

function runtimeToolOutput(result: string | ToolCallDetailedResult): string {
  return typeof result === "string" ? result : result.output;
}

/**
 * Build the one-shot recovery identity for a granular range write.
 *
 * @param toolName - Candidate executor tool name.
 * @param parsedArgs - Parsed arguments containing sheet and address.
 * @returns A normalized key for granular writes, otherwise `undefined`.
 */
export function granularRecoveryKey(
  toolName: string,
  parsedArgs: unknown
): string | undefined {
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return undefined;
  }
  const args = recordValue(parsedArgs);
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address) return undefined;
  return [
    toolName,
    stringValue(args.stepId) ?? "",
    normalizeSheetName(sheetName) ?? sheetName,
    normalizeLocalAddressKey(address),
  ].join("|");
}

export async function recoverAmbiguousGranularRangeWrite(
  options: AmbiguousGranularRecoveryOptions
): Promise<ToolCallDetailedResult> {
  const {
    toolName,
    parsedArgs,
    result,
    executors,
    signal,
    pinnedWorkbookName,
    recoveryKeys,
    accumulators,
    onEvent,
  } = options;
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return result;
  }
  const output = safeParseJson(result.output);
  const outputRecord = recordValue(output);
  const failedActions = Array.isArray(outputRecord.failedActions) ? outputRecord.failedActions : [];
  if (
    outputRecord.applied !== false ||
    outputRecord.retryReason !== undefined ||
    outputRecord.code === "range_matrix_shape_mismatch" ||
    (failedActions.length === 0 && outputRecord.ambiguousWrite !== true)
  ) {
    return result;
  }

  const args = recordValue(parsedArgs);
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address) return result;
  const recoveryKey = granularRecoveryKey(toolName, parsedArgs);
  if (!recoveryKey) return result;
  if (recoveryKeys.has(recoveryKey)) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        instruction: "The one readback-first recovery round for this range is already exhausted.",
      }),
    };
  }
  recoveryKeys.add(recoveryKey);

  const readExecutor = executors.read_range;
  const writeExecutor = executors[toolName];
  if (!readExecutor || !writeExecutor) return result;
  throwIfAborted(signal);
  onEvent?.({
    type: "status",
    message: `Checking ${sheetName}!${address} before bounded write recovery.`,
  });
  const readArgs = {
    sheetName,
    address,
    workbookName: pinnedWorkbookName ?? null,
    format: "json",
    includeFormulas: true,
    includeFormats: false,
  };
  const readOutput = safeParseJson(runtimeToolOutput(await readExecutor(readArgs, signal)));
  const readRecord = recordValue(readOutput);
  const target = readbackContentTargetsFromWriteArgs(toolName, parsedArgs)[0];
  if (!target) return result;
  if (
    readRecord.error ||
    readRecord.truncated === true ||
    !hasDenseReadbackEvidence(target, readRecord)
  ) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        recoveryReadback: readOutput,
        recoveryError:
          "The exact dense formula/value read-back was unavailable or truncated, so the runtime did not replay an ambiguous write.",
      }),
    };
  }
  const evaluation = evaluateExecutorContentReadback(target, readArgs, readOutput);
  const missingKeys = new Set(
    evaluation.missingCells.map((cell) => `${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedCells = target.cells.filter(
    (cell) => !missingKeys.has(`${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedAddresses = recordReadbackConfirmedGranularWrite(
    accumulators,
    toolName,
    args,
    target,
    confirmedCells
  );
  if (evaluation.complete) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        ok: true,
        applied: true,
        error: undefined,
        failedActions: undefined,
        initialFailedActions: failedActions,
        editedCells: confirmedAddresses,
        recoveredByReadback: true,
        recoveryReadback: readOutput,
      }),
    };
  }
  const segments = buildMissingRowSegments(
    evaluation.missingCells,
    toolName === "set_range_formulas" ? "formulas" : "values"
  );
  if (segments.length === 0) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        recoveryReadback: readOutput,
        recoveryError: evaluation.reason ?? "Read-back did not prove full target coverage.",
      }),
    };
  }

  const retryResults: Array<{
    address: string;
    output: unknown;
    applied: boolean;
  }> = [];
  for (const segment of segments) {
    throwIfAborted(signal);
    const retryArgs = {
      stepId: typeof args.stepId === "string" && args.stepId.trim() ? args.stepId : null,
      sheetName,
      address: segment.address,
      [segment.field]: segment.matrix,
    };
    const retryOutput = safeParseJson(runtimeToolOutput(await writeExecutor(retryArgs, signal)));
    retryResults.push({
      address: segment.address,
      output: retryOutput,
      applied: recordValue(retryOutput).applied === true,
    });
  }
  const allApplied = retryResults.every((retry) => retry.applied);
  return {
    ...result,
    output: JSON.stringify({
      ...outputRecord,
      ok: allApplied,
      applied: allApplied,
      error: allApplied ? undefined : "One or more bounded missing-coordinate retries failed.",
      failedActions: allApplied
        ? undefined
        : retryResults
            .filter((retry) => !retry.applied)
            .map((retry) => `range ${retry.address} bounded recovery`),
      initialFailedActions: failedActions,
      editedCells: Array.from(
        new Set([
          ...confirmedAddresses,
          ...retryResults.filter((retry) => retry.applied).map((retry) => retry.address),
        ])
      ),
      recoveredByRetry: allApplied,
      recoveryExhausted: true,
      recoveryReadback: readOutput,
      recoveryAttempts: retryResults,
    }),
  };
}

function recordReadbackConfirmedGranularWrite(
  accumulators: ExecutorRunAccumulators,
  toolName: string,
  args: Record<string, unknown>,
  target: ExecutorContentReadbackTarget,
  confirmedCells: readonly ExecutorExpectedContentCell[] = target.cells
): string[] {
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address || confirmedCells.length === 0) return [];
  const isFormulaWrite =
    toolName === "set_range_formulas" || confirmedCells.some((cell) => cell.kind === "formula");
  const field = toolName === "set_range_formulas" ? ("formulas" as const) : ("values" as const);
  const isFullTarget =
    confirmedCells.length === target.cells.length &&
    target.cells.every((cell) =>
      confirmedCells.some(
        (confirmed) =>
          confirmed.kind === cell.kind &&
          normalizeLocalAddressKey(confirmed.address) === normalizeLocalAddressKey(cell.address)
      )
    );
  const segments: MissingRowSegment[] = isFullTarget
    ? [
        {
          address,
          field,
          matrix:
            field === "formulas"
              ? (args.formulas as (string | null)[][])
              : (args.values as (string | number | boolean | null)[][]),
        },
      ]
    : buildMissingRowSegments(confirmedCells, field);
  const confirmedAddresses = segments.map((segment) => segment.address);
  for (const confirmedAddress of confirmedAddresses) {
    if (!accumulators.editedCells.includes(confirmedAddress)) {
      accumulators.editedCells.push(confirmedAddress);
    }
  }
  accumulators.lastModifications = {
    worksheets: [
      {
        sheetName,
        ranges: segments.map((segment) => ({
          address: segment.address,
          action: "fill" as const,
          [segment.field]: segment.matrix,
        })),
      },
    ],
    message:
      `Confirmed ${confirmedAddresses.map((entry) => `${sheetName}!${entry}`).join(", ")} ` +
      "from read-back after an ambiguous host acknowledgement.",
  };
  recordColumnSizingIntent(accumulators, accumulators.lastModifications);
  accumulators.autofitSheetNames ??= new Set<string>();
  accumulators.autofitTargets ??= [];
  accumulators.autofitSheetNames.add(sheetName);
  for (const confirmedAddress of confirmedAddresses) {
    if (
      !accumulators.autofitTargets.some(
        (entry) =>
          normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
          normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(confirmedAddress)
      )
    ) {
      accumulators.autofitTargets.push({
        sheetName,
        address: confirmedAddress,
      });
    }
  }
  if (isFormulaWrite) {
    accumulators.formulaWriteTargets ??= [];
    for (const confirmedAddress of confirmedAddresses) {
      if (
        !accumulators.formulaWriteTargets.some(
          (entry) =>
            normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
            normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(confirmedAddress)
        )
      ) {
        accumulators.formulaWriteTargets.push({
          sheetName,
          address: confirmedAddress,
        });
      }
    }
  }
  const fdsCells = confirmedCells.filter(
    (cell) => cell.kind === "formula" && isFdsBoundFormula(cell.expected)
  );
  if (fdsCells.length > 0) {
    accumulators.fdsRefreshSheets.add(sheetName);
    accumulators.fdsFormulaWriteTargets ??= [];
    for (const cell of fdsCells) {
      if (
        !accumulators.fdsFormulaWriteTargets.some(
          (entry) =>
            normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
            normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(cell.address)
        )
      ) {
        accumulators.fdsFormulaWriteTargets.push({
          sheetName,
          address: cell.address,
        });
      }
    }
  }
  return confirmedAddresses;
}

interface MissingRowSegment {
  address: string;
  field: "values" | "formulas";
  matrix: Array<Array<string | number | boolean | null>>;
}

function buildMissingRowSegments(
  missingCells: readonly ExecutorExpectedContentCell[],
  field: "values" | "formulas"
): MissingRowSegment[] {
  const byRow = new Map<
    number,
    Array<{ row: number; column: number; cell: ExecutorExpectedContentCell }>
  >();
  for (const cell of missingCells) {
    const position = parseA1Cell(cell.address);
    if (!position) continue;
    const entries = byRow.get(position.row) ?? [];
    entries.push({ ...position, cell });
    byRow.set(position.row, entries);
  }
  const segments: MissingRowSegment[] = [];
  for (const [row, entries] of Array.from(byRow.entries()).sort(
    ([left], [right]) => left - right
  )) {
    const sorted = entries.sort((left, right) => left.column - right.column);
    let current: typeof sorted = [];
    const flush = (): void => {
      if (current.length === 0) return;
      const start = current[0];
      const end = current[current.length - 1];
      const address =
        start.column === end.column
          ? formatA1Cell(row, start.column)
          : `${formatA1Cell(row, start.column)}:${formatA1Cell(row, end.column)}`;
      segments.push({
        address,
        field,
        matrix: [current.map((entry) => entry.cell.expected)],
      });
      current = [];
    };
    for (const entry of sorted) {
      if (current.length > 0 && entry.column !== current[current.length - 1].column + 1) {
        flush();
      }
      current.push(entry);
    }
    flush();
  }
  return segments;
}

function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

const MAX_CHECKPOINT_FORMULA_CACHE_ENTRIES = 200;
const FORMULA_CONTINUATION_BATCH_SIZE = 50;
const MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY = 2;

interface GenerateFormulaCachePlan {
  queries: string[];
  missingQueries: string[];
  exhaustedQueries: string[];
  dispatchArgs: Record<string, unknown>;
}

function prepareGenerateFormulaCachePlan(
  args: unknown,
  cache: ReadonlyMap<string, Record<string, unknown>>,
  attempts: ReadonlyMap<string, number>
): GenerateFormulaCachePlan | null {
  const input = recordValue(args);
  if (!Array.isArray(input.queries)) return null;
  const queries = input.queries.map((query) => (typeof query === "string" ? query.trim() : ""));
  const exhaustedQueries = queries.filter(
    (query) =>
      !!query &&
      !cache.has(query) &&
      (attempts.get(query) ?? 0) >= MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY
  );
  const exhausted = new Set(exhaustedQueries);
  const missingQueries = queries.filter(
    (query) => (!query || !cache.has(query)) && !exhausted.has(query)
  );
  return {
    queries,
    missingQueries,
    exhaustedQueries,
    dispatchArgs:
      missingQueries.length === queries.length ? input : { ...input, queries: missingQueries },
  };
}

function buildGenerateFormulaCacheEnvelope(
  results: Array<Record<string, unknown>>,
  cacheHitCount: number
): Record<string, unknown> {
  const unresolvedQueries = results
    .filter((result) => typeof result.error === "string" && result.dataUnavailable !== true)
    .map((result) => String(result.query ?? ""));
  const retryableQueries = results
    .filter((result) => typeof result.error === "string" && result.retryable !== false)
    .map((result) => String(result.query ?? ""));
  // Surfaced separately from `unresolvedQueries` so the model can see at a
  // glance which metrics are finished-as-unavailable rather than outstanding.
  const unavailableQueries = results
    .filter((result) => result.dataUnavailable === true)
    .map((result) => String(result.query ?? ""));
  const resolvedCount = results.length - unresolvedQueries.length;
  const status =
    unresolvedQueries.length === 0 ? "complete" : resolvedCount > 0 ? "partial" : "failed";
  const continuationBatches: string[][] = [];
  for (let start = 0; start < retryableQueries.length; start += FORMULA_CONTINUATION_BATCH_SIZE) {
    continuationBatches.push(
      retryableQueries.slice(start, start + FORMULA_CONTINUATION_BATCH_SIZE)
    );
  }
  return {
    ok: status === "complete",
    status,
    partial: status === "partial",
    retryable: retryableQueries.length > 0,
    results,
    summary: {
      requestedCount: results.length,
      resolvedCount,
      failedCount: unresolvedQueries.length,
      unavailableCount: unavailableQueries.length,
      cacheHitCount,
    },
    unresolvedQueries,
    ...(unavailableQueries.length > 0 ? { unavailableQueries } : {}),
    continuationBatches,
  };
}

function mergeGenerateFormulaCacheResult(
  plan: GenerateFormulaCachePlan,
  result: ToolCallDetailedResult | null,
  cache: Map<string, Record<string, unknown>>,
  attempts: ReadonlyMap<string, number>
): ToolCallDetailedResult {
  const parsed = result ? recordValue(safeParseJson(result.output)) : {};
  const freshResults = Array.isArray(parsed.results) ? parsed.results.map(recordValue) : [];
  const freshByQuery = new Map<string, Record<string, unknown>>();
  for (const entry of freshResults) {
    if (typeof entry.query === "string") {
      freshByQuery.set(entry.query.trim(), entry);
    }
  }
  let cacheHitCount = 0;
  const mergedResults = plan.queries.map((query) => {
    const cached = cache.get(query);
    if (cached) {
      cacheHitCount += 1;
      return { ...cached, query, cacheHit: true };
    }
    if (plan.exhaustedQueries.includes(query)) {
      return {
        query,
        error: "Formula resolution retry budget exhausted after one continuation attempt.",
        retryable: false,
      };
    }
    const fresh = freshByQuery.get(query);
    if (fresh) {
      if (
        typeof fresh.error === "string" &&
        (parsed.retryable === false ||
          (query && (attempts.get(query) ?? 0) >= MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY))
      ) {
        return { ...fresh, retryable: false };
      }
      return fresh;
    }
    return {
      query,
      error:
        typeof parsed.error === "string" ? parsed.error : "TTF returned no result for this query.",
      retryable: !query || (attempts.get(query) ?? 0) < MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY,
    };
  });
  for (const entry of mergedResults) {
    // A definitive "no such data" verdict is cached alongside successes: it is
    // as final as a formula, and caching it keeps a resubmitted query from
    // spending another TTF round trip to learn the same thing.
    const isCacheableOutcome =
      (typeof entry.error !== "string" &&
        (typeof entry.formula === "string" || typeof entry.formula_template === "string")) ||
      entry.dataUnavailable === true;
    if (typeof entry.query === "string" && isCacheableOutcome) {
      const cachedEntry = { ...entry };
      delete cachedEntry.cacheHit;
      cache.delete(entry.query);
      cache.set(entry.query, cachedEntry);
      while (cache.size > MAX_CHECKPOINT_FORMULA_CACHE_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (typeof oldest !== "string") break;
        cache.delete(oldest);
      }
    }
  }
  return {
    ...(result ?? {}),
    output: JSON.stringify(buildGenerateFormulaCacheEnvelope(mergedResults, cacheHitCount)),
  };
}

/**
 * Build a deterministic semantic fingerprint for a guarded workbook write.
 *
 * @param toolName - Provider-visible executor tool name.
 * @param args - Parsed write arguments; synthetic `stepId` is ignored.
 * @returns A compact deterministic fingerprint.
 */
export function stableWriteFingerprint(toolName: string, args: unknown): string {
  const fingerprintArgs =
    args !== null && typeof args === "object" && !Array.isArray(args)
      ? Object.fromEntries(
          Object.entries(args as Record<string, unknown>).filter(([key]) => key !== "stepId")
        )
      : args;
  const canonical = canonicalJson(fingerprintArgs);
  return [
    toolName,
    canonical.length,
    hashCanonicalString(canonical, 0x811c9dc5),
    hashCanonicalString(canonical, 0x9e3779b9),
  ].join(":");
}

function canonicalJson(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(String(value));
}

function hashCanonicalString(value: string, seed: number): string {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Determine whether a host observation confirms an unambiguous write.
 *
 * @param output - Parsed tool observation.
 * @returns Whether no error, failure, or skipped action is present.
 */
export function isConfirmedWriteResult(output: Record<string, unknown>): boolean {
  if (
    output.applied === false ||
    output.error ||
    (Array.isArray(output.failedActions) && output.failedActions.length > 0) ||
    (Array.isArray(output.skippedActions) && output.skippedActions.length > 0)
  ) {
    return false;
  }
  return output.applied === true || output.ok === true;
}

function isResolvedHostAcknowledgement(output: Record<string, unknown>): boolean {
  return (
    output.applied === true &&
    !output.error &&
    output.ambiguousWrite !== true &&
    output.retryReason === undefined &&
    !(Array.isArray(output.failedActions) && output.failedActions.length > 0) &&
    !(Array.isArray(output.skippedActions) && output.skippedActions.length > 0)
  );
}

export function shouldCheckpointBlockedWrite(output: Record<string, unknown>): boolean {
  if (output.code === "range_matrix_shape_mismatch") return false;
  if (output.ambiguousWrite === true) return true;
  if (isResolvedHostAcknowledgement(output)) return false;
  const listed = (key: string): boolean => {
    const value = output[key];
    return Array.isArray(value) && value.length > 0;
  };
  const retryMayFollowDispatch =
    output.retryReason !== undefined && output.hostDispatchAttempted !== false;
  const evidenceOfContact =
    retryMayFollowDispatch || listed("editedCells") || listed("failedActions");
  // A tool that reports it applied nothing and names neither confirmed edits
  // nor attempted failures has no mutation that might have half landed.
  // Declared skips and explicitly identified pre-dispatch host-ready pauses are
  // no-attempts, not evidence of an ambiguous write. A retry without that
  // explicit marker fails closed because a first operation may have landed.
  // Checkpointing it would refuse the identical call on the next run as a
  // possible partial write, which is the one thing a no-op cannot be.
  if (output.applied === false && !evidenceOfContact) return false;
  return output.applied === true || output.ok === true || evidenceOfContact;
}

/**
 * Compare a candidate write with the unresolved write retained by the run.
 *
 * @param blockedOperation - Existing unresolved write, when any.
 * @param toolName - Candidate tool name.
 * @param args - Candidate parsed arguments.
 * @returns Whether both writes have the same semantic fingerprint.
 */
export function blockedOperationExactlyMatches(
  blockedOperation: ExecutorRunCheckpoint["blockedOperation"] | undefined,
  toolName: string,
  args: unknown
): boolean {
  return (
    blockedOperation?.toolName === toolName &&
    stableWriteFingerprint(toolName, blockedOperation.args) ===
      stableWriteFingerprint(toolName, args)
  );
}

/**
 * Whether a pending write would touch the cells whose acknowledgement is still
 * unresolved.
 *
 * Only overlapping writes are dangerous: replaying them could double-apply, or
 * race the read-back that is meant to establish what actually landed. Writes
 * to other sheets or other ranges are independent, and blocking them just
 * serializes the run behind one uncertain acknowledgement. A write whose
 * target cannot be determined is treated as overlapping, since the gate should
 * fail closed. Focus-only activation and bounded formatting/layout calls are
 * exceptions because they cannot double-apply cell content.
 *
 * @param blockedOperation - Unresolved write retained by the run.
 * @param toolName - Candidate write tool name.
 * @param args - Candidate parsed write arguments.
 * @returns Whether ranges overlap or cannot be safely distinguished.
 */
export function writeOverlapsBlockedOperation(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>,
  toolName: string,
  args: unknown
): boolean {
  if (
    ANNOTATION_WRITE_TOOLS.has(blockedOperation.toolName) &&
    !ANNOTATION_WRITE_TOOLS.has(toolName)
  ) {
    // Notes and comments are a separate mutation domain from workbook cells.
    // The exact-call replay guard still blocks the ambiguous annotation, while
    // unrelated values, formulas, formatting, and structural writes may proceed.
    return false;
  }
  const blockedTargets = readbackContentTargetsFromWriteArgs(
    blockedOperation.toolName,
    blockedOperation.args
  );
  const candidateTargets = readbackContentTargetsFromWriteArgs(toolName, args);
  const candidate = recordValue(args);
  if (
    toolName === "activate_sheet" ||
    (toolName === "manage_worksheet" && candidate.operation === "activate")
  ) {
    return false;
  }
  const blockedArgs = recordValue(blockedOperation.args);
  if (
    blockedOperation.toolName === "format_range" ||
    blockedOperation.toolName === "layout_range" ||
    blockedOperation.toolName === "activate_sheet" ||
    (blockedOperation.toolName === "manage_worksheet" &&
      blockedArgs.operation === "activate")
  ) {
    // A lost acknowledgement for formatting, sizing, or focus cannot have
    // partially changed cell content. The exact-call replay guard still blocks
    // repeating that operation, but unrelated content writes remain safe.
    return false;
  }
  if (toolName === "format_range" || toolName === "layout_range") {
    if (blockedWriteIsCoordinateStableAndContentIndependent(blockedOperation)) {
      return false;
    }
    const candidateSheet = normalizeSheetName(candidate.sheetName);
    const blockedSheets = affectedSheetNamesFromWrite(
      blockedOperation.toolName,
      blockedOperation.args,
    );
    if (
      candidateSheet &&
      blockedSheets.length > 0 &&
      !blockedSheets.includes(candidateSheet)
    ) {
      return false;
    }
    const candidateRange =
      typeof candidate.address === "string"
        ? parseA1Range(candidate.address)
        : undefined;
    if (
      candidateSheet &&
      candidateRange &&
      blockedTargets.length > 0 &&
      blockedTargets.every((blocked) => {
        if (normalizeSheetName(blocked.sheetName) !== candidateSheet) return true;
        const blockedRange = parseA1Range(blocked.address);
        return Boolean(blockedRange && !rangesOverlap(blockedRange, candidateRange));
      })
    ) {
      return false;
    }
    return true;
  }
  if (blockedTargets.length === 0 || candidateTargets.length === 0) return true;
  return candidateTargets.some((candidate) => {
    const candidateRange = parseA1Range(candidate.address);
    if (!candidateRange) return true;
    return blockedTargets.some((blocked) => {
      if (normalizeSheetName(blocked.sheetName) !== normalizeSheetName(candidate.sheetName)) {
        return false;
      }
      const blockedRange = parseA1Range(blocked.address);
      return !blockedRange || rangesOverlap(blockedRange, candidateRange);
    });
  });
}

function affectedSheetNamesFromWrite(
  toolName: string,
  rawArgs: unknown,
): string[] {
  const args = recordValue(rawArgs);
  const names = new Set<string>();
  const add = (value: unknown): void => {
    const normalized = normalizeSheetName(value);
    if (normalized) names.add(normalized);
  };
  add(args.sheetName);
  add(args.destinationSheetName);
  if (toolName === "copy_worksheet") add(args.sourceSheetName);
  if (toolName === "manage_worksheet") add(args.name);
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  for (const worksheetValue of Array.isArray(modifications.worksheets)
    ? modifications.worksheets
    : []) {
    add(recordValue(worksheetValue).sheetName);
  }
  for (const sheetValue of Array.isArray(modifications.sheets)
    ? modifications.sheets
    : []) {
    add(recordValue(sheetValue).name);
  }
  return Array.from(names);
}

function blockedWriteIsCoordinateStableAndContentIndependent(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>,
): boolean {
  if (
    blockedOperation.toolName === "format_range" ||
    blockedOperation.toolName === "layout_range" ||
    blockedOperation.toolName === "activate_sheet"
  ) {
    return true;
  }
  if (blockedOperation.toolName === "manage_worksheet") {
    return recordValue(blockedOperation.args).operation === "activate";
  }
  if (blockedOperation.toolName !== "modify_workbook") return false;

  const args = recordValue(blockedOperation.args);
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  let sawMutation = false;
  const safeRangeActions = new Set([
    "format",
    "autofit",
    "resize",
    "hideColumns",
    "showColumns",
    "hideRows",
    "showRows",
  ]);
  for (const worksheetValue of Array.isArray(modifications.worksheets)
    ? modifications.worksheets
    : []) {
    const worksheet = recordValue(worksheetValue);
    if (
      (Array.isArray(worksheet.cells) && worksheet.cells.length > 0) ||
      (Array.isArray(worksheet.tables) && worksheet.tables.length > 0) ||
      (Array.isArray(worksheet.logos) && worksheet.logos.length > 0)
    ) {
      return false;
    }
    for (const rangeValue of Array.isArray(worksheet.ranges) ? worksheet.ranges : []) {
      sawMutation = true;
      if (!safeRangeActions.has(String(recordValue(rangeValue).action ?? ""))) {
        return false;
      }
    }
  }
  for (const sheetValue of Array.isArray(modifications.sheets) ? modifications.sheets : []) {
    sawMutation = true;
    if (recordValue(sheetValue).action !== "activate") return false;
  }
  if (
    Array.isArray(modifications.workbookOperations) &&
    modifications.workbookOperations.length > 0
  ) {
    return false;
  }
  return sawMutation;
}

/**
 * Validate a narrower retry against read-back-proven missing cells.
 *
 * @param blockedOperation - Unresolved write with read-back evidence.
 * @param toolName - Candidate granular write tool.
 * @param args - Candidate parsed write arguments.
 * @returns Remediated cells when safe, otherwise `undefined`.
 */
export function narrowGranularRemediationCells(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>,
  toolName: string,
  args: unknown
): string[] | undefined {
  if (
    blockedOperation.toolName !== toolName ||
    blockedOperation.readbackObserved !== true ||
    !Array.isArray(blockedOperation.unresolvedCells) ||
    blockedOperation.unresolvedCells.length === 0 ||
    (toolName !== "set_range_values" && toolName !== "set_range_formulas")
  ) {
    return undefined;
  }
  const blockedStepId = stringValue(recordValue(blockedOperation.args).stepId);
  const candidateStepId = stringValue(recordValue(args).stepId);
  if ((blockedStepId ?? "") !== (candidateStepId ?? "")) return undefined;
  const blockedTarget = readbackContentTargetsFromWriteArgs(toolName, blockedOperation.args)[0];
  const candidateTarget = readbackContentTargetsFromWriteArgs(toolName, args)[0];
  if (
    !blockedTarget ||
    !candidateTarget ||
    normalizeSheetName(blockedTarget.sheetName) !== normalizeSheetName(candidateTarget.sheetName)
  ) {
    return undefined;
  }
  const unresolved = new Set(blockedOperation.unresolvedCells.map(normalizeLocalAddressKey));
  const expectedByAddress = new Map(
    blockedTarget.cells.map((cell) => [normalizeLocalAddressKey(cell.address), cell])
  );
  const matches = candidateTarget.cells.every((candidate) => {
    const key = normalizeLocalAddressKey(candidate.address);
    const expected = expectedByAddress.get(key);
    return (
      unresolved.has(key) &&
      !!expected &&
      expected.kind === candidate.kind &&
      Object.is(expected.expected, candidate.expected)
    );
  });
  return matches && candidateTarget.cells.length > 0
    ? candidateTarget.cells.map((cell) => cell.address)
    : undefined;
}

function blockedGranularEvidenceFromResult(
  toolName: string,
  args: unknown,
  output: Record<string, unknown>
):
  | Pick<
      NonNullable<ExecutorRunCheckpoint["blockedOperation"]>,
      "readbackObserved" | "unresolvedCells"
    >
  | Record<string, never> {
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return {};
  }
  const target = readbackContentTargetsFromWriteArgs(toolName, args)[0];
  const readback = recordValue(output.recoveryReadback);
  if (!target || !hasDenseReadbackEvidence(target, readback)) return {};
  const evaluation = evaluateExecutorContentReadback(
    target,
    { format: "json", includeFormulas: true },
    readback
  );
  const editedRanges = Array.isArray(output.editedCells)
    ? output.editedCells
        .filter((address): address is string => typeof address === "string")
        .map(parseA1Range)
        .filter((range): range is ParsedA1Range => !!range)
    : [];
  const unresolvedCells = evaluation.missingCells
    .filter((cell) => {
      const position = parseA1Cell(cell.address);
      return (
        !position ||
        !editedRanges.some(
          (range) =>
            position.row >= range.startRow &&
            position.row <= range.endRow &&
            position.column >= range.startColumn &&
            position.column <= range.endColumn
        )
      );
    })
    .map((cell) => cell.address);
  return { readbackObserved: true, unresolvedCells };
}

function evaluateBlockedGranularWriteFromRead(
  blockedOperation: NonNullable<ExecutorRunCheckpoint["blockedOperation"]>,
  readArgs: unknown,
  readOutput: unknown,
  accumulators: ExecutorRunAccumulators
): {
  complete: boolean;
  readbackObserved: boolean;
  unresolvedCells: string[];
  confirmedEditedCells: string[];
} {
  if (
    blockedOperation.toolName !== "set_range_values" &&
    blockedOperation.toolName !== "set_range_formulas"
  ) {
    return {
      complete: false,
      readbackObserved: false,
      unresolvedCells: [],
      confirmedEditedCells: [],
    };
  }
  const target = readbackContentTargetsFromWriteArgs(
    blockedOperation.toolName,
    blockedOperation.args
  )[0];
  const readArgsRecord = recordValue(readArgs);
  const readSheetName = stringValue(readArgsRecord.sheetName);
  const readOutputRecord = recordValue(readOutput);
  const outputSheetName = stringValue(readOutputRecord.sheetName);
  if (
    !target ||
    !readSheetName ||
    normalizeSheetName(readSheetName) !== normalizeSheetName(target.sheetName) ||
    (outputSheetName &&
      normalizeSheetName(outputSheetName) !== normalizeSheetName(target.sheetName)) ||
    !hasDenseReadbackEvidence(target, readOutputRecord)
  ) {
    return {
      complete: false,
      readbackObserved: false,
      unresolvedCells: [],
      confirmedEditedCells: [],
    };
  }
  const evaluation = evaluateExecutorContentReadback(target, readArgs, readOutput);
  const missing = new Set(
    evaluation.missingCells.map((cell) => `${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedCells = target.cells.filter(
    (cell) => !missing.has(`${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedEditedCells = recordReadbackConfirmedGranularWrite(
    accumulators,
    blockedOperation.toolName,
    recordValue(blockedOperation.args),
    target,
    confirmedCells
  );
  return {
    complete: evaluation.complete,
    readbackObserved: true,
    unresolvedCells: evaluation.missingCells.map((cell) => cell.address),
    confirmedEditedCells,
  };
}

function updatePendingShapeCorrections(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): void {
  const output = recordValue(parsedOutput);
  const args = recordValue(parsedArgs);
  const stepId = typeof args.stepId === "string" ? args.stepId : null;
  if (output.code === "range_matrix_shape_mismatch") {
    const targetAddress =
      stringValue(output.targetAddress) ?? stringValue(args.address) ?? "(unknown range)";
    const matchingTarget = readbackContentTargetsFromWriteArgs(toolName, parsedArgs).find(
      (target) =>
        normalizeLocalAddressKey(target.address) === normalizeLocalAddressKey(targetAddress)
    );
    const sheetName = matchingTarget?.sheetName ?? stringValue(args.sheetName);
    const matrixField =
      output.matrixField === "values" || output.matrixField === "formulas"
        ? output.matrixField
        : undefined;
    const existing = state.pendingShapeCorrections.find(
      (correction) =>
        correction.toolName === toolName &&
        correction.stepId === stepId &&
        normalizeSheetName(correction.sheetName) === normalizeSheetName(sheetName) &&
        normalizeLocalAddressKey(correction.address) === normalizeLocalAddressKey(targetAddress) &&
        correction.matrixField === matrixField
    );
    if (!existing) {
      state.pendingShapeCorrections.push({
        toolName,
        stepId,
        ...(sheetName ? { sheetName } : {}),
        address: targetAddress,
        ...(matrixField ? { matrixField } : {}),
        attempted: false,
      });
    }
    return;
  }
  if (output.applied === true) {
    const resolved = findMatchingShapeCorrection(
      state.pendingShapeCorrections,
      toolName,
      stepId,
      parsedArgs
    );
    if (resolved) {
      state.pendingShapeCorrections = state.pendingShapeCorrections.filter(
        (correction) => correction !== resolved
      );
    }
  }
}

function readbackContentTargetsFromWriteCompletion(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): ExecutorContentReadbackTarget[] {
  if (
    toolName !== "set_range_values" &&
    toolName !== "set_range_formulas" &&
    toolName !== "modify_workbook"
  ) {
    return [];
  }
  const output = recordValue(parsedOutput);
  const editedCells = Array.isArray(output.editedCells)
    ? output.editedCells.filter(
        (address): address is string => typeof address === "string" && address.trim().length > 0
      )
    : [];
  if (output.applied !== true && editedCells.length === 0) return [];
  const targets = readbackContentTargetsFromWriteArgs(toolName, parsedArgs);
  if (output.applied === true) return targets;
  const partialTargets: ExecutorContentReadbackTarget[] = [];
  const rangedTargets = targets
    .map((target) => ({ target, range: parseA1Range(target.address) }))
    .filter(
      (
        entry
      ): entry is {
        target: ExecutorContentReadbackTarget;
        range: ParsedA1Range;
      } => !!entry.range
    );
  for (const editedCell of editedCells) {
    const editedRange = parseA1Range(editedCell);
    if (!editedRange) continue;
    const matches = rangedTargets.filter(
      ({ range }) =>
        range.startRow <= editedRange.endRow &&
        range.endRow >= editedRange.startRow &&
        range.startColumn <= editedRange.endColumn &&
        range.endColumn >= editedRange.startColumn
    );
    // Host evidence is currently local-address only. When the same local range
    // occurs in more than one requested target (for example SheetA!A1 and
    // SheetB!A1), assigning it to either target would be a false confirmation.
    if (matches.length !== 1) continue;
    const { target, range: targetRange } = matches[0];
    const startRow = Math.max(targetRange.startRow, editedRange.startRow);
    const endRow = Math.min(targetRange.endRow, editedRange.endRow);
    const startColumn = Math.max(targetRange.startColumn, editedRange.startColumn);
    const endColumn = Math.min(targetRange.endColumn, editedRange.endColumn);
    if (startRow > endRow || startColumn > endColumn) continue;
    const cells = target.cells.filter((cell) => {
      const position = parseA1Cell(cell.address);
      return (
        !!position &&
        position.row >= startRow &&
        position.row <= endRow &&
        position.column >= startColumn &&
        position.column <= endColumn
      );
    });
    if (cells.length === 0) continue;
    const startAddress = formatA1Cell(startRow, startColumn);
    const endAddress = formatA1Cell(endRow, endColumn);
    partialTargets.push({
      sheetName: target.sheetName,
      address: startAddress === endAddress ? startAddress : `${startAddress}:${endAddress}`,
      rowCount: endRow - startRow + 1,
      columnCount: endColumn - startColumn + 1,
      cells,
    });
  }
  return partialTargets;
}

function readbackContentTargetsFromWriteArgs(
  toolName: string,
  parsedArgs: unknown
): ExecutorContentReadbackTarget[] {
  const args = recordValue(parsedArgs);
  const targets: ExecutorContentReadbackTarget[] = [];
  if (toolName === "set_range_values") {
    addMatrixContentReadbackTarget(targets, args.sheetName, args.address, args.values, "values");
    return targets;
  }
  if (toolName === "set_range_formulas") {
    addMatrixContentReadbackTarget(
      targets,
      args.sheetName,
      args.address,
      args.formulas,
      "formulas"
    );
    return targets;
  }

  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const sheetName = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    const cells = Array.isArray(worksheet.cells) ? worksheet.cells : [];
    for (const cellValue of cells) {
      const cell = recordValue(cellValue);
      if (cell.action !== "set") continue;
      if (cell.formula !== undefined) {
        addScalarContentReadbackTarget(
          targets,
          sheetName,
          cell.address,
          cell.formula,
          cell.formula === null ? "blank" : "formula"
        );
      } else if (cell.value !== undefined) {
        addScalarContentReadbackTarget(
          targets,
          sheetName,
          cell.address,
          cell.value,
          cell.value === null
            ? "blank"
            : typeof cell.value === "string" && cell.value.startsWith("=")
              ? "formula"
              : "value"
        );
      }
    }
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    for (const rangeValue of ranges) {
      const range = recordValue(rangeValue);
      if (range.action !== "fill") continue;
      if (range.formulas !== undefined) {
        addMatrixContentReadbackTarget(
          targets,
          sheetName,
          range.address,
          range.formulas,
          "formulas"
        );
      } else if (range.values !== undefined) {
        addMatrixContentReadbackTarget(targets, sheetName, range.address, range.values, "values");
      }
    }
  }
  return targets;
}

function addScalarContentReadbackTarget(
  targets: ExecutorContentReadbackTarget[],
  sheetNameValue: unknown,
  addressValue: unknown,
  value: unknown,
  kind: "formula" | "value" | "blank"
): void {
  const sheetName = normalizeSheetName(sheetNameValue);
  const address = stringValue(addressValue);
  const range = address ? parseA1Range(address) : undefined;
  if (
    !sheetName ||
    !address ||
    !range ||
    value === undefined ||
    (kind === "blank" && value !== null) ||
    (kind === "formula" && typeof value !== "string") ||
    (kind === "value" &&
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean")
  ) {
    return;
  }
  const cells: ExecutorExpectedContentCell[] = [];
  for (let row = range.startRow; row <= range.endRow; row += 1) {
    for (let column = range.startColumn; column <= range.endColumn; column += 1) {
      cells.push({
        address: formatA1Cell(row, column),
        kind,
        expected: value,
      } as ExecutorExpectedContentCell);
    }
  }
  targets.push({
    sheetName,
    address,
    rowCount: range.endRow - range.startRow + 1,
    columnCount: range.endColumn - range.startColumn + 1,
    cells,
  });
}

function addMatrixContentReadbackTarget(
  targets: ExecutorContentReadbackTarget[],
  sheetNameValue: unknown,
  addressValue: unknown,
  matrixValue: unknown,
  field: "values" | "formulas"
): void {
  const sheetName = normalizeSheetName(sheetNameValue);
  const address = stringValue(addressValue);
  const range = address ? parseA1Range(address) : undefined;
  if (!sheetName || !address || !range || !Array.isArray(matrixValue)) return;
  const cells: ExecutorExpectedContentCell[] = [];
  for (let rowOffset = 0; rowOffset < matrixValue.length; rowOffset += 1) {
    const row = matrixValue[rowOffset];
    if (!Array.isArray(row)) continue;
    for (let columnOffset = 0; columnOffset < row.length; columnOffset += 1) {
      const value = row[columnOffset];
      if (
        value === undefined ||
        (value !== null &&
          typeof value !== "string" &&
          typeof value !== "number" &&
          typeof value !== "boolean")
      ) {
        continue;
      }
      const kind =
        value === null
          ? "blank"
          : field === "formulas" || (typeof value === "string" && value.startsWith("="))
            ? "formula"
            : "value";
      if (kind === "formula" && typeof value !== "string") continue;
      cells.push({
        address: formatA1Cell(range.startRow + rowOffset, range.startColumn + columnOffset),
        kind,
        expected: value,
      });
    }
  }
  if (cells.length === 0) return;
  targets.push({
    sheetName,
    address,
    rowCount: range.endRow - range.startRow + 1,
    columnCount: range.endColumn - range.startColumn + 1,
    cells,
  });
}

/** Drop every outstanding content expectation and the failures they produced. */
function clearContentReadbackVerification(state: ExecutorVerificationState): void {
  state.pendingContentReadbackTargets = [];
  state.verifiedContentReadbackTargetKeys = [];
  state.contentReadbackFailures = [];
  const hasDurableVerificationDebt =
    state.refreshRequired ||
    state.recalcRequired ||
    state.hardErrors.length > 0 ||
    state.pendingShapeCorrections.length > 0 ||
    !allPendingReadbackSheetsVerified(state) ||
    !allPendingReadbackTargetsVerified(state) ||
    ((state.refreshObserved || state.recalcObserved) &&
      !state.cleanReadbackAfterRecalc);
  if (!hasDurableVerificationDebt) {
    state.requiresVerification = false;
  }
}

/**
 * Decide whether a write needs its own confirming read-back.
 *
 * A cleanly applied static write is already acknowledged by the host, so
 * demanding a full-shape read of every such write costs a host round trip and
 * an extra model iteration for no new evidence. Reserve the read for writes
 * whose outcome is genuinely uncertain: an ambiguous or partial host result, or
 * FDS-bound formulas, which only produce their real content after the trailing
 * refresh and recalculate pass.
 */
function requiresContentReadbackVerification(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): boolean {
  const output = recordValue(parsedOutput);
  const hostResultUncertain =
    output.applied !== true ||
    output.ambiguousWrite === true ||
    output.retryReason !== undefined ||
    (Array.isArray(output.failedActions) && output.failedActions.length > 0) ||
    (Array.isArray(output.skippedActions) && output.skippedActions.length > 0);
  if (hostResultUncertain) return true;
  return readbackContentTargetsFromWriteArgs(toolName, parsedArgs).some((target) =>
    target.cells.some((cell) => cell.kind === "formula" && isFdsBoundFormula(cell.expected))
  );
}

/**
 * Retire expectations that a newer confirmed write has overwritten.
 *
 * Pending targets are keyed by `sheet!address`, so a later narrower write does
 * not replace a wider earlier target. Without this, filling `B2:K31` and then
 * writing formulas into `E6:K30` leaves the `B2:K31` target expecting the
 * original content at 175 coordinates that no longer hold it — an expectation
 * no read-back can ever satisfy, which strands the run in "needs attention"
 * and makes every continuation replay the same dead end.
 */
function supersedeContentReadbackCells(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown,
  confirmedTargets: readonly ExecutorContentReadbackTarget[]
): void {
  if (state.pendingContentReadbackTargets.length === 0) return;
  const supersededBySheet = new Map<string, Set<string>>();
  const addSuperseded = (sheetName: string, address: string): void => {
    const sheetKey = normalizeSheetName(sheetName) ?? sheetName;
    const addresses = supersededBySheet.get(sheetKey) ?? new Set<string>();
    addresses.add(normalizeLocalAddressKey(address));
    supersededBySheet.set(sheetKey, addresses);
  };
  for (const target of confirmedTargets) {
    for (const cell of target.cells) addSuperseded(target.sheetName, cell.address);
  }
  for (const cleared of clearedRangesFromWriteCompletion(toolName, parsedArgs, parsedOutput)) {
    const range = parseA1Range(cleared.address);
    if (!range) continue;
    for (let row = range.startRow; row <= range.endRow; row += 1) {
      for (let column = range.startColumn; column <= range.endColumn; column += 1) {
        addSuperseded(cleared.sheetName, formatA1Cell(row, column));
      }
    }
  }
  if (supersededBySheet.size === 0) return;

  // Targets the incoming write replaces outright are handled by the merge that
  // follows; only older, differently-addressed targets need trimming here.
  const incomingKeys = new Set(confirmedTargets.map(contentReadbackTargetKey));
  const trimmedKeys = new Set<string>();
  const survivors: ExecutorContentReadbackTarget[] = [];
  for (const target of state.pendingContentReadbackTargets) {
    const key = contentReadbackTargetKey(target);
    const superseded = supersededBySheet.get(
      normalizeSheetName(target.sheetName) ?? target.sheetName
    );
    if (incomingKeys.has(key) || !superseded) {
      survivors.push(target);
      continue;
    }
    const cells = target.cells.filter(
      (cell) => !superseded.has(normalizeLocalAddressKey(cell.address))
    );
    if (cells.length === target.cells.length) {
      survivors.push(target);
      continue;
    }
    trimmedKeys.add(key);
    // The address stays as written so the shape check keeps asking for the
    // same rectangle; only the per-coordinate expectations shrink.
    if (cells.length > 0) survivors.push({ ...target, cells });
  }
  if (trimmedKeys.size === 0) return;
  state.pendingContentReadbackTargets = survivors;
  const survivingKeys = new Set(survivors.map(contentReadbackTargetKey));
  state.verifiedContentReadbackTargetKeys = state.verifiedContentReadbackTargetKeys.filter((key) =>
    survivingKeys.has(key)
  );
  // A recorded failure names coordinates that may no longer be expected, so
  // drop it and let the next read-back produce a current reason.
  state.contentReadbackFailures = state.contentReadbackFailures.filter(
    (failure) => survivingKeys.has(failure.key) && !trimmedKeys.has(failure.key)
  );
}

/** Confirmed `clear` range actions, which also retire earlier expectations. */
function clearedRangesFromWriteCompletion(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): Array<{ sheetName: string; address: string }> {
  if (recordValue(parsedOutput).applied !== true) return [];
  const args = recordValue(parsedArgs);
  if (toolName === "clear_range") {
    const clearType = stringValue(args.clearType);
    const sheetName = stringValue(args.sheetName);
    const address = stringValue(args.address);
    return (clearType === "Contents" || clearType === "All") && sheetName && address
      ? [{ sheetName, address }]
      : [];
  }
  if (toolName !== "modify_workbook") return [];
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  const cleared: Array<{ sheetName: string; address: string }> = [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const sheetName = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    if (!sheetName) continue;
    for (const rangeValue of Array.isArray(worksheet.ranges) ? worksheet.ranges : []) {
      const range = recordValue(rangeValue);
      const address = stringValue(range.address);
      const clearType = stringValue(recordValue(range.clear).clearType) ?? "All";
      if (
        range.action === "clear" &&
        (clearType === "Contents" || clearType === "All") &&
        address
      ) {
        cleared.push({ sheetName, address });
      }
    }
  }
  return cleared;
}

function mergePendingContentReadbackTargets(
  state: ExecutorVerificationState,
  targets: readonly ExecutorContentReadbackTarget[]
): void {
  const merged = new Map(
    state.pendingContentReadbackTargets.map((target) => [contentReadbackTargetKey(target), target])
  );
  const replacedKeys = new Set<string>();
  for (const target of targets) {
    const key = contentReadbackTargetKey(target);
    merged.set(key, target);
    replacedKeys.add(key);
  }
  state.pendingContentReadbackTargets = Array.from(merged.values());
  state.verifiedContentReadbackTargetKeys = state.verifiedContentReadbackTargetKeys.filter(
    (key) => !replacedKeys.has(key) && merged.has(key)
  );
  state.contentReadbackFailures = state.contentReadbackFailures.filter(
    (failure) => !replacedKeys.has(failure.key) && merged.has(failure.key)
  );
}

function updateContentReadbackVerification(
  state: ExecutorVerificationState,
  parsedArgs: unknown,
  parsedOutput: unknown
): void {
  if (state.pendingContentReadbackTargets.length === 0) return;
  const args = recordValue(parsedArgs);
  const readSheet = normalizeSheetName(
    stringValue(args.sheetName) ?? stringValue(recordValue(parsedOutput).sheetName)
  );
  const requestedAddress = stringValue(args.address);
  const requestedRanges = requestedAddress ? parseA1Ranges(requestedAddress) : [];
  if (!readSheet) return;

  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  const failureByKey = new Map(
    state.contentReadbackFailures.map((failure) => [failure.key, failure.reason])
  );
  for (const target of state.pendingContentReadbackTargets) {
    if (target.sheetName !== readSheet) continue;
    const targetRange = parseA1Range(target.address);
    if (
      !targetRange ||
      (requestedRanges.length > 0 &&
        !requestedRanges.some((range) => rangesOverlap(range, targetRange)))
    ) {
      continue;
    }
    const evaluation = evaluateExecutorContentReadback(target, parsedArgs, parsedOutput);
    const key = contentReadbackTargetKey(target);
    if (evaluation.complete) {
      verified.add(key);
      failureByKey.delete(key);
    } else {
      verified.delete(key);
      failureByKey.set(
        key,
        evaluation.reason ?? `${target.sheetName}!${target.address} did not match`
      );
    }
  }
  state.verifiedContentReadbackTargetKeys = Array.from(verified);
  state.contentReadbackFailures = Array.from(failureByKey.entries()).map(([key, reason]) => ({
    key,
    reason,
  }));
  if (!allPendingContentReadbackTargetsVerified(state)) {
    state.requiresVerification = true;
  }
}

function allPendingContentReadbackTargetsVerified(state: ExecutorVerificationState): boolean {
  if (state.pendingContentReadbackTargets.length === 0) return true;
  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  return state.pendingContentReadbackTargets.every((target) =>
    verified.has(contentReadbackTargetKey(target))
  );
}

function unverifiedContentReadbackTargets(
  state: ExecutorVerificationState
): ExecutorContentReadbackTarget[] {
  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  return state.pendingContentReadbackTargets.filter(
    (target) => !verified.has(contentReadbackTargetKey(target))
  );
}

function contentReadbackTargetKey(target: ExecutorContentReadbackTarget): string {
  return `${target.sheetName}!${normalizeLocalAddressKey(target.address)}`;
}

function normalizeLocalAddressKey(address: string): string {
  const local = address.includes("!") ? address.slice(address.lastIndexOf("!") + 1) : address;
  return local.replace(/\$/g, "").trim().toUpperCase();
}

function formatA1Cell(row: number, column: number): string {
  let remaining = column + 1;
  let letters = "";
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return `${letters}${row + 1}`;
}

function mergePendingReadbackSheets(
  state: ExecutorVerificationState,
  sheetNames: readonly string[]
): void {
  const merged = new Set(state.pendingReadbackSheetNames);
  for (const sheetName of sheetNames) {
    const normalized = normalizeSheetName(sheetName);
    if (normalized) merged.add(normalized);
  }
  state.pendingReadbackSheetNames = Array.from(merged);
  state.verifiedReadbackSheetNames = state.verifiedReadbackSheetNames.filter((sheetName) =>
    merged.has(sheetName)
  );
}

function mergePendingReadbackTargets(
  state: ExecutorVerificationState,
  targets: readonly ExecutorReadbackTarget[]
): void {
  const merged = new Map<string, ExecutorReadbackTarget>();
  for (const target of state.pendingReadbackTargets) {
    merged.set(readbackTargetKey(target), target);
  }
  for (const target of targets) {
    const normalized = normalizeReadbackTarget(target);
    if (normalized) merged.set(readbackTargetKey(normalized), normalized);
  }
  state.pendingReadbackTargets = Array.from(merged.values());
  const pendingKeys = new Set(state.pendingReadbackTargets.map(readbackTargetKey));
  state.verifiedReadbackTargets = state.verifiedReadbackTargets.filter((target) =>
    pendingKeys.has(readbackTargetKey(target))
  );
}

function markReadbackSheetVerified(
  state: ExecutorVerificationState,
  sheetName: string | undefined
): boolean {
  if (state.pendingReadbackSheetNames.length === 0) return state.requiresVerification;
  const normalized = normalizeSheetName(sheetName);
  if (!normalized || !state.pendingReadbackSheetNames.includes(normalized)) return false;
  if (!state.verifiedReadbackSheetNames.includes(normalized)) {
    state.verifiedReadbackSheetNames.push(normalized);
  }
  return true;
}

function markReadbackTargetVerified(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  readAddress: string | undefined
): boolean {
  if (state.pendingReadbackTargets.length === 0) return state.requiresVerification;
  const normalizedSheetName = normalizeSheetName(sheetName);
  if (!normalizedSheetName || !readAddress) return false;
  const readRanges = parseA1Ranges(readAddress);
  if (readRanges.length === 0) return false;
  const matchedTargets = state.pendingReadbackTargets.filter((target) => {
    if (target.sheetName !== normalizedSheetName) return false;
    const targetRange = parseA1Range(target.address);
    return Boolean(
      targetRange && readRanges.some((readRange) => rangeContains(readRange, targetRange))
    );
  });
  if (matchedTargets.length === 0) return false;

  const verified = new Map<string, ExecutorReadbackTarget>();
  for (const target of state.verifiedReadbackTargets) {
    verified.set(readbackTargetKey(target), target);
  }
  for (const target of matchedTargets) {
    verified.set(readbackTargetKey(target), target);
  }
  state.verifiedReadbackTargets = Array.from(verified.values());
  return true;
}

function isReadbackSheetRelevant(
  state: ExecutorVerificationState,
  sheetName: string | undefined
): boolean {
  if (!state.requiresVerification) return false;
  if (state.pendingReadbackSheetNames.length === 0) return true;
  const normalized = normalizeSheetName(sheetName);
  return Boolean(normalized && state.pendingReadbackSheetNames.includes(normalized));
}

function allPendingReadbackSheetsVerified(state: ExecutorVerificationState): boolean {
  return state.pendingReadbackSheetNames.every((sheetName) =>
    state.verifiedReadbackSheetNames.includes(sheetName)
  );
}

function allPendingReadbackTargetsVerified(state: ExecutorVerificationState): boolean {
  if (state.pendingReadbackTargets.length === 0) return true;
  const verified = new Set(state.verifiedReadbackTargets.map(readbackTargetKey));
  return state.pendingReadbackTargets.every((target) => verified.has(readbackTargetKey(target)));
}

function sheetNameFromReadRange(parsedArgs: unknown, parsedOutput: unknown): string | undefined {
  const argsSheet =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as { sheetName?: unknown }).sheetName
      : undefined;
  if (typeof argsSheet === "string" && argsSheet.trim()) return argsSheet;
  const outputSheet =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { sheetName?: unknown }).sheetName
      : undefined;
  return typeof outputSheet === "string" && outputSheet.trim() ? outputSheet : undefined;
}

function readRangeAddress(parsedArgs: unknown, parsedOutput: unknown): string | undefined {
  const outputAddress =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { address?: unknown }).address
      : undefined;
  if (typeof outputAddress === "string" && outputAddress.trim()) return outputAddress;
  const argsAddress =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as { address?: unknown }).address
      : undefined;
  return typeof argsAddress === "string" && argsAddress.trim() ? argsAddress : undefined;
}

function readRangeReturnedData(parsedOutput: unknown): boolean {
  const output =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { values?: unknown; formulas?: unknown; csv?: unknown })
      : {};
  if (Array.isArray(output.values)) {
    if (
      output.values.some(
        (row) =>
          Array.isArray(row) &&
          row.some(
            (cell) =>
              cell !== null &&
              cell !== undefined &&
              (typeof cell !== "string" || cell.trim() !== "")
          )
      )
    ) {
      return true;
    }
    return (
      Array.isArray(output.formulas) &&
      output.formulas.some(
        (row) => Array.isArray(row) && row.some((formula) => isFdsBoundFormula(formula))
      )
    );
  }
  return typeof output.csv === "string" && output.csv.trim().length > 0;
}

function classifyRelevantReadbackHardErrors(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  readAddress: string | undefined,
  parsedOutput: unknown
): ExcelHardErrorCell[] {
  const output =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as {
          values?: unknown;
          formulas?: unknown;
          sheetName?: unknown;
          address?: unknown;
          startRow?: unknown;
          startColumn?: unknown;
        })
      : {};

  const formulaErrors =
    Array.isArray(output.values) && Array.isArray(output.formulas)
      ? classifyHardErrorsInFormulaValues(output.values, output.formulas, {
          sheetName: sheetNameFromReadRange({ sheetName }, parsedOutput),
          address: readAddress ?? readRangeAddress({}, parsedOutput),
          startRow: numberValue(output.startRow),
          startColumn: numberValue(output.startColumn),
        }).errors
      : [];

  const pendingTargetErrors = classifyHardErrorsInReadRangeResult({
    ...output,
    sheetName: sheetNameFromReadRange({ sheetName }, parsedOutput),
    address: readAddress ?? readRangeAddress({}, parsedOutput),
  }).errors.filter((error) => isHardErrorInPendingReadbackTarget(state, sheetName, error));

  return mergeHardErrors(formulaErrors, pendingTargetErrors);
}

function recordUnavailableFdsTargetsAfterCorrection(
  state: ExecutorVerificationState,
  accumulators: ExecutorRunAccumulators,
  errors: readonly ExcelHardErrorCell[]
): void {
  if (!state.refreshObserved || !state.recalcObserved) return;
  const attempts = accumulators.fdsFormulaWriteAttempts ?? {};
  const protectedTargets = new Set(
    (accumulators.fdsFormulaWriteTargets ?? []).map(
      (target) =>
        `${target.sheetName.trim().toLowerCase()}!${normalizeLocalAddressKey(target.address)}`
    )
  );
  const unavailable = (accumulators.unavailableFdsFormulaTargets ??= []);
  const unavailableKeys = new Set(
    unavailable.map(
      (target) =>
        `${target.sheetName.trim().toLowerCase()}!${normalizeLocalAddressKey(target.address)}`
    )
  );
  for (const error of errors) {
    const sheetName = error.sheetName.trim();
    const address = normalizeLocalAddressKey(error.address);
    const key = `${sheetName.toLowerCase()}!${address}`;
    if (
      !sheetName ||
      !address ||
      !protectedTargets.has(key) ||
      (attempts[key] ?? 0) < 2 ||
      unavailableKeys.has(key)
    ) {
      continue;
    }
    unavailableKeys.add(key);
    unavailable.push({ sheetName, address });
  }
}

function isHardErrorInPendingReadbackTarget(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  error: ExcelHardErrorCell
): boolean {
  if (state.pendingReadbackTargets.length === 0) return false;
  const normalizedSheetName = normalizeSheetName(sheetName) ?? normalizeSheetName(error.sheetName);
  const errorTarget = readbackTargetFromQualifiedAddress(error.address) ?? {
    sheetName: normalizeSheetName(error.sheetName) ?? "",
    address: error.address,
  };
  if (!normalizedSheetName || errorTarget.sheetName !== normalizedSheetName) return false;
  const errorRange = parseA1Range(errorTarget.address);
  if (!errorRange) return false;
  return state.pendingReadbackTargets.some((target) => {
    if (target.sheetName !== normalizedSheetName) return false;
    const targetRange = parseA1Range(target.address);
    return Boolean(targetRange && rangesOverlap(errorRange, targetRange));
  });
}

function hardErrorsNotCoveredByCleanRead(
  hardErrors: readonly ExcelHardErrorCell[],
  sheetName: string | undefined,
  readAddress: string | undefined
): ExcelHardErrorCell[] {
  if (hardErrors.length === 0) return [];
  const normalizedSheetName = normalizeSheetName(sheetName);
  const readRanges = readAddress ? parseA1Ranges(readAddress) : [];
  if (!normalizedSheetName || readRanges.length === 0) return Array.from(hardErrors);

  return hardErrors.filter((error) => {
    const target = readbackTargetFromQualifiedAddress(error.address) ?? {
      sheetName: normalizeSheetName(error.sheetName) ?? "",
      address: error.address,
    };
    if (target.sheetName !== normalizedSheetName) return true;
    const errorRange = parseA1Range(target.address);
    return !(errorRange && readRanges.some((readRange) => rangesOverlap(errorRange, readRange)));
  });
}

function mergeHardErrors(
  existingErrors: readonly ExcelHardErrorCell[],
  newErrors: readonly ExcelHardErrorCell[]
): ExcelHardErrorCell[] {
  const merged = new Map<string, ExcelHardErrorCell>();
  for (const error of existingErrors) {
    merged.set(hardErrorKey(error), error);
  }
  for (const error of newErrors) {
    merged.set(hardErrorKey(error), error);
  }
  return Array.from(merged.values());
}

function hardErrorKey(error: ExcelHardErrorCell): string {
  return `${normalizeSheetName(error.sheetName) ?? ""}!${error.address.toUpperCase()}!${error.token}`;
}

function readbackSheetsFromRefreshResult(
  parsedArgs: unknown,
  parsedOutput: unknown,
  accumulators: ExecutorRunAccumulators,
  state: ExecutorVerificationState
): string[] {
  const result =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as {
          scopeConfirmed?: unknown;
          refreshedSheetNames?: unknown;
        })
      : {};
  if (result.scopeConfirmed === false) return [];
  if (Array.isArray(result.refreshedSheetNames)) {
    const confirmedSheets = result.refreshedSheetNames.filter(
      (sheetName): sheetName is string =>
        typeof sheetName === "string" && sheetName.trim().length > 0
    );
    const confirmedKeys = new Set(
      confirmedSheets.map((sheetName) => sheetName.trim().toLowerCase())
    );
    const pendingConfirmed = state.pendingReadbackSheetNames.filter((sheetName) =>
      confirmedKeys.has(sheetName.trim().toLowerCase())
    );
    if (pendingConfirmed.length > 0) return pendingConfirmed;
    const requestedSheets = recordValue(parsedArgs).sheetNames;
    if (Array.isArray(requestedSheets)) {
      return requestedSheets.filter(
        (sheetName): sheetName is string =>
          typeof sheetName === "string" && confirmedKeys.has(sheetName.trim().toLowerCase())
      );
    }
    return [];
  }
  const args =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as {
          scope?: unknown;
          sheetNames?: unknown;
          refreshAll?: unknown;
        })
      : {};
  if (Array.isArray(args.sheetNames)) {
    const sheets = args.sheetNames.filter(
      (sheetName): sheetName is string =>
        typeof sheetName === "string" && sheetName.trim().length > 0
    );
    if (sheets.length > 0) return sheets;
  }
  if (args.scope === "workbook" || args.refreshAll === true) {
    const fdsSheets = Array.from(accumulators.fdsRefreshSheets);
    return fdsSheets.length > 0 ? fdsSheets : state.pendingReadbackSheetNames;
  }
  return [];
}

function readbackSheetsFromWriteArgs(
  parsedArgs: unknown,
  accumulators: ExecutorRunAccumulators
): string[] {
  const sheets = new Set<string>();
  const args =
    parsedArgs && typeof parsedArgs === "object" ? (parsedArgs as Record<string, unknown>) : {};
  addReadbackSheet(sheets, args.sheetName);
  addReadbackSheet(sheets, args.targetSheet);

  const modifications =
    args.modifications && typeof args.modifications === "object"
      ? (args.modifications as { worksheets?: unknown })
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheet of worksheets) {
    if (worksheet && typeof worksheet === "object") {
      addReadbackSheet(sheets, (worksheet as { sheetName?: unknown }).sheetName);
    }
  }

  for (const editedCell of accumulators.editedCells) {
    addReadbackSheet(sheets, sheetNameFromQualifiedAddress(editedCell));
  }

  return Array.from(sheets);
}

function readbackFormulaTargetsFromWriteArgs(parsedArgs: unknown): ExecutorReadbackTarget[] {
  const targets: ExecutorReadbackTarget[] = [];
  const args =
    parsedArgs && typeof parsedArgs === "object" ? (parsedArgs as Record<string, unknown>) : {};
  const topLevelSheetName = stringValue(args.sheetName) || stringValue(args.targetSheet);
  if (isFdsBoundFormula(args.formula) || formulaMatrixHasFdsBoundFormula(args.formulas)) {
    addReadbackTarget(targets, topLevelSheetName, args.address);
  }

  const modifications =
    args.modifications && typeof args.modifications === "object"
      ? (args.modifications as { worksheets?: unknown })
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheet of worksheets) {
    if (!worksheet || typeof worksheet !== "object") continue;
    const sheetName = stringValue((worksheet as { sheetName?: unknown }).sheetName);
    const cells = (worksheet as { cells?: unknown }).cells;
    if (Array.isArray(cells)) {
      for (const cell of cells) {
        if (cell && typeof cell === "object") {
          const cellRecord = cell as { action?: unknown; address?: unknown; formula?: unknown };
          if (cellRecord.action === "set" && isFdsBoundFormula(cellRecord.formula)) {
            addReadbackTarget(targets, sheetName, cellRecord.address);
          }
        }
      }
    }
    const ranges = (worksheet as { ranges?: unknown }).ranges;
    if (Array.isArray(ranges)) {
      for (const range of ranges) {
        if (range && typeof range === "object") {
          const rangeRecord = range as {
            action?: unknown;
            address?: unknown;
            formulas?: unknown;
          };
          if (
            rangeRecord.action === "fill" &&
            formulaMatrixHasFdsBoundFormula(rangeRecord.formulas)
          ) {
            addReadbackTarget(targets, sheetName, rangeRecord.address);
          }
        }
      }
    }
  }

  return targets;
}

function addReadbackSheet(sheets: Set<string>, value: unknown): void {
  const normalized = normalizeSheetName(value);
  if (normalized) sheets.add(normalized);
}

function addReadbackTarget(
  targets: ExecutorReadbackTarget[],
  sheetName: unknown,
  address: unknown
): void {
  const normalizedSheetName = normalizeSheetName(sheetName);
  if (normalizedSheetName && typeof address === "string" && address.trim()) {
    targets.push({ sheetName: normalizedSheetName, address: address.trim() });
  }
}

function formulaMatrixHasFdsBoundFormula(formulas: unknown): boolean {
  return (
    Array.isArray(formulas) &&
    formulas.some((row) => Array.isArray(row) && row.some((formula) => isFdsBoundFormula(formula)))
  );
}

function sheetNameFromQualifiedAddress(address: string): string | undefined {
  const bang = address.lastIndexOf("!");
  if (bang <= 0) return undefined;
  return address.slice(0, bang).replace(/^'|'$/g, "");
}

function readbackTargetFromQualifiedAddress(address: string): ExecutorReadbackTarget | undefined {
  const bang = address.lastIndexOf("!");
  if (bang <= 0) return undefined;
  const sheetName = normalizeSheetName(address.slice(0, bang).replace(/^'|'$/g, ""));
  const localAddress = address.slice(bang + 1).trim();
  return sheetName && localAddress ? { sheetName, address: localAddress } : undefined;
}

function normalizeSheetName(sheetName: unknown): string | undefined {
  return typeof sheetName === "string" && sheetName.trim()
    ? sheetName.trim().toLowerCase()
    : undefined;
}

function normalizeReadbackTarget(
  target: ExecutorReadbackTarget
): ExecutorReadbackTarget | undefined {
  const sheetName = normalizeSheetName(target.sheetName);
  const address = typeof target.address === "string" ? target.address.trim() : "";
  return sheetName && address ? { sheetName, address } : undefined;
}

function readbackTargetKey(target: ExecutorReadbackTarget): string {
  return `${target.sheetName}!${target.address.toUpperCase()}`;
}

interface ParsedA1Range {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

const EXCEL_MAX_ROW_INDEX = 1_048_575;
const EXCEL_MAX_COLUMN_INDEX = 16_383;

function parseA1Range(address: string): ParsedA1Range | undefined {
  const localAddress = address.includes("!")
    ? address.slice(address.lastIndexOf("!") + 1)
    : address;
  const [startRaw, endRaw = startRaw] = localAddress.split(":");
  const start = parseA1Cell(startRaw);
  const end = parseA1Cell(endRaw);
  if (start && end) {
    return {
      startRow: Math.min(start.row, end.row),
      endRow: Math.max(start.row, end.row),
      startColumn: Math.min(start.column, end.column),
      endColumn: Math.max(start.column, end.column),
    };
  }
  const startColumn = parseA1Column(startRaw);
  const endColumn = parseA1Column(endRaw);
  if (startColumn !== undefined && endColumn !== undefined) {
    return {
      startRow: 0,
      endRow: EXCEL_MAX_ROW_INDEX,
      startColumn: Math.min(startColumn, endColumn),
      endColumn: Math.max(startColumn, endColumn),
    };
  }
  const startRow = parseA1Row(startRaw);
  const endRow = parseA1Row(endRaw);
  if (startRow !== undefined && endRow !== undefined) {
    return {
      startRow: Math.min(startRow, endRow),
      endRow: Math.max(startRow, endRow),
      startColumn: 0,
      endColumn: EXCEL_MAX_COLUMN_INDEX,
    };
  }
  return undefined;
}

function parseA1Ranges(address: string): ParsedA1Range[] {
  return address
    .split(",")
    .map((area) => parseA1Range(area.trim()))
    .filter((area): area is ParsedA1Range => area !== undefined);
}

function parseA1Cell(address: string | undefined): { row: number; column: number } | undefined {
  const match = /^([A-Z]+)(\d+)$/i.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  return {
    row: Number(match[2]) - 1,
    column: columnIndex(match[1]),
  };
}

function parseA1Column(address: string | undefined): number | undefined {
  const match = /^([A-Z]+)$/i.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  const column = columnIndex(match[1]);
  return column >= 0 && column <= EXCEL_MAX_COLUMN_INDEX ? column : undefined;
}

function parseA1Row(address: string | undefined): number | undefined {
  const match = /^(\d+)$/.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  const row = Number(match[1]) - 1;
  return row >= 0 && row <= EXCEL_MAX_ROW_INDEX ? row : undefined;
}

function rangesOverlap(a: ParsedA1Range, b: ParsedA1Range): boolean {
  return (
    a.startRow <= b.endRow &&
    a.endRow >= b.startRow &&
    a.startColumn <= b.endColumn &&
    a.endColumn >= b.startColumn
  );
}

function rangeContains(container: ParsedA1Range, target: ParsedA1Range): boolean {
  return (
    container.startRow <= target.startRow &&
    container.endRow >= target.endRow &&
    container.startColumn <= target.startColumn &&
    container.endColumn >= target.endColumn
  );
}

/** Parsed terminal response from the executor's final JSON message. */
interface TerminalResponse {
  mode?: "answer" | "clarification" | "workbook_edit" | "partial" | "failed";
  reply?: string;
}

/**
 * Normalizes the executor's terminal model response, accepting strict JSON,
 * fenced JSON, or plain text as a fallback reply.
 */
export function parseTerminalResponse(text: string): TerminalResponse {
  const trimmed = text.trim();
  if (!trimmed) return {};
  const parsed = extractTerminalJsonObject(trimmed);
  if (parsed) {
    const rawMode = parsed.mode;
    const mode =
      rawMode === "answer" ||
      rawMode === "clarification" ||
      rawMode === "workbook_edit" ||
      rawMode === "partial" ||
      rawMode === "failed"
        ? rawMode
        : undefined;
    const reply =
      typeof parsed.reply === "string"
        ? parsed.reply
        : typeof parsed.summary === "string"
          ? parsed.summary
          : typeof parsed.answer === "string"
            ? parsed.answer
            : undefined;
    return { mode, reply };
  }
  return { reply: trimmed };
}

/**
 * Finds the final JSON object in a terminal response, preferring fenced JSON
 * and then falling back to direct/top-level object parsing.
 */
function extractTerminalJsonObject(text: string): Record<string, unknown> | undefined {
  const fenced = parseFencedJsonObjects(text);
  if (fenced.length > 0) return fenced[fenced.length - 1];
  try {
    const direct = JSON.parse(text);
    if (direct && typeof direct === "object" && !Array.isArray(direct)) {
      return direct as Record<string, unknown>;
    }
  } catch {
    // Continue to top-level object extraction below.
  }
  const starts: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === "{") starts.push(i);
  }
  for (let i = starts.length - 1; i >= 0; i -= 1) {
    const start = starts[i];
    const end = findJsonObjectEnd(text, start);
    if (end === -1) continue;
    try {
      const parsed = JSON.parse(text.slice(start, end + 1));
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Try the next earlier top-level object candidate.
    }
  }
  return undefined;
}

/**
 * Parses JSON objects from markdown code fences in model output.
 */
function parseFencedJsonObjects(text: string): Record<string, unknown>[] {
  const objects: Record<string, unknown>[] = [];
  const fenceRegex = /```(?:json)?\s*([\s\S]*?)```/gi;
  let match = fenceRegex.exec(text);
  while (match) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        objects.push(parsed as Record<string, unknown>);
      }
    } catch {
      // Ignore non-JSON fenced blocks.
    }
    match = fenceRegex.exec(text);
  }
  return objects;
}

/**
 * Returns the closing brace index for a JSON object candidate while respecting
 * strings and escapes.
 */
function findJsonObjectEnd(text: string, start: number): number {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const char = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
    } else if (char === "{") {
      depth += 1;
    } else if (char === "}") {
      depth -= 1;
      if (depth === 0) return i;
      if (depth < 0) return -1;
    }
  }
  return -1;
}

/**
 * De-duplicates retrieved sources before returning them in the final run result.
 */
function dedupeSources(sources: ExcelAgentSource[]): ExcelAgentSource[] | undefined {
  if (sources.length === 0) return undefined;
  const seen = new Set<string>();
  const deduped: ExcelAgentSource[] = [];
  for (const source of sources) {
    const key = source.id || `${source.source}:${source.title}:${source.date ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(source);
  }
  return deduped;
}

/**
 * Builds a synthetic tool response used after a template re-pin failure tells
 * the executor to stop issuing tools.
 */
function buildSkippedToolOutput(toolName: string, reason: string): string {
  return JSON.stringify({
    ok: false,
    skipped: true,
    tool: toolName,
    error: `Skipped because the runner stopped before this tool could run: ${reason}`,
    instruction:
      "Do not retry or call more tools. Return the final JSON response with mode 'partial' and a concise reply.",
  });
}

function buildMissingRunPlanSkippedToolOutput(toolName: string): string {
  return JSON.stringify({
    ok: true,
    skipped: true,
    requiresRunPlan: true,
    tool: toolName,
    message: "Skipped until run steps exist.",
    instruction: "Call set_run_plan as the next tool call before retrying this action.",
  });
}

function extractToolError(output: string): string | null {
  try {
    const parsed = JSON.parse(output) as { error?: unknown };
    return typeof parsed.error === "string" ? parsed.error : null;
  } catch {
    return null;
  }
}

function extractBlockedWriteReason(output: Record<string, unknown>): string | null {
  for (const key of ["error", "recoveryError", "message"] as const) {
    const value = output[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const summarizeActions = (key: "failedActions" | "skippedActions", label: string) => {
    const actions = Array.isArray(output[key])
      ? output[key].filter((value): value is string => typeof value === "string" && !!value.trim())
      : [];
    return actions.length > 0 ? `${label}: ${actions.join("; ")}` : null;
  };
  return (
    summarizeActions("failedActions", "Failed actions") ??
    summarizeActions("skippedActions", "Skipped actions")
  );
}

/**
 * Summarizes a completed parallel research wave for the run console. The answer
 * sizes matter as much as the error count: an over-packed request often returns
 * HTTP 200 with a stub answer that covers none of its rows, which is invisible
 * if only hard errors are counted.
 */
function summarizeResearchWave(outputs: readonly string[]): {
  requestsWithoutUsableAnswer: number;
  minAnswerChars: number;
  maxAnswerChars: number;
} {
  let requestsWithoutUsableAnswer = 0;
  let minAnswerChars = Number.POSITIVE_INFINITY;
  let maxAnswerChars = 0;
  for (const output of outputs) {
    let answer = "";
    if (!extractToolError(output)) {
      try {
        const parsed = JSON.parse(output) as { answer?: unknown };
        if (typeof parsed.answer === "string") answer = parsed.answer.trim();
      } catch {
        answer = output.trim();
      }
    }
    if (!answer) requestsWithoutUsableAnswer += 1;
    minAnswerChars = Math.min(minAnswerChars, answer.length);
    maxAnswerChars = Math.max(maxAnswerChars, answer.length);
  }
  return {
    requestsWithoutUsableAnswer,
    minAnswerChars: Number.isFinite(minAnswerChars) ? minAnswerChars : 0,
    maxAnswerChars,
  };
}

function isMissingRunPlanSkippedWrite(output: string): boolean {
  try {
    const parsed = JSON.parse(output) as { requiresRunPlan?: unknown; skipped?: unknown };
    return parsed.requiresRunPlan === true && parsed.skipped === true;
  } catch {
    return false;
  }
}

export const WORKBOOK_EDIT_STEPS_REQUIRED_HARD_STOP_ERROR = WORKBOOK_EDIT_STEPS_REQUIRED_ERROR;

export const RUN_STEPS_REQUIRED_FOR_STATUS_ERROR = "Create run steps before updating step status.";

export function isHardMissingRunStepsWriteError(error: string | null): boolean {
  return error === WORKBOOK_EDIT_STEPS_REQUIRED_HARD_STOP_ERROR;
}

export function isMissingRunStepsStatusError(error: string | null): boolean {
  return error === RUN_STEPS_REQUIRED_FOR_STATUS_ERROR;
}

/**
 * Converts unfinished plan steps into explicit partial terminal states after
 * the orchestrator has decided the run must stop. This keeps final chat,
 * progress UI, and persisted step statuses aligned.
 */
export function terminalizeNonTerminalSteps(
  planState: ExecutorPlanState,
  accumulators: ExecutorRunAccumulators,
  reason: string,
  chartStoppingEvidence?: Map<string, ChartStoppingEvidence>,
): void {
  const nonTerminalSteps = planState.steps.filter(
    (s) => s.status === "queued" || s.status === "running" || s.status === "verifying"
  );
  if (nonTerminalSteps.length === 0) return;

  const activeStepId = planState.currentStepId;
  const warningStepId =
    activeStepId && nonTerminalSteps.some((s) => s.id === activeStepId)
      ? activeStepId
      : nonTerminalSteps[0].id;

  for (const step of nonTerminalSteps) {
    const index = planState.steps.findIndex((s) => s.id === step.id);
    const label = step.planStep.step || index + 1;
    const wasStarted =
      step.id === warningStepId ||
      step.status === "running" ||
      step.status === "verifying";
    // A chart step that started but produced no satisfied evidence left the
    // workbook in an unknown state. "Needs attention" reads like the chart is
    // there but imperfect, so those end as failed instead.
    const chartEvidenceMissing =
      wasStarted &&
      chartStepRequiresEvidence(step, chartStoppingEvidence) &&
      chartStoppingEvidence?.get(step.id)?.satisfied !== true;
    const status: ExcelStepStatus = chartEvidenceMissing
      ? "failed"
      : wasStarted
        ? "warning"
        : "paused";

    step.status = status;
    step.note = reason;
    accumulators.stepStatuses[index] = status;
    accumulators.stepDisplayResults[index] =
      status === "paused"
        ? `${step.planStep.description} (not started: ${reason})`
        : status === "failed"
          ? `${step.planStep.description} (failed: ${reason})`
          : `${step.planStep.description} (warning: ${reason})`;
    setStepResultLine(
      accumulators,
      step.id,
      status === "paused"
        ? `${label}. not started: ${reason}`
        : status === "failed"
          ? `${label}. failed: ${reason}`
          : `${label}. needs attention: ${reason}`,
    );
  }

  planState.currentStepId = undefined;
}

/**
 * Wraps the template outcome callback so the runner can observe
 * `apply_template` side effects without requiring `ExcelApp.tsx` to point its
 * legacy per-step buffer at the multi-hop run.
 *
 * @param context - Existing global tool-call context.
 * @param buffer - Run-scoped destination for template outcomes.
 * @returns A capturing context, or the original context when unavailable.
 */
export function withApplyTemplateOutcomeCapture(
  context: ToolCallContext | undefined,
  buffer: ApplyTemplateOutcome[]
): ToolCallContext | undefined {
  const templateContext = context?.templateContext;
  if (!templateContext) return context;
  return {
    ...context,
    templateContext: {
      ...templateContext,
      onApplyTemplateOutcome: (outcome) => {
        buffer.push(outcome);
        templateContext.onApplyTemplateOutcome?.(outcome);
      },
    },
  };
}

/**
 * Safely parses a JSON string, returning an empty object on failure.
 */
function safeParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw || "{}");
  } catch {
    return {};
  }
}

/**
 * Throws a DOMException if the signal is already aborted.
 */
function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}

// ---------------------------------------------------------------------------
// apply_template re-pin
// ---------------------------------------------------------------------------

type ApplyTemplateProcessResult = { shouldStop: false } | { shouldStop: true; reason: string };

/**
 * Processes buffered `ApplyTemplateOutcome`s after an iteration that called
 * `apply_template`. Handles workbook re-pinning when a template opens a new
 * workbook, and sets `session.skipFinalVerificationReason` when re-pin fails
 * but the template itself succeeded.
 *
 * This is the multi-hop equivalent of `resolveApplyTemplateStep` in the
 * legacy path but lighter: the model already sees the tool response and can
 * self-correct, so the runner only needs to handle host-level re-targeting.
 */
export async function processApplyTemplateOutcomes(
  outcomes: ApplyTemplateOutcome[],
  session: ExecutorRunSessionState,
  opts: {
    probeActiveContext?: () => Promise<{ workbookName?: string }>;
    bridgeAvailable: boolean;
    onWorkbookHandoff?: (
      handoff: ExecutorWorkbookHandoff,
    ) => void | Promise<void>;
  },
  onEvent?: (event: ExecutorRunEvent) => void
): Promise<ApplyTemplateProcessResult> {
  const outcome = outcomes.find((o) => o.success) ?? outcomes[outcomes.length - 1];
  if (!outcome || !outcome.success) return { shouldStop: false };

  const openedAwayFromCurrent =
    outcome.targetWorkbook === "new" || outcome.retargetedFrom === "current";

  if (!openedAwayFromCurrent) return { shouldStop: false };
  const previousPinnedWorkbookName = session.pinnedWorkbookName;

  if (!opts.bridgeAvailable || !opts.probeActiveContext) {
    const reason =
      "Template opened in a new workbook but bridge unavailable for re-pin - skipping verification.";
    session.skipFinalVerificationReason = reason;
    onEvent?.({
      type: "status",
      message: "apply_template opened new workbook; bridge unavailable for re-pin.",
    });
    return { shouldStop: true, reason };
  }

  let newWorkbookName = "";
  try {
    const ctx = await opts.probeActiveContext();
    newWorkbookName = ctx.workbookName?.trim() ?? "";
  } catch (e) {
    console.warn("[Excel] Could not identify workbook opened by apply_template:", e);
  }

  if (newWorkbookName) {
    session.pinnedWorkbookName = newWorkbookName;
    try {
      await opts.onWorkbookHandoff?.({
        sourceWorkbookName: previousPinnedWorkbookName,
        targetWorkbookName: newWorkbookName,
        reason: "apply_template",
      });
    } catch (error) {
      const reason =
        `Template opened in "${newWorkbookName}", but moving the conversation to that workbook failed: ` +
        `${error instanceof Error ? error.message : String(error)}`;
      session.skipFinalVerificationReason = reason;
      onEvent?.({
        type: "status",
        message: `apply_template conversation handoff to "${newWorkbookName}" failed.`,
      });
      return { shouldStop: true, reason };
    }
    onEvent?.({ type: "status", message: `Re-pinned to template workbook "${newWorkbookName}".` });
    diag("llm", "multi_hop_executor_repin", { newWorkbookName });
    return { shouldStop: false };
  } else {
    const reason =
      "Template opened in a new workbook but couldn't identify it - skipping verification against the old workbook.";
    session.skipFinalVerificationReason = reason;
    onEvent?.({
      type: "status",
      message: "apply_template re-pin failed - could not identify new workbook.",
    });
    return { shouldStop: true, reason };
  }
}

/**
 * Creates a short, filesystem-safe slug from a step description or action.
 * Used for generating stable step IDs.
 */
function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 24) || "step"
  );
}

/**
 * Detects the narrow case where the user explicitly wants sourced formulas
 * converted to static values. Normal workbook/model requests keep the
 * provenance guard enabled.
 */
function userRequestedFdsFormulaOverwriteWithStaticValues(request: string): boolean {
  if (
    /\b(?:do not|don't|never|without)\s+(?:paste|freeze|hardcode|hard-code|convert|replace)\b/i.test(
      request
    )
  ) {
    return false;
  }
  return (
    /\b(?:factset|fds|formulas?)\b.{0,60}\b(?:as|to|with)\s+(?:static\s+)?values?\b/i.test(
      request
    ) ||
    /\b(?:paste|freeze|hardcode|hard-code)\b.{0,60}\b(?:factset|fds|formulas?|data|values?)\b/i.test(
      request
    )
  );
}
